// src/pages/Tabs/OnStreetParkingPage.jsx
import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AddProjectButton, AddStreetIcon, AddPndIcon } from "../SidebarAddControls";
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  MapPin,
  Building2,
  CircleDot,
  X,
  Navigation,
  Layers,
  Search,
  SlidersHorizontal,
  CalendarRange,
  Check,
  Calendar,
  TrendingUp,
  Loader2,
  CreditCard,
  Wallet,
  Globe,
  BarChart2,
  Clock,
  Clock1,
  AlertTriangle,
  Ban,
  GripVertical,
  CircleParking,
  Download,
  Banknote,
} from "lucide-react";
import { toPng, toJpeg } from "html-to-image";
import jsPDF from "jspdf";
import { GOOGLE_MAP_KEY } from "@/constants/constant";
import { useTheme } from "@/components/theme-provider";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import ReactSelect from "@/components/common/ReactSelect";
import HourlyRevenueChart from "../HourlyRevenueChart";
import MapShimmer from "@/components/common/MapShimmer";
import { usePermissions } from "@/context/PermissionContext";
import {
  PaymentTableShimmer,
  PndListShimmer,
  StatsGridShimmer,
} from "@/components/child/SectionShimmer";

// ============================================
// CONSTANTS - ONSTREET CONTRACTS for Permission Filtering
// ============================================
// Fallback root/country contract code — only used until the API response
// (countryInfo.city_cc_contract) has loaded. Once loaded, the real value
// from the response takes over everywhere the root contract code is needed.
const DEFAULT_COUNTRY_CONTRACT_CODE = "city_cc_contract";

const ONSTREET_CONTRACTS = {
  fullName: "Saudi Arabia",
  children: [
    { name: "0030021", fullName: "AL-BAHA" },
    { name: "0030006", fullName: "AL-HASSA" },
    { name: "0030018", fullName: "BALJURASHI" },
    { name: "1501005", fullName: "DIGITAL CITY" },
    { name: "0030001", fullName: "DAMMAM" },
    { name: "0020001", fullName: "JEDDAH" },
    { name: "1930002", fullName: "JEDDAH JV" },
    { name: "0030013", fullName: "JUBAIL" },
    { name: "0030017", fullName: "KFCA" },
    { name: "0030014", fullName: "KFMH" },
    { name: "0030009", fullName: "KHAMISH MUSHAITH" },
    { name: "0030001", fullName: "KHOBAR" },
    { name: "0040003", fullName: "MADINA" },
    { name: "1400101", fullName: "MAKKAH" },
    { name: "0020025", fullName: "MINISTRY OF CULTURE" },
    { name: "0020013", fullName: "TAIF" },
    { name: "2010001", fullName: "RIYADH PARKING" },
  ],
};

// ============================================
// UNIQUE KEY for each contract (code + name)
// ============================================
const getContractKey = (contract) => `${contract.name}|${contract.fullName}`;

// ============================================
// API Permission to Contract Mapping - COMPLETE
// ============================================
const API_PERMISSION_TO_CONTRACT = {
  "BHA-AL-BAHA": { name: "0030021", fullName: "AL-BAHA" },
  "BLJ-BALJURASHI": { name: "0030018", fullName: "BALJURASHI" },
  "DAM-Dammam": { name: "0030001", fullName: "DAMMAM" },
  "DC-DIGITAL CITY": { name: "1501005", fullName: "DIGITAL CITY" },
  "HAS-AL-HASA": { name: "0030006", fullName: "AL-HASSA" },
  "JED-JEDDAH": { name: "0020001", fullName: "JEDDAH" },
  "JUB-JUBAIL": { name: "0030013", fullName: "JUBAIL" },
  "JV-JEDDAH JV": { name: "1930002", fullName: "JEDDAH JV" },
  "KFC-KFCA": { name: "0030017", fullName: "KFCA" },
  "KFMH-KFMH": { name: "0030014", fullName: "KFMH" },
  "KHA-KHAMIS MUSHAIT": { name: "0030009", fullName: "KHAMISH MUSHAITH" },
  "KHO-KHOBER": { name: "0030001", fullName: "KHOBAR" },
  "MAK-MAKKAH": { name: "1400101", fullName: "MAKKAH" },
  "MED-MADINAH": { name: "0040003", fullName: "MADINA" },
  "MOC-MINISTRY OF CULTURE": { name: "0020025", fullName: "MINISTRY OF CULTURE" },
  "RYDO-RIYADH PARKING": { name: "2010001", fullName: "RIYADH PARKING" },
  "Saudi Arabia": { name: DEFAULT_COUNTRY_CONTRACT_CODE, fullName: "Saudi Arabia" },
  "TAF-TAIF": { name: "0020013", fullName: "TAIF" },
};

// ============================================
// NORMALIZE NAME - Complete Fix
// ============================================
const normalizeName = (s) => {
  if (!s) return "";

  let cleaned = String(s).toUpperCase().trim();

  if (cleaned.includes("-")) {
    cleaned = cleaned.split("-").slice(1).join("-");
  }

  cleaned = cleaned.replace(/\s+/g, " ").trim();

  const specialMappings = {
    HASA: "HASSA",
    "AL HASA": "HASSA",
    "AL HASSA": "HASSA",
    "AL-HASA": "HASSA",
    ALAHSA: "HASSA",
    "AL AHSA": "HASSA",
    "AL-AHSA": "HASSA",
    AHSA: "HASSA",
    "KHAMIS MUSHAIT": "KHAMISH MUSHAITH",
    KHOBER: "KHOBAR",
    MADINAH: "MADINA",
  };

  return specialMappings[cleaned] || cleaned;
};

// ============================================
// Normalize API Permissions to Contracts
// `rootContractCode` (from countryInfo.city_cc_contract) overrides the
// static fallback for the country-level "Saudi Arabia" entry, so the
// permission key always matches whatever code the API returns.
// ============================================
const normalizeToContract = (permission, rootContractCode = DEFAULT_COUNTRY_CONTRACT_CODE) => {
  if (!permission) return null;
  const trimmed = String(permission).trim();

  if (API_PERMISSION_TO_CONTRACT[trimmed]) {
    const match = API_PERMISSION_TO_CONTRACT[trimmed];
    if (match.fullName === "Saudi Arabia") {
      return { ...match, name: rootContractCode || match.name };
    }
    return match;
  }

  const foundByCode = ONSTREET_CONTRACTS.children.find((c) => c.name === trimmed);
  if (foundByCode) return foundByCode;

  const normalizedName = normalizeName(trimmed);
  const foundByName = ONSTREET_CONTRACTS.children.find(
    (c) => normalizeName(c.fullName) === normalizedName
  );
  if (foundByName) return foundByName;

  return null;
};

const normalizePermissions = (userContracts, rootContractCode = DEFAULT_COUNTRY_CONTRACT_CODE) => {
  if (!userContracts) return { onstreet: [], offstreet: [] };

  const normalizeList = (list) => {
    if (!list || !Array.isArray(list)) return [];
    return list.map((item) => normalizeToContract(item, rootContractCode)).filter(Boolean);
  };

  return {
    onstreet: normalizeList(userContracts.onstreet),
    offstreet: normalizeList(userContracts.offstreet),
  };
};

// ─── Device helpers ───────────────────────────────────────────────────────────
const getDeviceColor = (deviceType) => {
  if (!deviceType) return "#9CA3AF";
  if (deviceType.includes("BNA")) return "#8B5CF6";
  if (deviceType.includes("TX")) return "#F59E0B";
  return "#10B981";
};

const getDeviceTypeName = (deviceType) => {
  if (!deviceType) return "Unknown";
  if (deviceType.includes("BNA")) return "BNA";
  if (deviceType.includes("TX")) return "TX";
  return deviceType;
};

// Device type → Lucide icon mapping (Device Type Breakdown cards, sidebar list,
// PND detail panel ke liye) — OffStreetMap.jsx wali approach yahan bhi follow ki hai.
const DEVICE_TYPE_ICONS = {
  BNA: Banknote,
  TX: CreditCard,
};

const getDeviceTypeIcon = (deviceType) => {
  if (!deviceType) return CircleParking;
  const t = deviceType.toUpperCase();
  if (t.includes("BNA")) return DEVICE_TYPE_ICONS.BNA;
  if (t.includes("TX")) return DEVICE_TYPE_ICONS.TX;
  return CircleParking;
};

// Raw Lucide icon markup (paths/shapes only, 24x24 viewBox) — map ke marker pin ke
// andar white circle mein baithane ke liye. Yeh raw <svg> data-URI se banta hai isliye
// React <Icon/> component use nahi ho sakta, isliye inner SVG markup string ke roop mein
// (OffStreetMap.jsx ke MARKER_ICON_PATHS jaisa hi pattern).
const MARKER_ICON_PATHS = {
  BNA: '<rect width="20" height="12" x="2" y="6" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/>',
  TX: '<rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>',
  DEFAULT: '<path d="M9 17V7h4a3 3 0 0 1 0 6H9"/>',
};

const getMarkerIconPaths = (deviceType) => {
  if (!deviceType) return MARKER_ICON_PATHS.DEFAULT;
  const t = deviceType.toUpperCase();
  if (t.includes("BNA")) return MARKER_ICON_PATHS.BNA;
  if (t.includes("TX")) return MARKER_ICON_PATHS.TX;
  return MARKER_ICON_PATHS.DEFAULT;
};

// ─── Street color palette ─────────────────────────────────────────────────────
const STREET_COLORS = [
  "#F59E0B",
  "#10B981",
  "#EC4899",
  "#14B8A6",
  "#F97316",
  "#06B6D4",
  "#84CC16",
  "#A78BFA",
  "#EF4444",
  "#3B82F6",
];

const darkenColor = (hex, amount = 0.35) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return hex;
  const h = hex.slice(1);
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  if (full.length !== 6) return hex;
  const num = parseInt(full, 16);
  let r = (num >> 16) & 255,
    g = (num >> 8) & 255,
    b = num & 255;
  r = Math.max(0, Math.round(r * (1 - amount)));
  g = Math.max(0, Math.round(g * (1 - amount)));
  b = Math.max(0, Math.round(b * (1 - amount)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// ─── Google Maps Styles ───────────────────────────────────────────────────────
const getDarkMapStyles = (hideLabels = false) => [
  { elementType: "geometry", stylers: [{ color: "#1a1d28" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a1d28" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8b92a8" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d0d3de" }],
  },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#232938" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c3142" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f1419" }] },
  ...(hideLabels ? [{ elementType: "labels", stylers: [{ visibility: "off" }] }] : []),
];

const getLightMapStyles = (hideLabels = false) => [
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e7eb" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#d1d5db" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dbeafe" }] },
  ...(hideLabels ? [{ elementType: "labels", stylers: [{ visibility: "off" }] }] : []),
];

// ─── Static Maps style converter ────────────────────────────────────────────
const stylesToStaticParams = (styles) => {
  const parts = [];
  (styles || []).forEach((rule) => {
    const seg = [`feature:${rule.featureType || "all"}`, `element:${rule.elementType || "all"}`];
    (rule.stylers || []).forEach((s) => {
      if (s.color) seg.push(`color:0x${String(s.color).replace("#", "")}`);
      if (s.visibility) seg.push(`visibility:${s.visibility}`);
    });
    parts.push(`style=${seg.join("%7C")}`);
  });
  return parts.join("&");
};

// ─── Month helpers ────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const getRawMonth = (month) => {
  if (!month) return MONTH_NAMES[new Date().getMonth()];
  if (month.includes("-")) return month.split("-")[1];
  return month;
};

const buildMonthParam = (month) => {
  if (!month) return null;
  const GROUP_NAMES = ["Full_Year", "FY"];
  if (GROUP_NAMES.includes(month)) return month;
  if (month.includes("-")) return month;
  return month;
};

// ─── PND status filter helpers ─────────────────────────────────────────────────
const PND_STATUS_LABELS = {
  all: "All",
  active: "Active",
  inactive: "Inactive",
  no_transaction: "No Transaction",
  below_threshold: "Below Threshold",
};

const EMPTY_FILTERS = [];

const formatCount = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  return Number.isNaN(num) ? String(value) : num.toLocaleString();
};

const parseCount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  return Number.isNaN(num) ? null : num;
};

const buildFiltersParam = (statusFilters) => {
  if (!statusFilters || statusFilters.length === 0) return "";
  return statusFilters.map((f) => `&filters[]=${f}`).join("");
};

// ─── Parking Bays helpers ───────────────────────────────────────────────────
const ONSTREET_CONTRACTS_FOR_BAYS = [
  { code: "0030021", name: "AL-BAHA" },
  {
    code: "0030006",
    name: "AL-HASSA",
    aliases: ["AL HASA", "AL-HASA", "ALAHSA", "AL AHSA", "AL-AHSA", "AHSA", "HASA"],
  },
  { code: "0030018", name: "BALJURASHI" },
  { code: "1501005", name: "DIGITAL CITY" },
  { code: "0030001", name: "DAMMAM" },
  { code: "0020001", name: "JEDDAH" },
  { code: "1930002", name: "JEDDAH JV" },
  { code: "0030013", name: "JUBAIL" },
  { code: "0030017", name: "KFCA" },
  { code: "0030014", name: "KFMH" },
  {
    code: "0030009",
    name: "KHAMISH MUSHAITH",
    aliases: ["KHAMIS MUSHAYT", "KHAMIS MUSHAIT", "KHAMIS MUSHAITH", "KHAMEES MUSHAYT", "KHAMIS"],
  },
  { code: "0030001", name: "KHOBAR" },
  { code: "0040003", name: "MADINA" },
  { code: "1400101", name: "MAKKAH" },
  { code: "0020025", name: "MINISTRY OF CULTURE" },
  { code: "0020013", name: "TAIF" },
  { code: "2010001", name: "RIYADH PARKING" },
];

const getContractCodeForCity = (city) => {
  if (!city) return null;
  const candidates = [city.city_name, city.city_send_name, city.city_cd]
    .filter(Boolean)
    .map(normalizeName);
  const namesFor = (c) => [c.name, ...(c.aliases || [])].map(normalizeName);
  const match =
    ONSTREET_CONTRACTS_FOR_BAYS.find((c) => namesFor(c).some((n) => candidates.includes(n))) ||
    ONSTREET_CONTRACTS_FOR_BAYS.find((c) =>
      namesFor(c).some((n) => candidates.some((cand) => cand.includes(n) || n.includes(cand)))
    );
  return match?.code || null;
};

const buildBaysMonthParam = (month) => {
  const GROUP_NAMES = ["Full_Year", "FY"];
  if (!month || GROUP_NAMES.includes(month)) {
    return MONTH_NAMES[new Date().getMonth()];
  }
  return buildMonthParam(month) || getRawMonth(month);
};

// `contractCode` now comes from the API response's `city_cc_contract` key
// (no more hardcoded '901') - see usePndData / countryInfo.city_cc_contract.
const useCountryParkingBaysCount = (month, year, contractCode) => {
  const [countryParkingBaysCount, setCountryParkingBaysCount] = useState(null);
  const [countryParkingBaysLoading, setCountryParkingBaysLoading] = useState(false);

  useEffect(() => {
    if (!month || !year || !contractCode) {
      setCountryParkingBaysCount(null);
      setCountryParkingBaysLoading(false);
      return;
    }
    let cancelled = false;
    setCountryParkingBaysLoading(true);
    const monthParam = buildBaysMonthParam(month);
    const url = `/get-enforcement-data-os?contract=${contractCode}&year=${year}&month=${monthParam}`;
    apiCall("get", url, null, null, false, false)
      .then((response) => {
        if (cancelled || !response?.success) return;
        const bays =
          response.data?.cns_cards?.parking_bays ??
          response.data?.enforcement_cards?.parking_bays ??
          null;
        setCountryParkingBaysCount(bays);
      })
      .catch(() => {
        if (!cancelled) setCountryParkingBaysCount(null);
      })
      .finally(() => {
        if (!cancelled) setCountryParkingBaysLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [month, year, contractCode]);

  return { countryParkingBaysCount, countryParkingBaysLoading };
};

const useParkingBaysCount = (city, month, year) => {
  const [parkingBaysCount, setParkingBaysCount] = useState(null);
  const [parkingBaysLoading, setParkingBaysLoading] = useState(false);

  useEffect(() => {
    // ✅ Pehle direct city_cc_contract, agar nahi toh getContractCodeForCity fallback
    const contractCode = city?.city_cc_contract || getContractCodeForCity(city);
    if (!contractCode) {
      setParkingBaysCount(null);
      return;
    }

    let cancelled = false;
    setParkingBaysLoading(true);
    const monthParam = buildBaysMonthParam(month);
    const url = `/get-enforcement-data-os?contract=${contractCode}&year=${year}&month=${monthParam}`;
    apiCall("get", url, null, null, false, false)
      .then((response) => {
        if (cancelled || !response?.success) return;
        const bays =
          response.data?.cns_cards?.parking_bays ??
          response.data?.enforcement_cards?.parking_bays ??
          null;
        setParkingBaysCount(bays);
      })
      .catch(() => {
        if (!cancelled) setParkingBaysCount(null);
      })
      .finally(() => {
        if (!cancelled) setParkingBaysLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [city?.city_name, city?.city_send_name, city?.city_cd, city?.city_cc_contract, month, year]);

  return { parkingBaysCount, parkingBaysLoading };
};

// ============================================
// AccordionSection — reusable toggle section
// ============================================
const exportPanelToPdf = async (panelRef, fileName, onProgress, mapComponentRef) => {
  const node = panelRef?.current;
  if (!node) return;

  let snapshotActive = false;
  if (mapComponentRef?.current?.prepareExportSnapshot) {
    try {
      snapshotActive = await mapComponentRef.current.prepareExportSnapshot();
    } catch {
      snapshotActive = false;
    }
  }

  try {
    await capturePanelToPdf(node, fileName, onProgress);
  } finally {
    if (snapshotActive) mapComponentRef.current?.clearExportSnapshot?.();
  }
};

const capturePanelToPdf = async (node, fileName, onProgress) => {
  const pixelRatio = 2;
  const ignoreNode = (domNode) => !domNode?.classList?.contains?.("pdf-export-ignore");

  await document.fonts.ready;
  const images = node.querySelectorAll("img");
  await Promise.all(
    Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
        setTimeout(resolve, 2000);
      });
    })
  );
  await new Promise((r) => setTimeout(r, 150));

  const scrollWidth = node.scrollWidth;
  const scrollHeight = node.scrollHeight;
  const bgColor = getComputedStyle(node).backgroundColor || "#ffffff";

  const captureOpts = {
    quality: 1.0,
    pixelRatio,
    cacheBust: true,
    backgroundColor: bgColor,
    width: scrollWidth,
    height: scrollHeight,
    filter: ignoreNode,
    style: { overflow: "visible" },
  };

  onProgress?.(20);
  let dataUrl;
  try {
    dataUrl = await toPng(node, captureOpts);
    if (!dataUrl || !dataUrl.startsWith("data:image/png")) throw new Error("Invalid PNG");
  } catch {
    dataUrl = await toJpeg(node, { ...captureOpts, quality: 0.95 });
  }
  onProgress?.(70);

  const imgEl = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

  const imgAspect = imgEl.width / imgEl.height;
  const isWideSnapshot = imgAspect > 1;
  const pdf = isWideSnapshot
    ? new jsPDF({
        orientation: "l",
        unit: "pt",
        format: [842, Math.max(1, Math.round(842 / imgAspect))],
      })
    : new jsPDF("p", "pt", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (imgEl.height * imgWidth) / imgEl.width;
  const format = dataUrl.startsWith("data:image/jpeg") ? "JPEG" : "PNG";

  if (isWideSnapshot) {
    pdf.addImage(dataUrl, format, 0, 0, imgWidth, imgHeight, undefined, "FAST");
  } else {
    const EPSILON = 1;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(dataUrl, format, 0, position, imgWidth, imgHeight, undefined, "FAST");
    heightLeft -= pageHeight;

    while (heightLeft > EPSILON) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(dataUrl, format, 0, position, imgWidth, imgHeight, undefined, "FAST");
      heightLeft -= pageHeight;
    }
  }

  onProgress?.(100);
  pdf.save(`${fileName}.pdf`);
};

const ExportPdfButton = ({ onClick, exporting, progress }) => (
  <button
    onClick={onClick}
    disabled={exporting}
    title="Export as PDF"
    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
  >
    {exporting ? (
      <>
        <Loader2 className="w-4 h-4 animate-spin" />
        {progress > 0 && <span className="text-[10px] font-mono tabular-nums">{progress}%</span>}
      </>
    ) : (
      <Download className="w-4 h-4" />
    )}
  </button>
);

const AccordionSection = ({
  icon,
  title,
  badge,
  children,
  defaultOpen = false,
  id,
  openId,
  onToggle,
}) => {
  const isControlled = openId !== undefined && onToggle !== undefined;
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const open = isControlled
    ? openId instanceof Set
      ? openId.has(id)
      : Array.isArray(openId)
        ? openId.includes(id)
        : openId === id
    : localOpen;
  const toggle = () => {
    if (isControlled) onToggle(id);
    else setLocalOpen((v) => !v);
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors text-left"
      >
        <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 text-muted-foreground">
          {icon}
        </div>
        <span className="flex-1 text-xs font-semibold text-foreground">{title}</span>
        {badge &&
          (Array.isArray(badge) ? (
            badge.map((b, i) => (
              <span
                key={i}
                className="text-[10px] text-muted-foreground bg-secondary border border-border px-2 py-0.5 rounded-full mr-1"
              >
                {b}
              </span>
            ))
          ) : (
            <span className="text-[10px] text-muted-foreground bg-secondary border border-border px-2 py-0.5 rounded-full mr-1">
              {badge}
            </span>
          ))}
        <ChevronDown
          className={`w-3.5 h-3.5 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// PaymentBreakdownCard
// ============================================
const PaymentBreakdownCard = ({ breakdown }) => {
  if (!breakdown) return null;

  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const groups = [
    {
      key: "app_web",
      label: "App / Web",
      items: [
        { key: "card", label: "Card" },
        { key: "ewallet", label: "eWallet" },
        { key: "web_card", label: "Web Card" },
      ],
    },
    {
      key: "pnd",
      label: "PND",
      items: [
        { key: "card", label: "Card" },
        { key: "notes", label: "Notes" },
        { key: "coins", label: "Coins" },
        { key: "ewallet", label: "eWallet" },
      ],
    },
  ];

  const pct = (v) => parseFloat(String(v ?? "0").replace("%", "")) || 0;
  const num = (v) => parseFloat(String(v ?? "0").replace(/,/g, "")) || 0;
  const fmtNum = (v) =>
    v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const appWebTotal = breakdown?.app_web?.total;
  const pndTotal = breakdown?.pnd?.total;

  const totalData =
    breakdown?.total ??
    (appWebTotal || pndTotal
      ? (() => {
          const totalCollection = num(appWebTotal?.collection) + num(pndTotal?.collection);
          const totalTickets = num(appWebTotal?.ticket_count) + num(pndTotal?.ticket_count);
          const weightedAls = totalTickets
            ? (num(appWebTotal?.als) * num(appWebTotal?.ticket_count) +
                num(pndTotal?.als) * num(pndTotal?.ticket_count)) /
              totalTickets
            : 0;
          return {
            collection: fmtNum(totalCollection),
            ticket_count: Math.round(totalTickets).toLocaleString("en-US"),
            als: weightedAls.toFixed(2),
            atv: totalTickets ? (totalCollection / totalTickets).toFixed(2) : "0.00",
            revenue_percentage: "100.00%",
          };
        })()
      : null);

  const sortedGroups = [...groups].sort(
    (a, b) =>
      pct(breakdown?.[b.key]?.total?.revenue_percentage) -
      pct(breakdown?.[a.key]?.total?.revenue_percentage)
  );

  const DataRow = ({ label, data }) => {
    return (
      <div className="grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 items-center px-3.5 py-2 border-b border-border/50 hover:bg-zinc-100 dark:hover:bg-zinc-800/60 transition-colors">
        <p className="text-[10px] font-medium text-foreground truncate">{label}</p>
        <p className="text-[10px] tabular-nums font-semibold text-foreground">
          {data?.collection ?? "0"}
        </p>
        <p className="text-[10px] tabular-nums text-foreground">{data?.ticket_count ?? "0"}</p>
        <p className="text-[10px] tabular-nums text-foreground">{data?.als ?? "0"}</p>
        <p className="text-[10px] tabular-nums text-foreground">{data?.atv ?? "0"}</p>
        <p className="text-[9px] tabular-nums font-semibold text-muted-foreground">
          {data?.revenue_percentage ?? "0.00%"}
        </p>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden w-full">
      {sortedGroups.map((group) => {
        const groupData = breakdown?.[group.key];
        const groupTotal = groupData?.total;
        const isOpen = !!openGroups[group.key];
        const sortedItems = [...group.items].sort(
          (a, b) =>
            pct(groupData?.[b.key]?.revenue_percentage) -
            pct(groupData?.[a.key]?.revenue_percentage)
        );

        return (
          <div key={group.key} className="border-b border-border last:border-b-0">
            <button
              onClick={() => toggleGroup(group.key)}
              className="w-full grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 items-center px-3.5 py-2.5 
              bg-[#ddebf7] 
              hover:bg-[#d2e3f3]
              transition-colors text-left"
            >
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {group.label}
              </span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {groupTotal?.collection ?? "0"}
              </span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {groupTotal?.ticket_count ?? "0"}
              </span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {groupTotal?.als ?? "0"}
              </span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {groupTotal?.atv ?? "0"}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-secondary border border-border text-foreground tabular-nums">
                  {groupTotal?.revenue_percentage ?? "0%"}
                </span>
                <ChevronDown
                  className={`w-3 h-3 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  {sortedItems.map(({ key, label }) => (
                    <DataRow key={key} label={label} data={groupData?.[key]} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      {totalData && (
        <div className="grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 items-center px-3.5 py-2.5 bg-zinc-200 dark:bg-zinc-700">
          <p className="text-[10px] font-black text-foreground uppercase tracking-wide">
            Grand Total
          </p>
          <p className="text-[11px] font-black tabular-nums text-foreground">
            {totalData.collection ?? 0}
          </p>
          <p className="text-[11px] font-black tabular-nums text-foreground">
            {totalData.ticket_count ?? 0}
          </p>
          <p className="text-[11px] font-black tabular-nums text-foreground">
            {totalData.als ?? 0}
          </p>
          <p className="text-[11px] font-black tabular-nums text-foreground">
            {totalData.atv ?? 0}
          </p>
          <p className="text-[10px] font-black tabular-nums text-foreground">
            {totalData.revenue_percentage ?? "100%"}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// CityRevenueSection
// ============================================
const CityRevenueSection = ({
  city,
  filterMonth,
  filterYear,
  filterThreshold,
  onBreakdownLoad,
  filterStatus = EMPTY_FILTERS,
}) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  const cityId = city?.city_send_name || city?.city_cd;

  const fetchRevenue = useCallback(async () => {
    if (!cityId) return;
    setLoading(true);
    setError(null);
    setRevenueData(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      // ✅ contract پیرامیٹر شامل کیا
      const contractParam = city?.city_cc_contract ? `&contract=${city.city_cc_contract}` : "";
      const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}${buildFiltersParam(filterStatus)}${contractParam}`;
      const response = await apiCall("get", url, null, null, false, false);

      if (response?.success) {
        const payload =
          response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;
        setRevenueData(payload);

        const belowSet = new Set();
        const noTxSet = new Set();
        const revMap = new Map();
        (payload.streets || []).forEach((st) => {
          (st.pnds || []).forEach((p) => {
            if (p.below_threshold === true) belowSet.add(String(p.pnd));
            if (p.no_transaction === true) noTxSet.add(String(p.pnd));
            revMap.set(String(p.pnd), {
              collection: p.collection,
              ticket_count: p.ticket_count,
              als: p.als,
              atv: p.atv,
              contribution: p.contribution,
              device_status_date: p.device_status_date,
              device_active_date: p.device_active_date,
            });
          });
        });
        onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet, revenueMap: revMap });
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to fetch revenue");
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [
    cityId,
    displayMonth,
    displayYear,
    threshold,
    filterStatus,
    onBreakdownLoad,
    city?.city_cc_contract,
  ]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchRevenue();
  }, [fetchRevenue]);

  return (
    <div>
      {loading && <PaymentTableShimmer groups={2} rowsPerGroup={3} />}
      {!loading && error && (
        <div className="flex flex-col items-center gap-2 py-5">
          <p className="text-xs text-muted-foreground text-center">{error}</p>
          <button
            onClick={fetchRevenue}
            className="text-[10px] font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}
      {!cityId && !loading && (
        <p className="text-xs text-muted-foreground text-center py-5">
          No city ID available for revenue lookup.
        </p>
      )}
      {!loading && !error && revenueData && revenueData.payment_breakdown && (
        <PaymentBreakdownCard breakdown={revenueData.payment_breakdown} />
      )}
    </div>
  );
};

// ============================================
// StreetRevenueSection
// ============================================
const StreetRevenueSection = ({
  street,
  filterMonth,
  filterYear,
  filterThreshold,
  onSelectPnd,
  onBreakdownLoad,
  filterStatus = EMPTY_FILTERS,
}) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);

  const streetId = street?.street_send_name || street?.street_cd;

  const fetchRevenue = useCallback(async () => {
    if (!streetId) return;
    setLoading(true);
    setError(null);
    setRevenueData(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?street=true&device_id=${streetId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}${buildFiltersParam(filterStatus)}`;
      const response = await apiCall("get", url, null, null, false, false);

      if (response?.success) {
        setRevenueData(response);
        const belowSet = new Set();
        const noTxSet = new Set();
        const revMap = new Map();
        (response.street_breakdown?.pnds || []).forEach((p) => {
          if (p.below_threshold === true) belowSet.add(String(p.pnd));
          if (p.no_transaction === true) noTxSet.add(String(p.pnd));
          revMap.set(String(p.pnd), {
            collection: p.collection,
            ticket_count: p.ticket_count,
            als: p.als,
            atv: p.atv,
            contribution: p.contribution,
            device_status_date: p.device_status_date,
            device_active_date: p.device_active_date,
          });
        });
        onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet, revenueMap: revMap });
      } else {
        setError(response?.message || "No revenue data found");
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to fetch revenue");
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [streetId, displayMonth, displayYear, threshold, filterStatus, onBreakdownLoad]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchRevenue();
  }, [fetchRevenue]);

  return (
    <div>
      {loading && <StatsGridShimmer count={4} />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-2 py-5">
          <p className="text-xs text-muted-foreground text-center">{error}</p>
          <button
            onClick={fetchRevenue}
            className="text-[10px] font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}
      {!streetId && !loading && (
        <p className="text-xs text-muted-foreground text-center py-5">
          No street ID available for revenue lookup.
        </p>
      )}
      {!loading && !error && revenueData && (
        <>
          {revenueData.payment_breakdown && (
            <div className="mb-3">
              <PaymentBreakdownCard breakdown={revenueData.payment_breakdown} />
            </div>
          )}
          {Array.isArray(revenueData.street_breakdown?.pnds) &&
            revenueData.street_breakdown.pnds.length > 0 && (
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-3.5 py-2 bg-secondary/60 border-b border-border">
                  {["PND", "Revenue", "Tickets", "ALS", "ATV", "Contribution"].map((h) => (
                    <p
                      key={h}
                      className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                    >
                      {h}
                    </p>
                  ))}
                </div>

                <div className="divide-y divide-border max-h-64 overflow-y-scroll cb-scroll">
                  {revenueData.street_breakdown.pnds.map((item, idx) => (
                    <div
                      key={`pnd_row_${item.pnd ?? idx}`}
                      onClick={() => onSelectPnd?.(item)}
                      className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr] gap-2 items-center px-3.5 py-2.5 hover:bg-secondary/40 transition-colors cursor-pointer"
                    >
                      <p className="text-[11px] font-mono font-semibold text-foreground">
                        #{item.pnd}
                      </p>
                      <p className="text-[11px] font-semibold">{item.collection ?? 0}</p>
                      <p className="text-[11px] text-foreground">{item.ticket_count ?? 0}</p>
                      <p className="text-[11px] text-foreground">{item.als ?? 0}</p>
                      <p className="text-[11px] text-foreground">{item.atv ?? 0}</p>
                      <p className="text-[10px] font-semibold tabular-nums text-muted-foreground">
                        {item.contribution ?? "—"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
};

// ============================================
// PND RevenueSection
// ============================================
const RevenueSection = ({ pnd, filterMonth, filterYear, filterThreshold }) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);
  const deviceId = pnd?.pnd_number || pnd?.device_id;

  const fetchRevenue = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    setRevenueData(null);
    try {
      const monthParam = buildMonthParam(displayMonth)
        ? displayMonth
        : displayMonth.includes("-")
          ? displayMonth
          : `01-${displayMonth}`;

      const url = `/get-pnd-collection?device_id=${deviceId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}`;
      const response = await apiCall("get", url, null, null, false, false);

      if (response?.success) {
        const payload = response.data ?? response;
        if (payload.collection !== null || payload.ticket_count !== null) {
          setRevenueData(payload);
        } else {
          setError("No revenue data found for this period");
        }
      } else {
        setError(response?.message || "No revenue data found");
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to fetch revenue");
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [deviceId, displayMonth, displayYear, threshold]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchRevenue();
  }, [fetchRevenue]);

  return (
    <div>
      {loading && (
        <div className="space-y-3">
          <PaymentTableShimmer groups={2} rowsPerGroup={2} />
          <PndListShimmer rows={5} />
        </div>
      )}
      {!loading && error && (
        <div className="flex flex-col items-center gap-2 py-5">
          <p className="text-xs text-muted-foreground text-center">{error}</p>
          <button
            onClick={fetchRevenue}
            className="text-[10px] font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}
      {!deviceId && !loading && (
        <p className="text-xs text-muted-foreground text-center py-5">
          No device ID available for revenue lookup.
        </p>
      )}
      {!loading && !error && revenueData && (
        <div className="grid grid-cols-2 gap-2.5">
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
              Revenue
            </p>
            <p className="text-[22px] font-medium leading-none">
              {revenueData.collection ?? revenueData.total_collection ?? revenueData.total ?? 0}
            </p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
              Ticket Count
            </p>
            <p className="text-[22px] font-medium text-foreground leading-none">
              {revenueData.ticket_count ?? 0}
            </p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ALS</p>
            <p className="text-[22px] font-medium text-foreground leading-none">
              {revenueData.als ?? 0}
            </p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ATV</p>
            <p className="text-[22px] font-medium text-foreground leading-none">
              {revenueData.atv ?? 0}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// CityStreetsRevenueSummary
// ============================================
const CityStreetsRevenueSummary = ({
  city,
  filterMonth,
  filterYear,
  filterThreshold,
  onSelectStreet,
  filterStatus = EMPTY_FILTERS,
}) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streetSearch, setStreetSearch] = useState("");

  const hasFetchedRef = useRef(false);

  const cityId = city?.city_send_name || city?.city_cd;

  const fetchBreakdown = useCallback(async () => {
    if (!cityId) return;
    setLoading(true);
    setError(null);
    setBreakdown(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      // ✅ contract پیرامیٹر شامل کیا
      const contractParam = city?.city_cc_contract ? `&contract=${city.city_cc_contract}` : "";
      const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}${buildFiltersParam(filterStatus)}${contractParam}`;
      const response = await apiCall("get", url, null, null, false, false);
      if (response?.success) {
        const payload =
          response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;
        setBreakdown(payload);
      } else {
        setError(response?.message || "No data");
      }
    } catch {
      setError("Failed to fetch");
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [cityId, displayMonth, displayYear, threshold, filterStatus, city?.city_cc_contract]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchBreakdown();
  }, [fetchBreakdown]);

  if (loading) {
    return <PndListShimmer rows={6} withHeader={false} />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-5">
        <p className="text-xs text-muted-foreground text-center">{error}</p>
        <button
          onClick={fetchBreakdown}
          className="text-[10px] font-semibold text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const streets = breakdown?.streets || [];
  if (!streets.length) {
    return (
      <p className="text-xs text-muted-foreground text-center py-5">No streets data available.</p>
    );
  }

  const totalStreets = streets.length;

  const q = streetSearch.trim().toLowerCase();
  const filteredStreets = q ? streets.filter((s) => s.street?.toLowerCase().includes(q)) : streets;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search streets..."
          value={streetSearch}
          onChange={(e) => setStreetSearch(e.target.value)}
          className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
        />
        {streetSearch && (
          <button
            onClick={() => setStreetSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {q && (
        <p className="text-[10px] text-muted-foreground px-0.5">
          {filteredStreets.length === 0
            ? `No streets matched "${streetSearch}"`
            : `${filteredStreets.length} of ${totalStreets} streets`}
        </p>
      )}

      <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
        {filteredStreets.length === 0 && q ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
            <Search className="w-5 h-5 opacity-30" />
            <p className="text-xs">No results for "{streetSearch}"</p>
          </div>
        ) : (
          filteredStreets.map((street, idx) => (
            <StreetRevenueSummaryRow
              key={`${street.street}_${idx}`}
              street={street}
              cityStreets={city?.streets || []}
              onSelectStreet={onSelectStreet}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ============================================
// StreetRevenueSummaryRow
// ============================================
const StreetRevenueSummaryRow = ({ street, cityStreets, onSelectStreet }) => {
  const [open, setOpen] = useState(false);

  const isNoTransaction = street.no_transaction === true;
  const contributionPct = parseFloat(street.contribution) || 0;

  const collection = street.collection ?? "0";
  const ticketCount = street.ticket_count ?? "0";
  const contribution = street.contribution ?? "0.00%";
  const als =
    street.als !== null && street.als !== undefined && street.als !== "" ? String(street.als) : "—";
  const atv =
    street.atv !== null && street.atv !== undefined && street.atv !== "" ? String(street.atv) : "—";

  const noTranCount = street.no_tran_pnd_count ?? 0;
  const belowThrCount = street.below_threshold_pnd_count ?? 0;
  const pnds = street.pnds || [];

  const fullStreet = cityStreets.find(
    (s) =>
      s.street_name === street.street ||
      s.street_send_name === street.street ||
      s.street_cd === street.street
  );

  const handleStreetClick = () => {
    if (fullStreet) {
      onSelectStreet?.(fullStreet);
    }
    setOpen(true);
  };

  return (
    <div
      className={`rounded-xl border overflow-hidden transition-colors ${
        isNoTransaction ? "border-red-500/30 bg-red-500/5" : "border-border bg-card"
      }`}
    >
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <div
            className={`w-2 h-2 rounded-full ${isNoTransaction ? "bg-red-500" : "bg-emerald-500"}`}
          />
        </button>

        <button
          onClick={handleStreetClick}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          title="View street details"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {street.street}
          </span>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {collection}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contribution}
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              isNoTransaction ? "bg-red-500" : "bg-primary"
            }`}
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              <div className="space-y-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Revenue
                  </p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">
                    {collection}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      Tickets
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {ticketCount}
                    </p>
                  </div>
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      Contribution
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {contribution}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      ALS
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {als}
                    </p>
                  </div>
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      ATV
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {atv}
                    </p>
                  </div>
                </div>
              </div>

              {(noTranCount > 0 || belowThrCount > 0) && (
                <div className="flex gap-2 flex-wrap">
                  {noTranCount > 0 && (
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/25">
                      <Ban className="w-3 h-3 text-red-500 flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-red-500">
                        {noTranCount} No Transaction
                      </span>
                    </div>
                  )}
                  {belowThrCount > 0 && (
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/10 border border-black/25">
                      <AlertTriangle className="w-3 h-3 text-black flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-black">
                        {belowThrCount} Below Threshold
                      </span>
                    </div>
                  )}
                </div>
              )}

              {pnds.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.7fr_0.8fr] gap-1.5 px-3 py-2 bg-secondary/60 border-b border-border">
                    {["PND", "Revenue", "Tickets", "ALS", "ATV", "Contribution"].map((h) => (
                      <p
                        key={h}
                        className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                      >
                        {h}
                      </p>
                    ))}
                  </div>

                  <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                    {pnds.map((pnd, pi) => {
                      const pndPct = parseFloat(pnd.contribution) || 0;
                      const pndNoTran = pnd.no_transaction === true;
                      const pndBelowThr = pnd.below_threshold === true;
                      const pndAls =
                        pnd.als !== null && pnd.als !== undefined && pnd.als !== ""
                          ? String(pnd.als)
                          : "—";
                      const pndAtv =
                        pnd.atv !== null && pnd.atv !== undefined && pnd.atv !== ""
                          ? String(pnd.atv)
                          : "—";

                      return (
                        <div
                          key={`pnd_${pnd.pnd}_${pi}`}
                          className={`px-3 py-2.5 transition-colors ${
                            pndNoTran
                              ? "bg-red-500/5"
                              : pndBelowThr
                                ? "bg-black/5"
                                : "hover:bg-secondary/40"
                          }`}
                        >
                          <div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.7fr_0.8fr] gap-1.5 items-center">
                            <div className="flex items-center gap-1 min-w-0">
                              {pndNoTran && (
                                <Ban className="w-2.5 h-2.5 text-red-500 flex-shrink-0" />
                              )}
                              {pndBelowThr && (
                                <AlertTriangle className="w-2.5 h-2.5 text-black    flex-shrink-0" />
                              )}
                              <p className="text-[10px] font-mono font-semibold text-foreground truncate">
                                #{pnd.pnd}
                              </p>
                            </div>

                            <p
                              className={`text-[10px] font-semibold tabular-nums truncate ${
                                pndNoTran ? "text-red-500" : "text-foreground"
                              }`}
                            >
                              {pnd.collection ?? "0"}
                            </p>

                            <p className="text-[10px] text-muted-foreground tabular-nums">
                              {pnd.ticket_count ?? "0"}
                            </p>

                            <p className="text-[10px] text-foreground tabular-nums">{pndAls}</p>

                            <p className="text-[10px] text-foreground tabular-nums">{pndAtv}</p>

                            <div className="space-y-1">
                              <p
                                className={`text-[9px] font-bold tabular-nums ${
                                  pndPct >= 30 ? "text-primary" : "text-muted-foreground"
                                }`}
                              >
                                {pnd.contribution ?? "0%"}
                              </p>
                              <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    pndNoTran
                                      ? "bg-red-500"
                                      : pndBelowThr
                                        ? "bg-black"
                                        : "bg-primary"
                                  }`}
                                  style={{ width: `${Math.min(pndPct, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {fullStreet ? (
                <button
                  onClick={() => onSelectStreet?.(fullStreet)}
                  className="w-full text-[10px] font-semibold text-primary hover:underline text-center py-1 flex items-center justify-center gap-1"
                >
                  View Street Detail <ChevronRight className="w-3 h-3" />
                </button>
              ) : (
                <p className="text-[9px] text-muted-foreground text-center py-1">
                  Street detail not available
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// DateFilterPanel
// ============================================
const DateFilterPanel = ({
  onApply,
  onReset,
  onClose,
  topPos,
  activeMonth,
  activeYear,
  activeThreshold,
  initialGroup = "",
  initialMonth = "",
  initialDay = null,
  monthGroups = [],
  yearOptions = [],
  optLoading = false,
  optError = null,
}) => {
  const _currentYear = String(new Date().getFullYear());
  const [selYear, setSelYear] = useState(
    activeYear
      ? { value: activeYear, label: activeYear }
      : { value: _currentYear, label: _currentYear }
  );
  const [selGroup, setSelGroup] = useState(initialGroup);
  const [selMonth, setSelMonth] = useState(initialMonth);
  const [selDay, setSelDay] = useState(initialDay);
  const [threshold, setThreshold] = useState(activeThreshold || "");
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  useEffect(() => {
    setSelYear(
      activeYear
        ? { value: activeYear, label: activeYear }
        : { value: _currentYear, label: _currentYear }
    );
    setSelGroup(initialGroup);
    setSelMonth(initialMonth);
    setSelDay(initialDay);
    setThreshold(activeThreshold || "");
  }, [activeYear, initialGroup, initialMonth, initialDay?.value, activeThreshold]);

  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: topPos, left: 328 });
  const [panelHeight, setPanelHeight] = useState(null);
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, top: 0, left: 0 });
  const resizeStart = useRef({ y: 0, height: 0 });

  const handleDragMouseDown = (e) => {
    if (e.target.closest("button")) return;
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      top: panelPos.top,
      left: panelPos.left,
    };
    e.preventDefault();
  };

  const handleResizeMouseDown = (e) => {
    isResizing.current = true;
    resizeStart.current = {
      y: e.clientY,
      height: panelRef.current?.offsetHeight || 400,
    };
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (isDragging.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPanelPos({
          top: Math.max(8, dragStart.current.top + dy),
          left: Math.max(8, dragStart.current.left + dx),
        });
      }
      if (isResizing.current) {
        const dy = e.clientY - resizeStart.current.y;
        const newH = Math.min(
          window.innerHeight - 80,
          Math.max(200, resizeStart.current.height + dy)
        );
        setPanelHeight(newH);
      }
    };
    const onUp = () => {
      isDragging.current = false;
      isResizing.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  useEffect(() => {
    setPanelPos({ top: topPos, left: 328 });
  }, [topPos]);

  useEffect(() => {
    if (!monthGroups || monthGroups.length === 0) return;
    if (initialGroup && initialMonth) {
      setExpandedGroups((prev) => new Set([...prev, initialGroup]));
      setExpandedMonths((prev) => new Set([...prev, initialMonth]));
      return;
    }
    const currentDate = new Date();
    const currentMonthName = MONTH_NAMES[currentDate.getMonth()];
    const currentYearValue = String(currentDate.getFullYear());
    const fullYearGroup = monthGroups.find((g) => g.name === "Full_Year");
    if (fullYearGroup?.children) {
      const currentMonthObj = fullYearGroup.children.find((m) => m.name === currentMonthName);
      if (currentMonthObj) {
        setSelGroup("Full_Year");
        setSelMonth(currentMonthName);
        setSelDay({ value: currentMonthName, label: currentMonthName });
        setSelYear({ value: currentYearValue, label: currentYearValue });
        setExpandedGroups(new Set(["Full_Year"]));
        setExpandedMonths(new Set([currentMonthName]));
      }
    }
  }, [monthGroups, initialGroup, initialMonth]);

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const n = new Set(prev);
      n.has(groupName) ? n.delete(groupName) : n.add(groupName);
      return n;
    });
  };
  const toggleMonth = (monthName) => {
    setExpandedMonths((prev) => {
      const n = new Set(prev);
      n.has(monthName) ? n.delete(monthName) : n.add(monthName);
      return n;
    });
  };
  const handleGroupSelect = (group) => {
    setSelGroup(group.name);
    setSelMonth(group.name);
    setSelDay({ value: group.name, label: group.name === "Full_Year" ? "Full Year" : "FY" });
    toggleGroup(group.name);
  };
  const handleMonthSelect = (groupName, month) => {
    setSelGroup(groupName);
    setSelMonth(month.name);
    setSelDay({ value: month.name, label: month.name });
    if (month.children?.length > 0 && !expandedMonths.has(month.name)) toggleMonth(month.name);
  };
  const handleDaySelect = (groupName, monthName, day) => {
    setSelGroup(groupName);
    setSelMonth(monthName);
    setSelDay({ value: day.name, label: day.name });
  };

  const isComplete = !!selMonth && !!selYear;
  const previewLabel = isComplete ? `${selDay.label} ${selYear.value}` : null;

  const handleApply = () => {
    if (!isComplete) return;
    onApply({
      month: selDay.value,
      year: selYear.value,
      type: selGroup,
      displayMonth: selDay.label || selMonth,
      panelMonth: selMonth,
      threshold: threshold || undefined,
    });
  };
  const handleReset = () => {
    setSelGroup("");
    setSelMonth("");
    setSelDay(null);
    setSelYear(null);
    setThreshold("");
    onReset();
  };

  return (
    <motion.div
      ref={panelRef}
      initial={{ opacity: 0, x: -6, scale: 0.97 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -6, scale: 0.97 }}
      transition={{ duration: 0.16 }}
      style={{
        position: "fixed",
        left: `${panelPos.left}px`,
        top: `${panelPos.top}px`,
        zIndex: 9999,
        width: "280px",
        boxShadow: "0 8px 40px rgba(0,0,0,0.28)",
        height: panelHeight ? `${panelHeight}px` : "auto",
        maxHeight: panelHeight ? "none" : "85vh",
        display: "flex",
        flexDirection: "column",
      }}
      className="rounded-xl border border-border bg-card overflow-hidden"
    >
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-border bg-sidebar flex-shrink-0"
        onMouseDown={handleDragMouseDown}
        style={{ cursor: "move", userSelect: "none" }}
      >
        <div className="flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-primary" />
          <span className="text-xs font-bold text-foreground tracking-tight"> Filter</span>
        </div>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto cb-scroll p-4 space-y-4">
        {optLoading && (
          <div className="flex items-center justify-center gap-2 py-6">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        )}
        {!optLoading && optError && (
          <p className="text-xs text-red-500 text-center py-2">{optError}</p>
        )}
        {!optLoading && !optError && (
          <>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">
                Threshold
              </p>
              <input
                type="number"
                placeholder="Enter threshold..."
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                className="w-full px-3 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
              />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">
                Year
              </p>
              <ReactSelect
                options={yearOptions}
                value={selYear}
                onChange={setSelYear}
                placeholder="Select year..."
              />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">
                Month / Period
              </p>
              <div className="border border-border rounded-lg overflow-hidden bg-background max-h-52 overflow-y-auto cb-scroll">
                {monthGroups.map((group) => {
                  const isGroupExpanded = expandedGroups.has(group.name);
                  const isGroupSelected = selGroup === group.name && selMonth === group.name;
                  return (
                    <div key={group.name}>
                      <button
                        onClick={() => handleGroupSelect(group)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors border-b border-border ${isGroupSelected ? "bg-primary/10" : "hover:bg-muted/50"}`}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroup(group.name);
                          }}
                          className="flex-shrink-0 text-muted-foreground"
                        >
                          {isGroupExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <span className="text-xs font-semibold text-foreground flex-1 text-left">
                          {group.name === "Full_Year" ? "Full Year" : "Fiscal Year (FY)"}
                        </span>
                        {isGroupSelected && (
                          <Check className="w-3 h-3 text-primary flex-shrink-0" />
                        )}
                      </button>
                      {isGroupExpanded &&
                        (group.children || []).map((month) => {
                          const isMonthExpanded = expandedMonths.has(month.name);
                          const isMonthSelected =
                            selGroup === group.name &&
                            selMonth === month.name &&
                            selDay?.value === month.name;
                          const hasDays = month.children?.length > 0;
                          return (
                            <div key={month.name}>
                              <button
                                onClick={() => handleMonthSelect(group.name, month)}
                                className={`w-full flex items-center gap-2 pl-7 pr-3 py-1.5 text-left transition-colors border-b border-border/50 ${isMonthSelected ? "bg-primary/8 text-primary" : "hover:bg-muted/40"}`}
                              >
                                {hasDays ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMonth(month.name);
                                    }}
                                    className="flex-shrink-0 text-muted-foreground"
                                  >
                                    {isMonthExpanded ? (
                                      <ChevronDown className="w-3 h-3" />
                                    ) : (
                                      <ChevronRight className="w-3 h-3" />
                                    )}
                                  </button>
                                ) : (
                                  <div className="w-3" />
                                )}
                                <span
                                  className={`text-xs flex-1 text-left ${isMonthSelected ? "font-semibold text-primary" : "text-foreground"}`}
                                >
                                  {month.name}
                                </span>
                                {isMonthSelected && (
                                  <Check className="w-3 h-3 ml-auto text-primary flex-shrink-0" />
                                )}
                              </button>
                              {hasDays && isMonthExpanded && (
                                <div className="bg-muted/20">
                                  {(month.children || []).map((day) => {
                                    const isDaySelected =
                                      selGroup === group.name &&
                                      selMonth === month.name &&
                                      selDay?.value === day.name;
                                    return (
                                      <button
                                        key={day.name}
                                        onClick={() => handleDaySelect(group.name, month.name, day)}
                                        className={`w-full flex items-center gap-2 pl-14 pr-3 py-1.5 text-left transition-colors border-b border-border/30 ${isDaySelected ? "bg-primary/10 text-primary" : "hover:bg-muted/50"}`}
                                      >
                                        <span
                                          className={`text-[11px] font-mono ${isDaySelected ? "font-semibold text-primary" : "text-foreground"}`}
                                        >
                                          {day.name}
                                        </span>
                                        {isDaySelected && (
                                          <Check className="w-3 h-3 ml-auto text-primary flex-shrink-0" />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
            {previewLabel && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
                <Check className="w-3 h-3 text-primary flex-shrink-0" />
                <span className="text-[10px] text-primary font-semibold">{previewLabel}</span>
                {threshold && (
                  <span className="text-[10px] text-primary font-semibold ml-1">
                    • Threshold: {threshold}
                  </span>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleReset}
                className="flex-1 py-1.5 text-xs font-semibold rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors"
              >
                Reset
              </button>
              <button
                disabled={!isComplete}
                onClick={handleApply}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${isComplete ? "bg-primary text-white hover:bg-primary/90" : "bg-muted text-muted-foreground cursor-not-allowed"}`}
              >
                Apply
              </button>
            </div>
          </>
        )}
      </div>

      <div
        onMouseDown={handleResizeMouseDown}
        className="flex-shrink-0 flex items-center justify-center border-t border-border hover:bg-primary/10 transition-colors"
        style={{ height: "10px", cursor: "ns-resize" }}
      >
        <div className="w-8 h-[3px] rounded-full bg-border" />
      </div>
    </motion.div>
  );
};

// ─── Draggable Legend ───
const BottomLeftLegend = ({ isDarkMode, sidebarOpen = true }) => {
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const legendRef = useRef(null);
  const [legendPos, setLegendPos] = useState(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, top: 0, left: 0 });

  const handleDragMouseDown = (e) => {
    const rect = legendRef.current?.getBoundingClientRect();
    if (!rect) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY, top: rect.top, left: rect.left };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setLegendPos({
        top: Math.max(8, dragStart.current.top + dy),
        left: Math.max(8, dragStart.current.left + dx),
      });
    };
    const onUp = () => {
      isDragging.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const items = [
    { color: "#8B5CF6", label: "BNA", desc: "BNA installed and active", deviceType: "BNA" },
    { color: "#F59E0B", label: "TX", desc: "TX installed and active", deviceType: "TX" },
    { color: "#9CA3AF", label: "Inactive", desc: "PND is currently inactive" },
    { color: "#EF4444", label: "No Transaction", desc: "Installed but no transaction recorded" },
    {
      color: "#000000",
      label: "Below Threshold",
      desc: "Transaction count is below the set threshold",
    },
  ];

  // deviceType diya jaye to marker ke andar us PND type ka icon dikhta hai
  // (map ke actual markers jaisa hi) — warna plain colored dot.
  const MiniMarker = ({ color, slash, deviceType }) => (
    <svg width="14" height="14" viewBox="0 0 64 64" style={{ flexShrink: 0 }}>
      <path d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z" fill={color} />
      <circle cx="32" cy="25" r="11" fill="white" />
      {deviceType ? (
        <g
          transform="translate(25,18) scale(0.583)"
          fill="none"
          stroke={color}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          dangerouslySetInnerHTML={{ __html: getMarkerIconPaths(deviceType) }}
        />
      ) : (
        <circle cx="32" cy="25" r="5" fill={color} />
      )}
      {slash && (
        <line
          x1="10"
          y1="14"
          x2="54"
          y2="46"
          stroke="#EF4444"
          strokeWidth="4.5"
          strokeLinecap="round"
        />
      )}
    </svg>
  );

  return (
    <div
      ref={legendRef}
      style={{
        position: "fixed",
        ...(legendPos
          ? { top: `${legendPos.top}px`, left: `${legendPos.left}px` }
          : { top: "74px", right: "530px" }),
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "8px 14px",
        borderRadius: "8px",
        border: `1px solid ${isDarkMode ? "rgba(75,85,99,0.3)" : "rgba(209,213,219,0.5)"}`,
        backgroundColor: isDarkMode ? "rgba(0,0,0,0.75)" : "rgba(255,255,255,0.85)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        onMouseDown={handleDragMouseDown}
        title="Drag to move"
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "grab",
          color: isDarkMode ? "#6B7280" : "#9CA3AF",
          marginRight: "2px",
          flexShrink: 0,
        }}
      >
        <GripVertical size={14} />
      </div>
      {items.map((item) => (
        <div
          key={item.label}
          onMouseEnter={() => setHoveredLabel(item.label)}
          onMouseLeave={() => setHoveredLabel(null)}
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            cursor: "default",
          }}
        >
          <MiniMarker color={item.color} slash={item.slash} deviceType={item.deviceType} />
          <span
            style={{
              fontSize: "9px",
              fontWeight: 500,
              color: isDarkMode ? "#D1D5DB" : "#374151",
              whiteSpace: "nowrap",
            }}
          >
            {item.label}
          </span>

          {hoveredLabel === item.label && (
            <div
              style={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                marginBottom: "8px",
                padding: "6px 10px",
                borderRadius: "6px",
                backgroundColor: isDarkMode ? "#1F2937" : "#111827",
                color: "#fff",
                fontSize: "10px",
                fontWeight: 400,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                zIndex: 999999,
                pointerEvents: "none",
              }}
            >
              {item.desc}
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderTop: `5px solid ${isDarkMode ? "#1F2937" : "#111827"}`,
                }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================
// GoogleMapComponent
// ============================================================
const GoogleMapComponent = forwardRef(
  (
    {
      citiesData = [],
      countryInfo = null, // ← yeh add karo
      selectedCity,
      selectedStreet,
      selectedPnd,
      onPndClick,
      onCityClick,
      onStreetClick,
      isDarkMode,
      belowThresholdPnds = new Set(),
      noTransactionPnds = new Set(),
      shimmerMode = "overview",
      pndStatusFilters = EMPTY_FILTERS,
      streetsOnlyMode = false,
      pndRevenueMap = new Map(),
      sidebarOpen = true,
      detailPanelWidth = 0,
      filteredMapData = null,
      filteredMapLoading = false,
    },
    forwardedRef
  ) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const polygonsRef = useRef([]);
    const markersRef = useRef([]);
    const animationFrameRef = useRef(null);
    const highlightedMarkerRef = useRef(null);
    const markerMapRef = useRef({});
    const mapReadyRef = useRef(false);
    const streetInfoWindowRef = useRef(null);
    const pndInfoWindowRef = useRef(null);
    const hoveredMarkerActiveRef = useRef(false);
    const labelsVisibleRef = useRef(true); 
    const belowThresholdRef = useRef(belowThresholdPnds);
    const noTransactionRef = useRef(noTransactionPnds);
    const revenueMapRef = useRef(pndRevenueMap);
    const pndStatusFiltersRef = useRef(pndStatusFilters);

    useEffect(() => {
      belowThresholdRef.current = belowThresholdPnds;
    }, [belowThresholdPnds]);
    useEffect(() => {
      noTransactionRef.current = noTransactionPnds;
    }, [noTransactionPnds]);
    useEffect(() => {
      revenueMapRef.current = pndRevenueMap;
    }, [pndRevenueMap]);
    useEffect(() => {
      pndStatusFiltersRef.current = pndStatusFilters;
    }, [pndStatusFilters]);

    const filteredMapDataRef = useRef(filteredMapData);
    useEffect(() => {
      filteredMapDataRef.current = filteredMapData;
    }, [filteredMapData]);

    const [mapLoading, setMapLoading] = useState(true);

    const getPndKey = (pnd) => `${pnd.latitude}_${pnd.longitude}`;

    useEffect(() => {
      if (!document.getElementById("google-maps-script")) {
        const script = document.createElement("script");
        script.id = "google-maps-script";
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_KEY}&libraries=geometry`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }, []);

    useEffect(() => {
      const styleId = "gm-infowindow-no-close-btn";
      if (document.getElementById(styleId)) return;
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
      .gm-style-iw-chr {
        display: none !important;
        height: 0 !important;
        min-height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .gm-ui-hover-effect {
        display: none !important;
      }
      .gm-style-iw-c {
        padding: 6px !important;
        padding-top: 6px !important;
      }
      .gm-style-iw-d {
        overflow: hidden !important;
        padding: 0 !important;
      }
    `;
      document.head.appendChild(style);
    }, []);

       useEffect(() => {
      if (!mapRef.current || mapInstanceRef.current) return;
      const init = () => {
        if (!window.google) {
          setTimeout(init, 100);
          return;
        }
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: 24.0, lng: 45.0 },
          zoom: 6,
          styles: isDarkMode
            ? getDarkMapStyles(!labelsVisibleRef.current)
            : getLightMapStyles(!labelsVisibleRef.current),
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: false,
        });

        const map = mapInstanceRef.current;

        // ── Toggle button hover style + Labels popup animation ──
        if (!document.getElementById("gm-view-toggle-style")) {
          const style = document.createElement("style");
          style.id = "gm-view-toggle-style";
          style.textContent = `
          .gm-view-toggle-btn { transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.12s ease; }
          .gm-view-toggle-btn:hover:not(.gm-view-toggle-active) {
            background: ${isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.06)"} !important;
          }
          .gm-labels-popup {
            transition: opacity 0.25s ease, transform 0.25s ease;
          }
          .gm-labels-popup.gm-hidden {
            opacity: 0;
            transform: translateY(6px) scale(0.95);
            pointer-events: none;
          }
        `;
          document.head.appendChild(style);
        }

        // ── Outer vertical stack: Labels (upar) + Default/Satellite (neeche) ──
        const outerWrapper = document.createElement("div");
        outerWrapper.style.cssText = `
        display: flex; flex-direction: column; align-items: center; gap: 6px;
        margin: 12px;
        font-family: 'Inter', Roboto, Arial, sans-serif; user-select: none;
      `;

        // ── Labels wrapper (initially HIDDEN) ──
        const labelsWrapper = document.createElement("div");
        labelsWrapper.className = "gm-labels-popup gm-hidden";
        labelsWrapper.style.cssText = `
        display: flex; gap: 2px; padding: 3px;
        background: ${isDarkMode ? "rgba(31,41,55,0.95)" : "rgba(255,255,255,0.97)"};
        backdrop-filter: blur(6px);
        border: 1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"};
        border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
        font-size: 12px;
      `;

        // ── Default / Satellite wrapper ──
        const wrapper = document.createElement("div");
        wrapper.style.cssText = `
        display: flex; gap: 2px; padding: 3px;
        background: ${isDarkMode ? "rgba(31,41,55,0.95)" : "rgba(255,255,255,0.97)"};
        backdrop-filter: blur(6px);
        border: 1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"};
        border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
        font-size: 13px;
      `;

        // ── Auto-hide timer ──
        let labelsHideTimer = null;
        const showLabelsBtnTemporarily = () => {
          labelsWrapper.classList.remove("gm-hidden");
          if (labelsHideTimer) clearTimeout(labelsHideTimer);
          labelsHideTimer = setTimeout(() => {
            labelsWrapper.classList.add("gm-hidden");
          }, 2500);
        };
        labelsWrapper.addEventListener("mouseenter", () => {
          if (labelsHideTimer) clearTimeout(labelsHideTimer);
        });
        labelsWrapper.addEventListener("mouseleave", () => {
          if (labelsHideTimer) clearTimeout(labelsHideTimer);
          labelsHideTimer = setTimeout(() => {
            labelsWrapper.classList.add("gm-hidden");
          }, 1500);
        });

        // ── Default/Satellite button banao ──
        const makeBtn = (label, type) => {
          const btn = document.createElement("div");
          btn.textContent = label;
          btn.dataset.type = type;
          btn.className = "gm-view-toggle-btn";
          btn.style.cssText = `
          padding: 7px 16px; cursor: pointer; white-space: nowrap; border-radius: 999px;
          font-weight: 500; letter-spacing: 0.01em;
          color: ${isDarkMode ? "#cbd5e1" : "#475569"};
        `;
          btn.addEventListener("click", () => {
            map.setMapTypeId(type);
            updateActive(type);
            showLabelsBtnTemporarily();
          });
          return btn;
        };

        const defaultBtn = makeBtn("Default View", window.google.maps.MapTypeId.ROADMAP);
        const satelliteBtn = makeBtn("Satellite View", "hybrid");

        const updateActive = (activeType) => {
          [defaultBtn, satelliteBtn].forEach((btn) => {
            const isActive = btn.dataset.type === activeType;
            btn.classList.toggle("gm-view-toggle-active", isActive);
            btn.style.background = isActive ? "var(--primary)" : "transparent";
            btn.style.color = isActive
              ? "var(--primary-foreground)"
              : isDarkMode
                ? "#cbd5e1"
                : "#475569";
            btn.style.fontWeight = isActive ? "600" : "500";
            btn.style.boxShadow = isActive ? "0 1px 4px rgba(73,127,253,0.4)" : "none";
          });
        };
        updateActive(window.google.maps.MapTypeId.ROADMAP);

        // ── Labels toggle button (initially hidden) ──
        const labelsBtn = document.createElement("div");
        labelsBtn.textContent = "Labels";
        labelsBtn.className = "gm-view-toggle-btn";
        labelsBtn.style.cssText = `
        padding: 5px 14px; cursor: pointer; white-space: nowrap; border-radius: 999px;
        font-weight: 500; letter-spacing: 0.01em;
        color: ${isDarkMode ? "#cbd5e1" : "#475569"};
      `;

        const applyMapStyles = () => {
          map.setOptions({
            styles: isDarkMode
              ? getDarkMapStyles(!labelsVisibleRef.current)
              : getLightMapStyles(!labelsVisibleRef.current),
          });
        };

        const updateLabelsBtnStyle = () => {
          const isOn = labelsVisibleRef.current;
          labelsBtn.classList.toggle("gm-view-toggle-active", isOn);
          labelsBtn.style.background = isOn ? "var(--primary)" : "transparent";
          labelsBtn.style.color = isOn
            ? "var(--primary-foreground)"
            : isDarkMode
              ? "#cbd5e1"
              : "#475569";
          labelsBtn.style.fontWeight = isOn ? "600" : "500";
          labelsBtn.style.boxShadow = isOn ? "0 1px 4px rgba(73,127,253,0.4)" : "none";
        };
        updateLabelsBtnStyle();

        labelsBtn.addEventListener("click", () => {
          labelsVisibleRef.current = !labelsVisibleRef.current;
          applyMapStyles();
          updateLabelsBtnStyle();
          showLabelsBtnTemporarily();
        });

        // ── Assemble ──
        labelsWrapper.appendChild(labelsBtn);
        wrapper.appendChild(defaultBtn);
        wrapper.appendChild(satelliteBtn);
        outerWrapper.appendChild(labelsWrapper); // upar (initially hidden)
        outerWrapper.appendChild(wrapper); // neeche

        map.controls[window.google.maps.ControlPosition.BOTTOM_CENTER].push(outerWrapper);
      };
      init();
    }, [isDarkMode]);

        useEffect(() => {
      if (mapInstanceRef.current)
        mapInstanceRef.current.setOptions({
          styles: isDarkMode
            ? getDarkMapStyles(!labelsVisibleRef.current)
            : getLightMapStyles(!labelsVisibleRef.current),
        });
    }, [isDarkMode]);

    const createMarkerIcon = useCallback(
      (deviceType, color, isSelected = false, isNoTransaction = false) => {
        const size = isSelected ? 50 : 38;
        const opacity = isSelected ? "1" : "0.85";
        const iconPaths = getMarkerIconPaths(deviceType);
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">
      <path d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z" fill="${color}" opacity="${opacity}"/>
      <circle cx="32" cy="25" r="11" fill="white" opacity="${opacity}"/>
      <g transform="translate(25,18) scale(0.583)" fill="none" stroke="${color}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" opacity="${opacity}">
        ${iconPaths}
      </g>
    </svg>`;
        return {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
          scaledSize: new window.google.maps.Size(size, size),
          anchor: new window.google.maps.Point(size / 2, size),
        };
      },
      []
    );

    const sortByPrincipalAxis = useCallback((coords) => {
      if (coords.length <= 2) return coords;
      const cx = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
      const cy = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
      let xx = 0,
        xy = 0,
        yy = 0;
      coords.forEach((c) => {
        const dx = c.lng - cx;
        const dy = c.lat - cy;
        xx += dx * dx;
        xy += dx * dy;
        yy += dy * dy;
      });
      const angle = Math.atan2(2 * xy, xx - yy) / 2;
      const ax = Math.cos(angle);
      const ay = Math.sin(angle);
      return [...coords].sort((a, b) => {
        const pa = (a.lng - cx) * ax + (a.lat - cy) * ay;
        const pb = (b.lng - cx) * ax + (b.lat - cy) * ay;
        return pa - pb;
      });
    }, []);

    const createRoadPolygon = useCallback(
      (points, offsetAmount) => {
        const sorted = sortByPrincipalAxis(points);
        const leftSide = [];
        const rightSide = [];
        sorted.forEach((point, index) => {
          const prev = sorted[index - 1] || point;
          const next = sorted[index + 1] || point;
          const dx = next.lng - prev.lng;
          const dy = next.lat - prev.lat;
          const safeLength = Math.sqrt(dx * dx + dy * dy) || 1;
          const nx = -dy / safeLength;
          const ny = dx / safeLength;
          leftSide.push({ lat: point.lat + ny * offsetAmount, lng: point.lng + nx * offsetAmount });
          rightSide.push({
            lat: point.lat - ny * offsetAmount,
            lng: point.lng - nx * offsetAmount,
          });
        });
        return [...leftSide, ...rightSide.reverse()];
      },
      [sortByPrincipalAxis]
    );

    const computeBoundingBox = (points) => {
      if (!points.length) return null;
      let minLat = Infinity,
        maxLat = -Infinity,
        minLng = Infinity,
        maxLng = -Infinity;
      points.forEach((p) => {
        minLat = Math.min(minLat, p.lat);
        maxLat = Math.max(maxLat, p.lat);
        minLng = Math.min(minLng, p.lng);
        maxLng = Math.max(maxLng, p.lng);
      });
      return { minLat, maxLat, minLng, maxLng };
    };

    const expandBoundingBox = (bbox, meters = 150) => {
      if (!bbox) return null;
      const latCenter = (bbox.minLat + bbox.maxLat) / 2;
      const latDelta = meters / 111320;
      const lngDelta = meters / (111320 * Math.cos((latCenter * Math.PI) / 180));
      return {
        minLat: bbox.minLat - latDelta,
        maxLat: bbox.maxLat + latDelta,
        minLng: bbox.minLng - lngDelta,
        maxLng: bbox.maxLng + lngDelta,
      };
    };

    const bboxToLatLngArray = (bbox) => {
      if (!bbox) return [];
      return [
        { lat: bbox.minLat, lng: bbox.minLng },
        { lat: bbox.minLat, lng: bbox.maxLng },
        { lat: bbox.maxLat, lng: bbox.maxLng },
        { lat: bbox.maxLat, lng: bbox.minLng },
      ];
    };

    const buildBboxRect = useCallback((pnds, expandMeters = 300) => {
      const valid = pnds.filter(
        (p) =>
          p.latitude &&
          p.longitude &&
          !isNaN(parseFloat(p.latitude)) &&
          !isNaN(parseFloat(p.longitude))
      );
      if (!valid.length) return null;
      const raw = valid.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));
      return bboxToLatLngArray(expandBoundingBox(computeBoundingBox(raw), expandMeters)).map(
        (p) => new window.google.maps.LatLng(p.lat, p.lng)
      );
    }, []);

    const smoothFlyTo = useCallback(
      (map, targetBounds, targetZoom, onComplete) => {
        if (!map || !window.google) return;
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

        const padding = {
          top: 60,
          bottom: 60,
          left: 40,
          right: detailPanelWidth > 0 ? detailPanelWidth + 40 : 40,
        };

        const startCenter = map.getCenter();
        const startZoom = map.getZoom();
        const prelimCenter = targetBounds.getCenter();
        const midZoom = Math.min(startZoom, targetZoom || 15) - 1.5;
        const duration = 900;
        let startTime = null;
        const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

        const step = (ts) => {
          if (!startTime) startTime = ts;
          const rawT = Math.min((ts - startTime) / duration, 1);
          const t = ease(rawT);
          map.panTo({
            lat: startCenter.lat() + (prelimCenter.lat() - startCenter.lat()) * t,
            lng: startCenter.lng() + (prelimCenter.lng() - startCenter.lng()) * t,
          });
          map.setZoom(
            rawT < 0.4
              ? startZoom + (midZoom - startZoom) * ease(rawT / 0.4)
              : midZoom + ((targetZoom || 15) - midZoom) * ease((rawT - 0.4) / 0.6)
          );
          if (rawT < 1) {
            animationFrameRef.current = requestAnimationFrame(step);
          } else {
            window.google.maps.event.addListenerOnce(map, "idle", () => {
              if (targetZoom) {
                const fittedZoom = map.getZoom();
                if (fittedZoom > targetZoom) {
                  map.setZoom(targetZoom);
                }
              }
            });
            map.fitBounds(targetBounds, padding);
            onComplete?.();
          }
        };
        animationFrameRef.current = requestAnimationFrame(step);
      },
      [detailPanelWidth]
    );

    const clearAll = useCallback(() => {
      polygonsRef.current.forEach((p) => p?.setMap?.(null));
      polygonsRef.current = [];
      markersRef.current.forEach((m) => m?.setMap?.(null));
      markersRef.current = [];
      markerMapRef.current = {};
      highlightedMarkerRef.current = null;
      streetInfoWindowRef.current?.close();
      pndInfoWindowRef.current?.close();
    }, []);

    // Polygon border pe halka "glow / shadow" effect dene ke liye — Google Maps
    // ka Polygon stroke native blur/box-shadow support nahi karta, isliye hum
    // asal (crisp, colored) border ke NEECHE 3 dark, semi-transparent aur
    // progressively patlay strokes wale invisible-fill polygons stack karte
    // hain. Result: bilkul waisa hi soft dark halo jaisa screenshot mein
    // airport boundary ke border ke ird-gird dikh raha hai. Yeh function
    // polygon banane se PEHLE call karo aur return hone wale sab polygons
    // ko bhi polygonsRef.current mein push karo taake clearAll() unhe hata sake.
    const addPolygonGlow = useCallback((map, paths, zIndexBase = 1) => {
      if (!map || !window.google) return [];
      const glowLayers = [
        { weight: 20, opacity: 0.06 },
        { weight: 16, opacity: 0.1 },
        { weight: 12, opacity: 0.16 },
        { weight: 8, opacity: 0.24 },
        { weight: 5, opacity: 0.34 },
      ];
      return glowLayers.map(
        (layer, i) =>
          new window.google.maps.Polygon({
            paths,
            strokeColor: "#000000",
            strokeOpacity: layer.opacity,
            strokeWeight: layer.weight,
            fillOpacity: 0,
            map,
            zIndex: zIndexBase - (glowLayers.length - i),
            clickable: false,
          })
      );
    }, []);

    // API coordinates → valid {lat,lng}[] (null/empty/invalid → [])
const parseApiCoords = (coords) => {
  if (!Array.isArray(coords) || !coords.length) return [];
  return coords
    .map((c) => {
      const lat = parseFloat(c?.lat ?? c?.latitude);
      const lng = parseFloat(c?.lng ?? c?.longitude);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { lat, lng };
    })
    .filter(Boolean);
};

    const highlightMarker = useCallback(
      (pnd) => {
        if (!pnd || !window.google) return;
        const key = getPndKey(pnd);
        Object.values(markerMapRef.current).forEach((m) => {
          m.setIcon(
            createMarkerIcon(
              m.__pndDeviceType,
              m.__deviceColor || getDeviceColor(m.__pndDeviceType),
              false,
              m.__isNoTransaction || false
            )
          );
          m.setZIndex(100);
        });
        const m = markerMapRef.current[key];
        if (m) {
          m.setIcon(
            createMarkerIcon(
              pnd.device_type,
              m.__deviceColor || getDeviceColor(pnd.device_type),
              true,
              m.__isNoTransaction || false
            )
          );
          m.setZIndex(300);
          highlightedMarkerRef.current = m;
        }
      },
      [createMarkerIcon]
    );

    const getMarkerColor = useCallback(
      (deviceType, isBelowThreshold, isNoTransaction, isInactive) => {
        if (isInactive) return "#9CA3AF";
        if (isNoTransaction) return "#EF4444";
        if (isBelowThreshold) return "#000000";
        return getDeviceColor(deviceType);
      },
      []
    );

    const addPndMarker = useCallback(
      (pnd, index, map) => {
        const lat = parseFloat(pnd.latitude);
        const lng = parseFloat(pnd.longitude);
        if (isNaN(lat) || isNaN(lng)) return;
        const pndNumStr = String(pnd.pnd_number ?? pnd.pnd ?? "");
        const isBelowThreshold = belowThresholdRef.current.has(pndNumStr);
        const isNoTransaction = noTransactionRef.current.has(pndNumStr);
        const isInactive = pnd.device_status === "INACTIVE";

        const deviceColor = getMarkerColor(
          pnd.device_type,
          isBelowThreshold,
          isNoTransaction,
          isInactive
        );

        const pndKey = getPndKey(pnd);
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map,
          icon: createMarkerIcon(pnd.device_type, deviceColor, false, isNoTransaction),
          zIndex: 100 + index,
        });
        marker.__pndKey = pndKey;
        marker.__pndDeviceType = pnd.device_type;
        marker.__deviceColor = deviceColor;
        marker.__isNoTransaction = isNoTransaction;
        marker.__isInactive = isInactive;
        marker.__pndNumber = pndNumStr;
        markerMapRef.current[pndKey] = marker;

        marker.addListener("mouseover", () => {
          hoveredMarkerActiveRef.current = true;
          streetInfoWindowRef.current?.close();

          if (highlightedMarkerRef.current !== marker) {
            marker.setIcon(
              createMarkerIcon(
                pnd.device_type,
                marker.__deviceColor,
                true,
                marker.__isNoTransaction
              )
            );
          }

          const revInfo = revenueMapRef.current.get(pndNumStr);
          const dTypeName = getDeviceTypeName(pnd.device_type);

          if (!pndInfoWindowRef.current) {
            pndInfoWindowRef.current = new window.google.maps.InfoWindow({ disableAutoPan: true });
          }

          const revenueHtml = revInfo
            ? `<div style="margin-top:3px;font-size:11px;font-weight:700;color:#059669;">Revenue: ${revInfo.collection ?? "0"}</div>
           <div style="font-size:10px;color:#6b7280;margin-top:1px;">Tickets: ${revInfo.ticket_count ?? "0"}</div>`
            : `<div style="margin-top:3px;font-size:10px;color:#9ca3af;">No revenue data for this period</div>`;

          const datesHtml = `<div style="font-size:10px;color:#6b7280;margin-top:3px;">Inactive Date: ${revInfo?.device_status_date ?? pnd.device_status_date ?? "—"}</div>
           <div style="font-size:10px;color:#6b7280;margin-top:1px;">Active Date: ${revInfo?.device_active_date ?? pnd.device_active_date ?? "—"}</div>`;

          pndInfoWindowRef.current.setContent(
            `<div style="padding:3px 5px;min-width:120px;font-family:sans-serif;">
          <div style="font-size:12px;font-weight:700;color:#111827;">${pnd.pnd_number ? "#" + pnd.pnd_number + " — " : ""}${dTypeName}</div>
          ${revenueHtml}
          ${datesHtml}
        </div>`
          );
          pndInfoWindowRef.current.open({ anchor: marker, map });
        });

        marker.addListener("mouseout", () => {
          hoveredMarkerActiveRef.current = false;

          if (highlightedMarkerRef.current !== marker) {
            marker.setIcon(
              createMarkerIcon(
                pnd.device_type,
                marker.__deviceColor,
                false,
                marker.__isNoTransaction
              )
            );
          }
          pndInfoWindowRef.current?.close();
        });

        marker.addListener("click", () => {
          highlightMarker(pnd);
          onPndClick(pnd);
        });
        markersRef.current.push(marker);
      },
      [createMarkerIcon, highlightMarker, onPndClick, getMarkerColor]
    );

    const filterPndsForCityStreet = useCallback(
      (allStreetPnds, streetCd) => {
        if (!pndStatusFilters || pndStatusFilters.length === 0) return allStreetPnds;
        if (pndStatusFilters.includes("all")) return allStreetPnds;

        const fmd = filteredMapDataRef.current;
        if (!fmd || fmd.type !== "city") return [];

        const filteredStreet = fmd.streets.find((s) => s.street_cd === streetCd);
        if (!filteredStreet) return [];

        const isBelowThresholdFilter =
          pndStatusFilters.length === 1 && pndStatusFilters[0] === "below_threshold";

        const backendPnds = (filteredStreet.pnds || []).filter((p) => {
          if (!isBelowThresholdFilter) return true;
          const tc = parseFloat(String(p.ticket_count ?? "0").replace(/,/g, ""));
          return !isNaN(tc) && tc > 0;
        });

        const allowedPndNums = new Set(backendPnds.map((p) => String(p.pnd)));
        return allStreetPnds.filter((p) => allowedPndNums.has(String(p.pnd_number ?? p.pnd)));
      },
      [pndStatusFilters]
    );

    const filterPndsForStreet = useCallback(
      (allPnds) => {
        if (!pndStatusFilters || pndStatusFilters.length === 0) return allPnds;
        if (pndStatusFilters.includes("all")) return allPnds;

        const fmd = filteredMapDataRef.current;
        if (!fmd || fmd.type !== "street") return [];

        const isBelowThresholdFilter =
          pndStatusFilters.length === 1 && pndStatusFilters[0] === "below_threshold";

        const backendPnds = (fmd.pnds || []).filter((p) => {
          if (!isBelowThresholdFilter) return true;
          const tc = parseFloat(String(p.ticket_count ?? "0").replace(/,/g, ""));
          return !isNaN(tc) && tc > 0;
        });

        const allowedPndNums = new Set(backendPnds.map((p) => String(p.pnd)));
        return allPnds.filter((p) => allowedPndNums.has(String(p.pnd_number ?? p.pnd)));
      },
      [pndStatusFilters]
    );

  const drawAllCities = useCallback(() => {
  const map = mapInstanceRef.current;
  if (!map || !window.google) return;
  setMapLoading(true);
  clearAll();

  const bounds = new window.google.maps.LatLngBounds();
  let hasBounds = false;

  // Country boundary polygon from API (`data.coordinates`)
  const apiCoords = countryInfo?.coordinates;
  if (Array.isArray(apiCoords) && apiCoords.length >= 3) {
    const path = parseApiCoords(apiCoords);
    if (path.length >= 3) {
      polygonsRef.current.push(...addPolygonGlow(map, path, 0));
      polygonsRef.current.push(
        new window.google.maps.Polygon({
          paths: path,
          strokeColor: "#3B82F6",
          strokeOpacity: 0.9,
          strokeWeight: 2.5,
          fillColor: "#3B82F6",
          fillOpacity: 0.12,
          map,
          zIndex: 0,
          clickable: false,
        })
      );
      path.forEach((p) => {
        bounds.extend(p);
        hasBounds = true;
      });
    }
  }

  (citiesData || []).forEach((city, idx) => {
    const color = STREET_COLORS[idx % STREET_COLORS.length];
    const darkColor = darkenColor(color, 0.35);

    const allPnds = (city.streets || []).flatMap((s) => s.pnds || []);
    const valid = allPnds.filter(
      (p) =>
        p.latitude &&
        p.longitude &&
        !isNaN(parseFloat(p.latitude)) &&
        !isNaN(parseFloat(p.longitude))
    );

    // ── City polygon: API coordinates prefer, warna PND-based bbox default ──
    const apiCityPath = parseApiCoords(city?.coordinates);
    let cityPath =
      apiCityPath.length >= 3
        ? apiCityPath
        : (() => {
            const rect = buildBboxRect(valid, 300);
            if (!rect || rect.length < 3) return [];
            return rect.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
          })();
    let centLat = null;
    let centLng = null;

    if (cityPath.length >= 3) {
      cityPath.forEach((p) => {
        bounds.extend(p);
        hasBounds = true;
      });
      polygonsRef.current.push(...addPolygonGlow(map, cityPath, 1));
      const cityPoly = new window.google.maps.Polygon({
        paths: cityPath,
        strokeColor: darkColor,
        strokeOpacity: 0.95,
        strokeWeight: 2.5,
        fillColor: color,
        fillOpacity: 0.15,
        map,
        zIndex: 1,
        clickable: true,
      });
      cityPoly.addListener("click", () => onCityClick?.(city));
      polygonsRef.current.push(cityPoly);

      centLat = cityPath.reduce((s, p) => s + p.lat, 0) / cityPath.length;
      centLng = cityPath.reduce((s, p) => s + p.lng, 0) / cityPath.length;
    }

    // Label centroid: API path → warna PNDs average
    if (centLat === null || centLng === null) {
      if (!valid.length) return;
      centLat = valid.reduce((s, p) => s + parseFloat(p.latitude), 0) / valid.length;
      centLng = valid.reduce((s, p) => s + parseFloat(p.longitude), 0) / valid.length;
    }

    valid.forEach((p) => {
      bounds.extend({
        lat: parseFloat(p.latitude),
        lng: parseFloat(p.longitude),
      });
      hasBounds = true;
    });

    const name = (city.city_name || "City").slice(0, 14);
    const pndCnt = city.total_pnds || valid.length;

    const labelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="36" viewBox="0 0 90 36">
      <rect x="0" y="0" width="90" height="36" rx="8" fill="${color}" opacity="0.9"/>
      <text x="45" y="13" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${name}</text>
      <text x="45" y="26" font-family="sans-serif" font-size="8" fill="white" opacity="0.9" text-anchor="middle" dominant-baseline="central">${pndCnt} PNDs</text>
    </svg>`;

    const labelMarker = new window.google.maps.Marker({
      position: { lat: centLat, lng: centLng },
      map,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(labelSvg)}`,
        scaledSize: new window.google.maps.Size(90, 36),
        anchor: new window.google.maps.Point(45, 18),
      },
      title: city.city_name,
      zIndex: 50,
    });

    labelMarker.addListener("click", () => onCityClick?.(city));
    markersRef.current.push(labelMarker);
  });

  if (hasBounds && !bounds.isEmpty()) {
    map.fitBounds(bounds, 48);
  }
  setTimeout(() => setMapLoading(false), 400);
}, [citiesData, countryInfo, clearAll, onCityClick, addPolygonGlow, buildBboxRect]);

   const drawCityView = useCallback(
  (city) => {
    const map = mapInstanceRef.current;
    if (!map || !window.google || !city) return;
    setMapLoading(true);
    clearAll();

    const allPnds = (city.streets || []).flatMap((s) => s.pnds || []);
    const validAll = allPnds.filter(
      (p) =>
        p.latitude &&
        p.longitude &&
        !isNaN(parseFloat(p.latitude)) &&
        !isNaN(parseFloat(p.longitude))
    );
    if (!validAll.length) {
      setMapLoading(false);
      return;
    }

    const cityBounds = new window.google.maps.LatLngBounds();
    validAll.forEach((p) =>
      cityBounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
    );

    // ── City backdrop: API coordinates prefer, warna PND-based bbox default ──
    const apiCityPath = parseApiCoords(city?.coordinates);
    const cityPath =
      apiCityPath.length >= 3
        ? apiCityPath
        : (() => {
            const rect = buildBboxRect(validAll, 300);
            if (!rect || rect.length < 3) return [];
            return rect.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
          })();
    if (cityPath.length >= 3) {
      cityPath.forEach((p) => cityBounds.extend(p));
      polygonsRef.current.push(...addPolygonGlow(map, cityPath));
      polygonsRef.current.push(
        new window.google.maps.Polygon({
          paths: cityPath,
          strokeColor: "#1F2937",
          strokeOpacity: 0.75,
          strokeWeight: 2.5,
          fillColor: "#6366F1",
          fillOpacity: 0.15,
          map,
          zIndex: 1,
          clickable: false,
        })
      );
    }

    (city.streets || []).forEach((street, si) => {
      const allStreetPnds = (street.pnds || []).filter(
        (p) =>
          p.latitude &&
          p.longitude &&
          !isNaN(parseFloat(p.latitude)) &&
          !isNaN(parseFloat(p.longitude))
      );
      let sPnds = filterPndsForCityStreet(allStreetPnds, street.street_cd);

      if (selectedPnd) {
        const selKey = getPndKey(selectedPnd);
        const alreadyThere = sPnds.some((p) => getPndKey(p) === selKey);
        if (!alreadyThere) {
          const match = allStreetPnds.find((p) => getPndKey(p) === selKey);
          if (match) sPnds = [...sPnds, match];
        }
      }

      if (!sPnds.length) return;
      const sColor = STREET_COLORS[si % STREET_COLORS.length];
      const sDark = darkenColor(sColor, 0.35);

      // ── Street polygon: API coordinates prefer, warna createRoadPolygon default ──
      const apiStreetPath = parseApiCoords(street?.coordinates);
      const streetPath =
        apiStreetPath.length >= 3
          ? apiStreetPath
          : (() => {
              const pts = sPnds.map((p) => ({
                lat: parseFloat(p.latitude),
                lng: parseFloat(p.longitude),
              }));
              if (pts.length < 2) return [];
              return createRoadPolygon(pts, 0.00018);
            })();
      if (streetPath.length >= 3) {
        streetPath.forEach((p) => cityBounds.extend(p));
        polygonsRef.current.push(...addPolygonGlow(map, streetPath, 2));
        const streetPolygon = new window.google.maps.Polygon({
          paths: streetPath,
          strokeColor: sDark,
          strokeOpacity: 0.95,
          strokeWeight: 2.75,
          fillColor: sColor,
          fillOpacity: 0.5,
          map,
          zIndex: 2,
          clickable: true,
        });

        const openStreetTooltip = (e) => {
          if (!streetInfoWindowRef.current) {
            streetInfoWindowRef.current = new window.google.maps.InfoWindow({
              disableAutoPan: true,
            });
          }
          streetInfoWindowRef.current.setContent(
            `<div style="font-size:12px;font-weight:600;padding:2px 4px;white-space:nowrap;">${street.street_name || "Street"}</div>`
          );
          streetInfoWindowRef.current.setPosition(e.latLng);
          streetInfoWindowRef.current.open({ map, shouldFocus: false });
        };

        streetPolygon.addListener("mouseover", (e) => {
          streetPolygon.setOptions({ fillOpacity: 0.7, strokeWeight: 4 });
          if (hoveredMarkerActiveRef.current) return;
          openStreetTooltip(e);
        });
        streetPolygon.addListener("mousemove", (e) => {
          if (hoveredMarkerActiveRef.current) {
            streetInfoWindowRef.current?.close();
            return;
          }
          openStreetTooltip(e);
        });
        streetPolygon.addListener("mouseout", () => {
          streetPolygon.setOptions({ fillOpacity: 0.5, strokeWeight: 2.75 });
          streetInfoWindowRef.current?.close();
        });
        streetPolygon.addListener("click", () => {
          onStreetClick?.(street);
        });

        polygonsRef.current.push(streetPolygon);
      }

      sPnds.forEach((pnd, pi) => addPndMarker(pnd, pi, map));
    });

    const sw = cityBounds.getSouthWest();
    const ne = cityBounds.getNorthEast();
    const pad = 0.005;
    smoothFlyTo(
      map,
      new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(sw.lat() - pad, sw.lng() - pad),
        new window.google.maps.LatLng(ne.lat() + pad, ne.lng() + pad)
      ),
      19,
      () => {
        setMapLoading(false);
        if (selectedPnd) highlightMarker(selectedPnd);
      }
    );
  },
  [
    clearAll,
    addPolygonGlow,
    addPndMarker,
    smoothFlyTo,
    onStreetClick,
    filterPndsForCityStreet,
    selectedPnd,
    highlightMarker,
    buildBboxRect,
    createRoadPolygon,
  ]
);

   const drawStreetView = useCallback(
  (street) => {
    const map = mapInstanceRef.current;
    if (!map || !window.google || !street) return;
    setMapLoading(true);
    clearAll();

    const valid = (street.pnds || []).filter(
      (p) =>
        p.latitude &&
        p.longitude &&
        !isNaN(parseFloat(p.latitude)) &&
        !isNaN(parseFloat(p.longitude))
    );
    let filteredValid = filterPndsForStreet(valid);

    if (selectedPnd) {
      const selKey = getPndKey(selectedPnd);
      const alreadyThere = filteredValid.some((p) => getPndKey(p) === selKey);
      if (!alreadyThere) {
        const match = valid.find((p) => getPndKey(p) === selKey);
        if (match) filteredValid = [...filteredValid, match];
      }
    }

    if (!filteredValid.length) {
      setMapLoading(false);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    filteredValid.forEach((p) =>
      bounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
    );

    // ── Street polygon: API coordinates prefer, warna createRoadPolygon default ──
    const apiStreetPath = parseApiCoords(street?.coordinates);
    const streetPath =
      apiStreetPath.length >= 3
        ? apiStreetPath
        : (() => {
            const pts = filteredValid.map((p) => ({
              lat: parseFloat(p.latitude),
              lng: parseFloat(p.longitude),
            }));
            if (pts.length < 2) return [];
            return createRoadPolygon(pts, 0.00018);
          })();
    if (streetPath.length >= 3) {
      streetPath.forEach((p) => bounds.extend(p));
      polygonsRef.current.push(...addPolygonGlow(map, streetPath));
      polygonsRef.current.push(
        new window.google.maps.Polygon({
          paths: streetPath,
          strokeColor: "#1F2937",
          strokeOpacity: 0.85,
          strokeWeight: 3,
          fillColor: "#6366F1",
          fillOpacity: 0.5,
          map,
          zIndex: 1,
          clickable: false,
        })
      );
    }

    filteredValid.forEach((pnd, i) => addPndMarker(pnd, i, map));

    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const pad = 0.004;
    smoothFlyTo(
      map,
      new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(sw.lat() - pad, sw.lng() - pad),
        new window.google.maps.LatLng(ne.lat() + pad, ne.lng() + pad)
      ),
      18,
      () => {
        setMapLoading(false);
        if (selectedPnd) highlightMarker(selectedPnd);
      }
    );
  },
  [
    clearAll,
    addPolygonGlow,
    addPndMarker,
    smoothFlyTo,
    filterPndsForStreet,
    selectedPnd,
    highlightMarker,
    createRoadPolygon,
  ]
);

    const drawSinglePnd = useCallback(
      (pnd) => {
        const map = mapInstanceRef.current;
        if (!map || !window.google || !pnd?.latitude || !pnd?.longitude) return;
        setMapLoading(true);
        clearAll();
        const lat = parseFloat(pnd.latitude);
        const lng = parseFloat(pnd.longitude);
        if (isNaN(lat) || isNaN(lng)) {
          setMapLoading(false);
          return;
        }
        const dc = getDeviceColor(pnd.device_type);
        markersRef.current.push(
          new window.google.maps.Marker({
            position: { lat, lng },
            map,
            icon: createMarkerIcon(pnd.device_type, dc, true),
            animation: window.google.maps.Animation.DROP,
            zIndex: 200,
          })
        );
        smoothFlyTo(map, new window.google.maps.LatLngBounds({ lat, lng }, { lat, lng }), 19, () =>
          setMapLoading(false)
        );
      },
      [clearAll, createMarkerIcon, smoothFlyTo]
    );

    useEffect(() => {
      if (mapReadyRef.current) return;
      const check = () => {
        if (!window.google || !mapInstanceRef.current) {
          setTimeout(check, 200);
          return;
        }
        mapReadyRef.current = true;
        drawAllCities();
      };
      check();
    }, [citiesData, drawAllCities]);

    useEffect(() => {
      if (!mapReadyRef.current) return;
      if (!selectedCity && !selectedStreet && !selectedPnd) drawAllCities();
      else if (selectedCity && !selectedStreet && !selectedPnd) drawCityView(selectedCity);
    }, [selectedCity, selectedStreet, selectedPnd, drawAllCities, drawCityView]);

    const prevStreetRef = useRef(null);

    useEffect(() => {
      if (!mapReadyRef.current || !selectedStreet) return;
      if (prevStreetRef.current === selectedStreet.street_cd) return;
      prevStreetRef.current = selectedStreet.street_cd;
      drawStreetView(selectedStreet);
    }, [selectedStreet, drawStreetView]);

    useEffect(() => {
      if (!mapReadyRef.current || !selectedCity || selectedStreet || selectedPnd) return;
      if (pndStatusFilters.length > 0 && !pndStatusFilters.includes("all") && filteredMapLoading)
        return;
      drawCityView(selectedCity);
    }, [
      pndStatusFilters,
      filteredMapData,
      filteredMapLoading,
      drawCityView,
      selectedCity,
      selectedStreet,
      selectedPnd,
    ]);

    useEffect(() => {
      if (!mapReadyRef.current || !selectedStreet) return;
      if (pndStatusFilters.length > 0 && !pndStatusFilters.includes("all") && filteredMapLoading)
        return;
      drawStreetView(selectedStreet);
    }, [pndStatusFilters, filteredMapData, filteredMapLoading, drawStreetView, selectedStreet]);

    useEffect(() => {
      if (!mapReadyRef.current) return;
      if (selectedCity && !selectedStreet && !selectedPnd) drawCityView(selectedCity);
      else if (selectedStreet) drawStreetView(selectedStreet);
    }, [streetsOnlyMode]);

    useEffect(() => {
      if (!mapReadyRef.current || !selectedPnd) return;
      const hasMarkers = Object.keys(markerMapRef.current).length > 0;
      if (hasMarkers) {
        setMapLoading(true);
        highlightMarker(selectedPnd);
        const t = setTimeout(() => setMapLoading(false), 600);
        return () => clearTimeout(t);
      } else {
        drawSinglePnd(selectedPnd);
      }
    }, [selectedPnd, highlightMarker, drawSinglePnd]);

    useEffect(() => {
      return () => {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      };
    }, []);

    useEffect(() => {
      if (!mapReadyRef.current || !window.google) return;
      Object.values(markerMapRef.current).forEach((marker) => {
        const pndNum = marker.__pndNumber || "";
        const isBelowThr = belowThresholdPnds.has(pndNum);
        const isNoTran = noTransactionPnds.has(pndNum);
        const isInactive = marker.__isInactive;

        const newColor = getMarkerColor(marker.__pndDeviceType, isBelowThr, isNoTran, isInactive);

        marker.__deviceColor = newColor;
        marker.__isNoTransaction = isNoTran;
        marker.setIcon(createMarkerIcon(marker.__pndDeviceType, newColor, false, isNoTran));
      });
    }, [belowThresholdPnds, noTransactionPnds, createMarkerIcon, getMarkerColor]);

    const snapshotOverlayRef = useRef(null);

    const prepareExportSnapshot = useCallback(async () => {
      const map = mapInstanceRef.current;
      const container = mapRef.current;
      if (!map || !container || !window.google?.maps) return false;
      const projection = map.getProjection();
      const center = map.getCenter();
      const zoom = Math.round(map.getZoom() ?? 6);
      if (!projection || !center) return false;

      const rect = container.getBoundingClientRect();
      const cssW = Math.max(1, Math.round(rect.width));
      const cssH = Math.max(1, Math.round(rect.height));

      const scale = 2;
      const overRatio = Math.max(cssW / 640, cssH / 640, 1);
      const zoomStepDown = overRatio > 1 ? Math.ceil(Math.log2(overRatio)) : 0;
      const factor = 2 ** zoomStepDown;
      const staticZoom = Math.max(0, zoom - zoomStepDown);
      const staticW = Math.min(640, Math.round(cssW / factor));
      const staticH = Math.min(640, Math.round(cssH / factor));

            const styleParams = stylesToStaticParams(
        isDarkMode
          ? getDarkMapStyles(!labelsVisibleRef.current)
          : getLightMapStyles(!labelsVisibleRef.current)
      );
      const staticUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${center.lat()},${center.lng()}&zoom=${staticZoom}&size=${staticW}x${staticH}&scale=${scale}&maptype=roadmap&${styleParams}&key=${GOOGLE_MAP_KEY}`;

      const centerPoint = projection.fromLatLngToPoint(center);
      const worldScale = 2 ** staticZoom;
      const sw = projection.fromPointToLatLng(
        new window.google.maps.Point(
          centerPoint.x - staticW / 2 / worldScale,
          centerPoint.y + staticH / 2 / worldScale
        )
      );
      const ne = projection.fromPointToLatLng(
        new window.google.maps.Point(
          centerPoint.x + staticW / 2 / worldScale,
          centerPoint.y - staticH / 2 / worldScale
        )
      );
      const bounds = new window.google.maps.LatLngBounds(sw, ne);

      const loaded = await new Promise((resolve) => {
        const probe = new Image();
        probe.crossOrigin = "anonymous";
        probe.onload = () => resolve(true);
        probe.onerror = () => resolve(false);
        probe.src = staticUrl;
      });
      if (!loaded) return false;

      class SnapshotOverlay extends window.google.maps.OverlayView {
        onAdd() {
          const img = document.createElement("img");
          img.src = staticUrl;
          img.crossOrigin = "anonymous";
          img.style.position = "absolute";
          img.style.display = "block";
          img.style.pointerEvents = "none";
          this.img = img;
          this.getPanes().mapPane.appendChild(img);
        }
        draw() {
          const proj = this.getProjection();
          const swPx = proj.fromLatLngToDivPixel(bounds.getSouthWest());
          const nePx = proj.fromLatLngToDivPixel(bounds.getNorthEast());
          if (!swPx || !nePx || !this.img) return;
          this.img.style.left = `${swPx.x}px`;
          this.img.style.top = `${nePx.y}px`;
          this.img.style.width = `${nePx.x - swPx.x}px`;
          this.img.style.height = `${swPx.y - nePx.y}px`;
        }
        onRemove() {
          this.img?.parentNode?.removeChild(this.img);
          this.img = null;
        }
      }

      const overlay = new SnapshotOverlay();
      overlay.setMap(map);
      snapshotOverlayRef.current = overlay;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      return true;
    }, [isDarkMode]);

    const clearExportSnapshot = useCallback(() => {
      snapshotOverlayRef.current?.setMap(null);
      snapshotOverlayRef.current = null;
    }, []);

    useImperativeHandle(forwardedRef, () => ({ prepareExportSnapshot, clearExportSnapshot }), [
      prepareExportSnapshot,
      clearExportSnapshot,
    ]);

    return (
      <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
        <div ref={mapRef} className="w-full h-full" />
        {selectedCity && <BottomLeftLegend isDarkMode={isDarkMode} sidebarOpen={sidebarOpen} />}
        <MapShimmer visible={mapLoading} isDarkMode={isDarkMode} mode={shimmerMode} />
      </div>
    );
  }
);

// ============================================
// City & Street Sidebar
// ============================================
const StatCardShimmer = () => (
  <div className="rounded-lg px-2 py-1.5 bg-muted/40 border border-border/60 animate-pulse">
    <div className="h-2 w-3/5 rounded bg-muted-foreground/20 mb-1.5" />
    <div className="h-4 w-2/5 rounded bg-muted-foreground/25" />
  </div>
);

const CityStreetSidebar = ({
  citiesData = [],
  countryInfo = null,
  allCitiesData,
  fullCountryInfo,
  selectedCity,
  selectedStreet,
  selectedPnd,
  onSelectCity,
  onSelectStreet,
  onSelectPnd,
  onSelectCountry,
  activeFilters,
  onFiltersChange,
  onToggle,
  baysPerPnd: baysPerPndProp,
  onBaysPerPndChange,
  monthGroups = [],
  yearOptions = [],
  optLoading = false,
  optError = null,
}) => {
  // Stat cards (Projects / Streets / Total PNDs / Active / Inactive) always show
  // the TOTAL count regardless of the logged-in user's assigned permissions.
  // The tree below (city -> street -> pnd list) still uses the permission-filtered
  // `citiesData` / `countryInfo` so navigation stays scoped to what's assigned.
  const statsCitiesData = allCitiesData ?? citiesData;
  const statsCountryInfo = fullCountryInfo !== undefined ? fullCountryInfo : countryInfo;
  const [expandedCountry, setExpandedCountry] = useState(true);
  const toggleCountry = () => {
    setExpandedCountry((p) => !p);
    onSelectCountry?.();
  };
  const [expandedCities, setExpandedCities] = useState(new Set());
  const [expandedStreets, setExpandedStreets] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterPanelTop, setFilterPanelTop] = useState(12);
  const filterBtnRef = useRef(null);

  const hasActiveFilter = !!(activeFilters?.month && activeFilters?.year);

  useEffect(() => {
    if (showFilters && filterBtnRef.current) {
      const rect = filterBtnRef.current.getBoundingClientRect();
      const panelHeight = 390;
      const maxTop = window.innerHeight - panelHeight - 12;
      setFilterPanelTop(Math.min(Math.max(rect.top, 12), maxTop));
    }
  }, [showFilters]);

  // NOTE: these drive the "Projects / Streets / Total PNDs / Active / Inactive" stat
  // cards at the top of the sidebar. They intentionally read from statsCitiesData /
  // statsCountryInfo (unfiltered) so they always show the grand total, regardless of
  // which cities/streets the logged-in user has permission to see. The tree below
  // (citiesListContent) still uses the permission-filtered `citiesData`.
  const totalCities = statsCountryInfo?.total_cities ?? statsCitiesData.length;
  const totalStreets =
    statsCountryInfo?.total_streets ??
    statsCitiesData.reduce((s, c) => s + (c.total_streets || 0), 0);
  const totalPnds =
    statsCountryInfo?.total_pnds ?? statsCitiesData.reduce((s, c) => s + (c.total_pnds || 0), 0);

  const sidebarMonth = activeFilters?.month || MONTH_NAMES[new Date().getMonth()];
  const sidebarYear = activeFilters?.year || String(new Date().getFullYear());
  const {
    countryParkingBaysCount: totalParkingBays,
    countryParkingBaysLoading: parkingBaysCardLoading,
  } = useCountryParkingBaysCount(sidebarMonth, sidebarYear, statsCountryInfo?.city_cc_contract);

  const [localBaysPerPnd, setLocalBaysPerPnd] = useState(25);
  const baysPerPnd = baysPerPndProp !== undefined ? baysPerPndProp : localBaysPerPnd;
  const setBaysPerPnd = onBaysPerPndChange || setLocalBaysPerPnd;

  const activePnds = statsCitiesData.reduce(
    (s, c) =>
      s +
      (c.streets || []).reduce(
        (ss, st) => ss + (st.pnds || []).filter((p) => p.device_status === "ACTIVE").length,
        0
      ),
    0
  );

  const inactivePnds = statsCitiesData.reduce(
    (s, c) =>
      s +
      (c.streets || []).reduce(
        (ss, st) => ss + (st.pnds || []).filter((p) => p.device_status === "INACTIVE").length,
        0
      ),
    0
  );

  const pndCoverage = useMemo(() => {
    const bays = parseCount(totalParkingBays);
    const ratio = Number(baysPerPnd) > 0 ? Number(baysPerPnd) : 25;
    if (bays === null) return { requiredPnds: null, diff: null };
    const requiredPnds = Math.ceil(bays / ratio);
    const diff = activePnds - requiredPnds;
    return { requiredPnds, diff };
  }, [totalParkingBays, baysPerPnd, activePnds]);

  const filteredCities = citiesData.filter((city) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      city.city_name?.toLowerCase().includes(q) ||
      city.streets?.some(
        (s) =>
          s.street_name?.toLowerCase().includes(q) ||
          s.pnds?.some(
            (p) =>
              p.device_type?.toLowerCase().includes(q) ||
              p.pnd_number?.toString().toLowerCase().includes(q)
          )
      )
    );
  });

  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      setExpandedCities(new Set());
      setExpandedStreets(new Set());
      return;
    }

    const newExpandedCities = new Set();

    citiesData.forEach((city) => {
      const hasStreetNameMatch = city.streets?.some((s) =>
        s.street_name?.toLowerCase().includes(q)
      );

      if (hasStreetNameMatch) {
        newExpandedCities.add(city.city_cd);
      }
    });

    setExpandedCities(newExpandedCities);
    setExpandedStreets(new Set());
  }, [searchQuery, citiesData]);

  const toggleCity = (cd) =>
    setExpandedCities((p) => {
      const n = new Set(p);
      n.has(cd) ? n.delete(cd) : n.add(cd);
      return n;
    });
  const toggleStreet = (cd) =>
    setExpandedStreets((p) => {
      const n = new Set(p);
      n.has(cd) ? n.delete(cd) : n.add(cd);
      return n;
    });

  const handleCityClick = (city) => {
    onSelectCity(city);
    onSelectStreet(null);
    onSelectPnd(null);
  };
  const handleStreetClick = (street, city) => {
    onSelectCity(city);
    onSelectStreet(street);
    onSelectPnd(null);
    if (!expandedStreets.has(street.street_cd)) toggleStreet(street.street_cd);
  };
  const handlePndClick = (pnd) => {
    onSelectPnd(pnd);
  };

  const highlight = (text, query) => {
    if (!query || !text) return String(text);
    const q = query.trim().toLowerCase();
    const str = String(text);
    const idx = str.toLowerCase().indexOf(q);
    if (idx === -1) return str;
    return (
      <span>
        {str.slice(0, idx)}
        <mark
          style={{
            background: "#6366F133",
            color: "inherit",
            borderRadius: "2px",
            padding: "0 1px",
          }}
        >
          {str.slice(idx, idx + q.length)}
        </mark>
        {str.slice(idx + q.length)}
      </span>
    );
  };

  const citiesListContent = (
    <>
      {filteredCities.length === 0 && searchQuery.trim() ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
          <Search className="w-6 h-6 opacity-30" />
          <p className="text-xs">No results for "{searchQuery}"</p>
        </div>
      ) : (
        filteredCities.map((city, ci) => {
          const expanded = expandedCities.has(city.city_cd);
          const selected = selectedCity?.city_cd === city.city_cd;
          const q = searchQuery.trim().toLowerCase();
          const visibleStreets = q
            ? city.city_name?.toLowerCase().includes(q)
              ? city.streets || []
              : (city.streets || []).filter(
                  (s) =>
                    s.street_name?.toLowerCase().includes(q) ||
                    s.pnds?.some(
                      (p) =>
                        p.device_type?.toLowerCase().includes(q) ||
                        p.pnd_number?.toString().toLowerCase().includes(q)
                    )
                )
            : city.streets || [];

          return (
            <div key={`city_${city.city_cd ?? ci}`}>
              <button
                onClick={() => handleCityClick(city)}
                className={`w-full text-left rounded-lg px-3 py-2.5 transition-all ${selected ? "bg-sidebar-accent border border-primary/30" : "hover:bg-sidebar-accent/50 border border-transparent"}`}
              >
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCity(city.city_cd);
                    }}
                    className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                  >
                    {expanded ? (
                      <ChevronDown className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-sidebar-foreground truncate">
                      {highlight(city.city_name, searchQuery)}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] font-mono text-muted-foreground">
                        Code: {city.city_cd}
                      </span>
                      <span className="text-[10px] text-muted-foreground">•</span>
                      <span className="text-[10px] text-muted-foreground">
                        {city.total_streets} streets
                      </span>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono font-semibold text-primary flex-shrink-0">
                    {city.total_pnds} PNDs
                  </span>
                  <AddStreetIcon city={city} onAdd={(c, s) => console.log("new street:", c, s)} />
                </div>
              </button>

              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="ml-4 pl-3 border-l border-sidebar-border space-y-0.5 py-1">
                      {visibleStreets.map((street, si) => {
                        const sExpanded = expandedStreets.has(street.street_cd);
                        const sSelected = selectedStreet?.street_cd === street.street_cd;
                        const bnaCount =
                          street.pnds?.filter((p) => p.device_type?.includes("BNA")).length || 0;
                        const txCount =
                          street.pnds?.filter((p) => p.device_type?.includes("TX")).length || 0;
                        const actCount = street.pnds?.filter((p) => p.device_type).length || 0;
                        const visiblePnds = q
                          ? (street.pnds || []).filter(
                              (p) =>
                                street.street_name?.toLowerCase().includes(q) ||
                                city.city_name?.toLowerCase().includes(q) ||
                                p.device_type?.toLowerCase().includes(q) ||
                                p.pnd_number?.toString().toLowerCase().includes(q)
                            )
                          : street.pnds || [];

                        return (
                          <div key={`street_${street.street_cd ?? si}`}>
                            <button
                              onClick={() => handleStreetClick(street, city)}
                              className={`w-full text-left rounded-md px-2.5 py-1.5 transition-all ${sSelected ? "bg-primary/10 border border-primary/20" : "hover:bg-sidebar-accent/40"}`}
                            >
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleStreet(street.street_cd);
                                  }}
                                  className="flex-shrink-0 text-muted-foreground"
                                >
                                  {sExpanded ? (
                                    <ChevronDown className="w-3 h-3" />
                                  ) : (
                                    <ChevronRight className="w-3 h-3" />
                                  )}
                                </button>
                                <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-[11px] text-sidebar-foreground truncate">
                                    {highlight(street.street_name, searchQuery)}
                                  </p>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {bnaCount > 0 && (
                                      <span
                                        className="text-[8px] font-semibold px-1 py-0.5 rounded-full"
                                        style={{ background: "#8B5CF622", color: "#8B5CF6" }}
                                      >
                                        BNA {bnaCount}
                                      </span>
                                    )}
                                    {txCount > 0 && (
                                      <span
                                        className="text-[8px] font-semibold px-1 py-0.5 rounded-full"
                                        style={{ background: "#F59E0B22", color: "#F59E0B" }}
                                      >
                                        TX {txCount}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <span className="text-[9px] font-mono text-muted-foreground flex-shrink-0">
                                  {actCount}/{street.total_pnds}
                                </span>
                                <AddPndIcon
                                  street={street}
                                  onAdd={(s, p) => console.log("new pnd:", s, p)}
                                />
                              </div>
                            </button>

                            <AnimatePresence>
                              {sExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="ml-6 pl-3 border-l border-sidebar-border space-y-0.5 py-1">
                                    {visiblePnds.map((pnd, pi) => {
                                      const pSelected =
                                        selectedPnd?.pnd_number != null &&
                                        selectedPnd.pnd_number === pnd.pnd_number;
                                      const dColor = getDeviceColor(pnd.device_type);
                                      const dType = getDeviceTypeName(pnd.device_type);
                                      const DTypeIcon = getDeviceTypeIcon(pnd.device_type);
                                      const hasCoords = pnd.latitude && pnd.longitude;
                                      return (
                                        <button
                                          key={`pnd_${pnd.pnd_number ?? pnd.device_type ?? ""}_${pi}`}
                                          onClick={() => handlePndClick(pnd)}
                                          className={`w-full text-left rounded-md px-2 py-1.5 transition-all flex items-center gap-2 ${pSelected ? "bg-primary/10 border border-primary/20" : "hover:bg-sidebar-accent/40"}`}
                                        >
                                          <DTypeIcon
                                            className="w-2.5 h-2.5 flex-shrink-0"
                                            style={{
                                              color: hasCoords
                                                ? pnd.device_type
                                                  ? dColor
                                                  : "#EF4444"
                                                : "#9CA3AF",
                                            }}
                                          />
                                          <span
                                            className="text-[7px] font-bold px-1 py-0.5 rounded"
                                            style={{ background: `${dColor}22`, color: dColor }}
                                          >
                                            {dType}
                                          </span>
                                          <div className="flex-1 min-w-0">
                                            {pnd.pnd_number && (
                                              <p className="text-[10px] font-mono font-semibold text-sidebar-foreground truncate">
                                                #{highlight(pnd.pnd_number.toString(), searchQuery)}
                                              </p>
                                            )}
                                            <p className="text-[9px] text-muted-foreground truncate">
                                              {highlight(pnd.device_type, searchQuery)}
                                            </p>
                                          </div>
                                          {hasCoords && (
                                            <Navigation className="w-2.5 h-2.5 text-primary flex-shrink-0" />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })
      )}
    </>
  );

  return (
    <div className="h-full flex flex-col bg-sidebar overflow-hidden border-r border-sidebar-border">
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-sidebar-foreground tracking-tight">
              On Street PND Explorer
            </h1>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest">
              Street-wise PNDs
            </p>
          </div>
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-all flex-shrink-0"
            title="Close sidebar"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          {[
            { label: "Projects", value: totalCities },
            { label: "Streets", value: totalStreets },
            { label: "Total PNDs", value: totalPnds },
            { label: "Active PNDs", value: activePnds, color: "emerald" },
            { label: "Inactive PNDs", value: inactivePnds, color: "red" },
            {
              label: "Parking Bays",
              value: totalParkingBays,
              color: "amber",
              loading: parkingBaysCardLoading,
            },
          ].map(({ label, value, color, loading }) =>
            loading ? (
              <StatCardShimmer key={label} />
            ) : (
              <div
                key={label}
                className={`rounded-lg px-2 py-1.5 ${
                  color === "emerald"
                    ? "bg-emerald-500/10 border border-emerald-500/30"
                    : color === "red"
                      ? "bg-red-500/10 border border-red-500/30"
                      : color === "amber"
                        ? "bg-amber-500/10 border border-amber-500/30"
                        : "bg-card border border-border"
                }`}
              >
                <p
                  className={`text-[9px] uppercase tracking-wider leading-tight truncate ${
                    color === "emerald"
                      ? "text-emerald-500"
                      : color === "red"
                        ? "text-red-500"
                        : color === "amber"
                          ? "text-amber-500"
                          : "text-muted-foreground"
                  }`}
                >
                  {label}
                </p>
                <p
                  className={`text-base font-bold leading-tight ${
                    color === "emerald"
                      ? "text-emerald-500"
                      : color === "red"
                        ? "text-red-500"
                        : color === "amber"
                          ? "text-amber-500"
                          : "text-foreground"
                  }`}
                >
                  {formatCount(value)}
                </p>
              </div>
            )
          )}
        </div>

        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
          {parkingBaysCardLoading ? (
            <>
              <StatCardShimmer />
              <StatCardShimmer />
            </>
          ) : (
            <>
              <div className="relative rounded-lg px-2 py-1.5 bg-secondary border border-border">
                <div className="flex items-center justify-between gap-1">
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground leading-tight truncate">
                    Required PNDs
                  </p>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <input
                      type="number"
                      min={1}
                      value={baysPerPnd}
                      onChange={(e) =>
                        setBaysPerPnd(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      title="Bays per PND"
                      className="w-11 px-1 py-0 text-[10px] text-right rounded bg-background border border-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
                <p className="text-base font-bold text-foreground leading-tight">
                  {pndCoverage.requiredPnds === null ? "—" : formatCount(pndCoverage.requiredPnds)}
                </p>
              </div>
              <div
                className={`rounded-lg px-2 py-1.5 border ${
                  pndCoverage.diff === null
                    ? "bg-secondary border-border"
                    : pndCoverage.diff > 0
                      ? "bg-emerald-500/10 border-emerald-500/30"
                      : pndCoverage.diff < 0
                        ? "bg-red-500/10 border-red-500/30"
                        : "bg-secondary border-border"
                }`}
              >
                <p
                  className={`text-[9px] uppercase tracking-wider leading-tight truncate ${
                    pndCoverage.diff === null
                      ? "text-muted-foreground"
                      : pndCoverage.diff > 0
                        ? "text-emerald-500"
                        : pndCoverage.diff < 0
                          ? "text-red-500"
                          : "text-muted-foreground"
                  }`}
                >
                  PND Status
                </p>
                <p
                  className={`text-base font-bold leading-tight truncate ${
                    pndCoverage.diff === null
                      ? "text-foreground"
                      : pndCoverage.diff > 0
                        ? "text-emerald-500"
                        : pndCoverage.diff < 0
                          ? "text-red-500"
                          : "text-foreground"
                  }`}
                >
                  {pndCoverage.diff === null
                    ? "—"
                    : pndCoverage.diff > 0
                      ? `+${formatCount(pndCoverage.diff)} Extra`
                      : pndCoverage.diff < 0
                        ? `-${formatCount(Math.abs(pndCoverage.diff))} Shortage`
                        : "Balanced"}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="px-3 py-2.5 border-b border-sidebar-border">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search project, street, device, PND#..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <button
            ref={filterBtnRef}
            onClick={() => setShowFilters((v) => !v)}
            className={`relative flex-shrink-0 p-1.5 rounded-lg border transition-all ${hasActiveFilter ? "bg-primary/15 border-primary/40 text-primary" : showFilters ? "bg-secondary border-border text-foreground" : "bg-background border-border text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
            title=" Date Filter"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {hasActiveFilter && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-primary" />
            )}
          </button>
        </div>

        {hasActiveFilter && (
          <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-lg bg-primary/8 border border-primary/15">
            <Calendar className="w-3 h-3 text-primary flex-shrink-0" />
            <span className="text-[9px] text-primary font-semibold truncate">
              {activeFilters.month} {activeFilters.year}
              {activeFilters.threshold && ` • Threshold: ${activeFilters.threshold}`}
            </span>
            <button
              onClick={() => onFiltersChange({})}
              className="ml-auto text-muted-foreground hover:text-foreground flex-shrink-0"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}

        {searchQuery.trim() && (
          <p className="text-[10px] text-muted-foreground mt-1.5 px-0.5">
            {filteredCities.length === 0
              ? "No results found"
              : `${filteredCities.length} cit${filteredCities.length === 1 ? "y" : "ies"} matched`}
          </p>
        )}
        <AddProjectButton onAdd={(newCity) => console.log("new project:", newCity)} />
      </div>

      <AnimatePresence>
        {showFilters && (
          <DateFilterPanel
            topPos={filterPanelTop}
            activeMonth={activeFilters?.month}
            activeYear={activeFilters?.year}
            activeThreshold={activeFilters?.threshold}
            initialGroup={activeFilters?.type || ""}
            initialMonth={
              activeFilters?.panelMonth || activeFilters?.displayMonth || activeFilters?.month || ""
            }
            initialDay={
              activeFilters?.month
                ? { value: activeFilters.month, label: activeFilters.month }
                : null
            }
            onApply={(f) => {
              onFiltersChange(f);
              setShowFilters(false);
            }}
            onReset={() => {
              onFiltersChange({});
              setShowFilters(false);
            }}
            onClose={() => setShowFilters(false)}
            monthGroups={monthGroups}
            yearOptions={yearOptions}
            optLoading={optLoading}
            optError={optError}
          />
        )}
      </AnimatePresence>

      <div className="flex-1 overflow-y-auto cb-scroll p-2 space-y-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1.5 font-semibold">
          Country, Projects &amp; Streets
        </p>

        {countryInfo ? (
          <div>
            <button
              onClick={toggleCountry}
              className="w-full text-left rounded-lg px-3 py-2.5 transition-all bg-primary/5 hover:bg-primary/10 border border-primary/15"
            >
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0 text-muted-foreground">
                  {expandedCountry ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  )}
                </span>
                <Globe className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-sidebar-foreground truncate">
                    {countryInfo.country_name || "All Countries"}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {countryInfo.country_cd && (
                      <>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          Code: {countryInfo.country_cd}
                        </span>
                        <span className="text-[10px] text-muted-foreground">•</span>
                      </>
                    )}
                    <span className="text-[10px] text-muted-foreground">
                      {totalCities} projects
                    </span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-semibold text-primary flex-shrink-0">
                  {totalPnds} PNDs
                </span>
              </div>
            </button>

            <AnimatePresence>
              {expandedCountry && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="ml-4 pl-3 border-l border-sidebar-border space-y-1 py-1">
                    {citiesListContent}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <div className="space-y-1">{citiesListContent}</div>
        )}
      </div>
    </div>
  );
};

// ============================================
// CityRevenueOverview
// ============================================
const CityRevenueOverview = ({
  city,
  filterMonth,
  filterYear,
  filterThreshold,
  pndStatusFilters = EMPTY_FILTERS,
  filteredMapData = null,
  filteredMapLoading = false,
}) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);

  const cityId = city?.city_send_name || city?.city_cd;

  const hasStatusFilter = pndStatusFilters.length > 0;

  const formatValue = (value) => {
    if (!value) return "0";
    const num = typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;
    return isNaN(num)
      ? "0"
      : num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const fetchRevenue = useCallback(async () => {
    if (!cityId) return;
    setLoading(true);
    setError(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      // ✅ contract پیرامیٹر شامل کیا
      const contractParam = city?.city_cc_contract ? `&contract=${city.city_cc_contract}` : "";
      const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}${contractParam}`;
      const response = await apiCall("get", url, null, null, false, false);

      if (response?.success) {
        setRevenueData(response);
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to fetch revenue");
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [cityId, displayMonth, displayYear, threshold, city?.city_cc_contract]);

  useEffect(() => {
    if (hasStatusFilter) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchRevenue();
  }, [fetchRevenue, hasStatusFilter]);

  const effectiveLoading = hasStatusFilter ? filteredMapLoading : loading;
  const effectiveData = hasStatusFilter
    ? filteredMapData?.type === "city"
      ? filteredMapData
      : null
    : revenueData;

  if (effectiveLoading) {
    return <StatsGridShimmer count={4} />;
  }

  if (!hasStatusFilter && error) {
    return (
      <div className="flex flex-col items-center gap-2 py-5">
        <p className="text-xs text-muted-foreground text-center">{error}</p>
        <button
          onClick={fetchRevenue}
          className="text-[10px] font-semibold text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!effectiveData) {
    return (
      <div className="text-center py-5">
        <p className="text-xs text-muted-foreground">No revenue data available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2.5">
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
          Total Revenue
        </p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.collection)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
          Ticket Count
        </p>
        <p className="text-xl font-bold text-foreground">
          {formatValue(effectiveData.ticket_count)}
        </p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ALS</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.als)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ATV</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.atv)}</p>
      </div>
    </div>
  );
};

// ============================================
// StreetRevenueOverview
// ============================================
const StreetRevenueOverview = ({
  street,
  filterMonth,
  filterYear,
  filterThreshold,
  pndStatusFilters = EMPTY_FILTERS,
  filteredMapData = null,
  filteredMapLoading = false,
}) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);

  const streetId = street?.street_send_name || street?.street_cd;

  const hasStatusFilter = pndStatusFilters.length > 0;

  const formatValue = (value) => {
    if (!value) return "0";
    const num = typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;
    return isNaN(num)
      ? "0"
      : num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const fetchRevenue = useCallback(async () => {
    if (!streetId) return;
    setLoading(true);
    setError(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?street=true&device_id=${streetId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}`;
      const response = await apiCall("get", url, null, null, false, false);

      if (response?.success) {
        setRevenueData(response);
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to fetch revenue");
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [streetId, displayMonth, displayYear, threshold]);

  useEffect(() => {
    if (hasStatusFilter) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchRevenue();
  }, [fetchRevenue, hasStatusFilter]);

  const effectiveLoading = hasStatusFilter ? filteredMapLoading : loading;
  const effectiveData = hasStatusFilter
    ? filteredMapData?.type === "street"
      ? filteredMapData
      : null
    : revenueData;

  if (effectiveLoading) {
    return <StatsGridShimmer count={4} />;
  }

  if (!hasStatusFilter && error) {
    return (
      <div className="flex flex-col items-center gap-2 py-5">
        <p className="text-xs text-muted-foreground text-center">{error}</p>
        <button
          onClick={fetchRevenue}
          className="text-[10px] font-semibold text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!effectiveData) {
    return (
      <div className="text-center py-5">
        <p className="text-xs text-muted-foreground">No revenue data available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2.5">
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
          Total Revenue
        </p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.collection)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
          Ticket Count
        </p>
        <p className="text-xl font-bold text-foreground">
          {formatValue(effectiveData.ticket_count)}
        </p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ALS</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.als)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ATV</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.atv)}</p>
      </div>
    </div>
  );
};

// ============================================
// CityRevenueSummaryRow — with permission filtering
// ============================================
const CityRevenueSummaryRow = ({ code, stats, city, onSelectCity }) => {
  const { userContracts } = usePermissions();
  const [open, setOpen] = useState(false);
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;

  // ============================================
  // Check if this city is allowed based on permissions
  // ============================================
  const isAllowed = useMemo(() => {
    if (!userContracts) return true;

    const normalized = normalizePermissions(userContracts);
    const allowedKeys = new Set([
      ...normalized.onstreet.map((c) => getContractKey(c)),
      ...normalized.offstreet.map((c) => getContractKey(c)),
    ]);

    if (allowedKeys.size === 0) return true;

    const cityName = normalizeName(city?.city_name || code);

    for (const contract of ONSTREET_CONTRACTS.children) {
      const contractKey = getContractKey(contract);
      if (allowedKeys.has(contractKey) && normalizeName(contract.fullName) === cityName) {
        return true;
      }
    }

    return allowedKeys.has(code);
  }, [userContracts, code, city]);

  if (!isAllowed) return null;

  const contributionPct = parseFloat(stats?.contribution) || 0;
  const collection = stats?.collection ?? "0";
  const ticketCount = stats?.ticket_count ?? "0";
  const contribution = stats?.contribution ?? "0.00%";
  const als =
    stats?.als !== null && stats?.als !== undefined && stats?.als !== ""
      ? String(stats.als)
      : "\u2014";
  const atv =
    stats?.atv !== null && stats?.atv !== undefined && stats?.atv !== ""
      ? String(stats.atv)
      : "\u2014";
  const parkingOccupancy = stats?.parking_occupancy ?? null;
  const avgDailyRevenue = stats?.monthly_avg_daily_revenue ?? null;
  const avgDailyTransaction = stats?.monthly_avg_daily_transaction ?? null;

  const handleNameClick = () => {
    if (city) onSelectCity?.(city);
    setOpen(true);
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
        </button>

        <button
          onClick={handleNameClick}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          title="View project details"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {city?.city_name || code}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {collection}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contribution}
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-2">
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                  Revenue
                </p>
                <p className="text-lg font-bold tabular-nums leading-none text-primary">
                  {collection}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Tickets
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {ticketCount}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Contribution
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {contribution}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    ALS
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {als}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    ATV
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {atv}
                  </p>
                </div>
              </div>

              {city && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      Streets
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {city.total_streets ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      PNDs
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {city.total_pnds ?? 0}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <div className="flex items-center gap-1 mb-1">
                    <TrendingUp className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" />
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground truncate">
                      Avg Daily Revenue
                    </p>
                  </div>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {avgDailyRevenue != null
                      ? Number(String(avgDailyRevenue).replace(/,/g, "")).toLocaleString()
                      : "\u2014"}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <div className="flex items-center gap-1 mb-1">
                    <BarChart2 className="w-2.5 h-2.5 text-primary flex-shrink-0" />
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground truncate">
                      Avg Daily Transaction
                    </p>
                  </div>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {avgDailyTransaction != null
                      ? Number(String(avgDailyTransaction).replace(/,/g, "")).toLocaleString()
                      : "\u2014"}
                  </p>
                </div>
              </div>

              <div className="rounded-lg bg-secondary p-2.5">
                <div className="flex items-center gap-1 mb-1">
                  <CircleParking className="w-2.5 h-2.5 text-amber-500 flex-shrink-0" />
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground truncate">
                    Occupancy
                  </p>
                </div>
                <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                  {parkingOccupancy ?? "\u2014"}
                </p>
              </div>

              {city && (
                <button
                  onClick={() => onSelectCity?.(city)}
                  className="w-full text-center text-[10px] font-semibold text-primary hover:underline py-1"
                >
                  Open Project Details
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// CountryDetailPanel
// ============================================
const CountryDetailPanel = ({
  countryInfo,
  citiesData = [],
  activeFilters,
  onClose,
  onSelectCity,
  pageRef,
  mapComponentRef,
}) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasFetchedRef = useRef(false);
  const [openSection, setOpenSection] = useState(() => new Set(["overview", "citiesRevenue"]));
  const handleToggleSection = (id) =>
    setOpenSection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [citySearch, setCitySearch] = useState("");
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const handleExportPdf = async () => {
    setPdfExporting(true);
    setPdfProgress(0);
    try {
      await exportPanelToPdf(
        pageRef?.current ? pageRef : exportRef,
        `Country_${countryInfo?.country_name || "Report"}`,
        setPdfProgress,
        mapComponentRef
      );
    } finally {
      setPdfExporting(false);
      setPdfProgress(0);
    }
  };

  const panelRef = useRef(null);
  const exportRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(440);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onResizeMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startW.current = panelRef.current?.offsetWidth || 440;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const diff = startX.current - e.clientX;
      const newW = Math.min(900, Math.max(320, startW.current + diff));
      setPanelWidth(newW);
    };
    const onUp = () => {
      isDragging.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const now = new Date();
  const displayMonth = activeFilters?.month || MONTH_NAMES[now.getMonth()];
  const displayYear = activeFilters?.year || String(now.getFullYear());
  const threshold = activeFilters?.threshold || "";
  const countryCd = countryInfo?.country_cd;

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);
    const monthParam = buildMonthParam(displayMonth)
      ? displayMonth
      : displayMonth.includes("-")
        ? displayMonth
        : `01-${displayMonth}`;
    const contractParam = countryInfo?.city_cc_contract
      ? `&contract=${countryInfo.city_cc_contract}`
      : "";
    const url = `/get-pnd-collection?device_id=${countryCd}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}${contractParam}`;
    apiCall("get", url, null, null, false, false)
      .then((response) => {
        if (!response?.success) {
          setPayload(null);
          return;
        }
        setPayload(response);
      })
      .catch(() => setPayload(null))
      .finally(() => {
        setLoading(false);
        hasFetchedRef.current = false;
      });
  }, [countryCd, displayMonth, displayYear, threshold, countryInfo?.city_cc_contract]); // ✅ dependency add

  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;

  const totalCities = countryInfo?.total_cities ?? citiesData.length;
  const totalStreets =
    countryInfo?.total_streets ?? citiesData.reduce((s, c) => s + (c.total_streets || 0), 0);
  const totalPnds =
    countryInfo?.total_pnds ?? citiesData.reduce((s, c) => s + (c.total_pnds || 0), 0);
  const activePnds = citiesData.reduce(
    (s, c) =>
      s +
      (c.streets || []).reduce(
        (ss, st) => ss + (st.pnds || []).filter((p) => p.device_status === "ACTIVE").length,
        0
      ),
    0
  );
  const inactivePnds = citiesData.reduce(
    (s, c) =>
      s +
      (c.streets || []).reduce(
        (ss, st) => ss + (st.pnds || []).filter((p) => p.device_status === "INACTIVE").length,
        0
      ),
    0
  );

  const collection = payload?.collection;
  const ticketCount = payload?.ticket_count;
  const als = payload?.als;
  const atv = payload?.atv;
  const parkingOccupancy = payload?.parking_occupancy;
  const avgDailyRevenue = payload?.monthly_avg_daily_revenue;
  const avgDailyTransaction = payload?.monthly_avg_daily_transaction;
  const cityBreakdown = payload?.payment_breakdown?.cities || {};

  const mergedCities = Object.entries(cityBreakdown)
    .map(([code, stats]) => {
      const match = citiesData.find(
        (c) =>
          c.city_send_name?.toUpperCase().startsWith(`${code.toUpperCase()}-`) || c.city_cd === code
      );
      return { code, stats, city: match };
    })
    .sort((a, b) => toNum(b.stats?.collection) - toNum(a.stats?.collection));

  const rows = mergedCities.length
    ? mergedCities
    : citiesData.map((c) => ({ code: c.city_cd, stats: null, city: c }));
  const q = citySearch.trim().toLowerCase();
  const filteredRows = q
    ? rows.filter((r) => (r.city?.city_name || r.code || "").toLowerCase().includes(q))
    : rows;

  return (
    <motion.div
      ref={panelRef}
      style={{ width: panelWidth, height: "100%" }}
      initial={{ y: "-100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: "-100%", opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="absolute top-0 right-0 bottom-0 z-40 overflow-y-auto cb-scroll bg-card border-l border-border shadow-2xl"
    >
      <div
        onMouseDown={onResizeMouseDown}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "6px",
          cursor: "col-resize",
          zIndex: 9999,
        }}
        className="hover:bg-primary/20 transition-colors pdf-export-ignore"
      />

      <div ref={exportRef}>
        <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
            <Globe className="w-4.5 h-4.5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-foreground truncate">
              {countryInfo?.country_name || "Country"}
            </p>
            {countryInfo?.country_cd && (
              <p className="text-[10px] font-mono text-muted-foreground">
                Code: {countryInfo.country_cd}
              </p>
            )}
          </div>
          <ExportPdfButton
            onClick={handleExportPdf}
            exporting={pdfExporting}
            progress={pdfProgress}
          />
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-4">
            <StatsGridShimmer />
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <AccordionSection
              id="overview"
              openId={openSection}
              onToggle={handleToggleSection}
              icon={<BarChart2 className="w-3.5 h-3.5" />}
              title="Overview"
              defaultOpen
            >
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      label: "Collection",
                      value:
                        collection != null ? `${toNum(collection).toLocaleString()}` : "\u2014",
                    },
                    {
                      label: "Tickets",
                      value: ticketCount != null ? toNum(ticketCount).toLocaleString() : "\u2014",
                    },
                    { label: "ALS", value: als ?? "\u2014" },
                    { label: "ATV", value: atv ?? "\u2014" },
                  ].map(({ label, value }) => (
                    <div
                      key={label}
                      className="rounded-lg p-3 bg-secondary/40 border border-border"
                    >
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {label}
                      </p>
                      <p className="text-lg font-bold text-foreground truncate">{value}</p>
                    </div>
                  ))}
                </div>

                {/* <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Avg Daily Revenue', value: avgDailyRevenue != null ? toNum(avgDailyRevenue).toLocaleString() : '\u2014', icon: <TrendingUp className="w-3 h-3 text-emerald-500 flex-shrink-0" /> },
                    { label: 'Avg Daily Transaction', value: avgDailyTransaction != null ? toNum(avgDailyTransaction).toLocaleString() : '\u2014', icon: <BarChart2 className="w-3 h-3 text-primary flex-shrink-0" /> },
                    { label: 'Occupancy',     value: parkingOccupancy ?? '\u2014', icon: <CircleParking className="w-3 h-3 text-amber-500 flex-shrink-0" /> },

                  ].map(({ label, value, icon }) => (
                    <div key={label} className="rounded-lg p-3 bg-secondary/40 border border-border">
                      <div className="flex items-center gap-1 mb-1">
                        {icon}
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground truncate">{label}</p>
                      </div>
                      <p className="text-base font-bold text-foreground truncate">{value}</p>
                    </div>
                  ))}
                </div> */}
              </div>
            </AccordionSection>

            <AccordionSection
              id="paymentBreakdown"
              openId={openSection}
              onToggle={handleToggleSection}
              icon={<TrendingUp className="w-3.5 h-3.5" />}
              title="Payment Breakdown"
              badge={[`${displayMonth} ${displayYear}`]}
            >
              {payload?.payment_breakdown ? (
                <PaymentBreakdownCard breakdown={payload.payment_breakdown} />
              ) : (
                <p className="text-xs text-muted-foreground text-center py-5">
                  No payment breakdown available.
                </p>
              )}
            </AccordionSection>

            <AccordionSection
              id="citiesRevenue"
              openId={openSection}
              onToggle={handleToggleSection}
              icon={<Building2 className="w-3.5 h-3.5" />}
              title="Projects Revenue"
              badge={[`${totalCities} Projects`, `${displayMonth} ${displayYear}`]}
            >
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                  />
                  {citySearch && (
                    <button
                      onClick={() => setCitySearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
                  {filteredRows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
                      <Search className="w-5 h-5 opacity-30" />
                      <p className="text-xs">
                        No results{citySearch ? ` for "${citySearch}"` : ""}
                      </p>
                    </div>
                  ) : (
                    filteredRows.map((row, idx) => (
                      <CityRevenueSummaryRow
                        key={`${row.code}_${idx}`}
                        code={row.code}
                        stats={row.stats}
                        city={row.city}
                        onSelectCity={onSelectCity}
                      />
                    ))
                  )}
                </div>
              </div>
            </AccordionSection>

            <AccordionSection
              id="hourlyRevenue"
              openId={openSection}
              onToggle={handleToggleSection}
              icon={<Clock className="w-3.5 h-3.5" />}
              title="Hourly Revenue Pattern"
              badge={`${displayMonth} ${displayYear}`}
            >
              <HourlyRevenueChart
                deviceId={countryCd}
                month={displayMonth}
                year={displayYear}
                title={`${countryInfo?.country_name || "Country"} - Hourly Revenue`}
              />
            </AccordionSection>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ============================================
// CityDetailPanel
// ============================================
const CityDetailPanel = ({
  city,
  onClose,
  activeFilters,
  onSelectStreet,
  onBack,
  onBreakdownLoad,
  pndStatusFilters = EMPTY_FILTERS,
  onTogglePndStatusFilter,
  onClearPndStatusFilters,
  streetsOnlyMode = false,
  onToggleStreetsOnly,
  filteredMapData = null,
  filteredMapLoading = false,
  baysPerPnd = 25,
  pageRef,
  mapComponentRef,
}) => {
  const [streetSearch, setStreetSearch] = useState("");
  const [openSection, setOpenSection] = useState("overview");
  const handleToggleSection = (id) => setOpenSection((prev) => (prev === id ? null : id));
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const handleExportPdf = async () => {
    setPdfExporting(true);
    setPdfProgress(0);
    try {
      await exportPanelToPdf(
        pageRef?.current ? pageRef : exportRef,
        `City_${city?.city_name || "Report"}`,
        setPdfProgress,
        mapComponentRef
      );
    } finally {
      setPdfExporting(false);
      setPdfProgress(0);
    }
  };
  const [noTransactionCount, setNoTransactionCount] = useState(null);
  const [belowThresholdCount, setBelowThresholdCount] = useState(null);
  const [activePndsCount, setActivePndsCount] = useState(null);
  const [inactivePndsCount, setInactivePndsCount] = useState(null);
  const [parkingOccupancy, setParkingOccupancy] = useState(null);
  const [avgDailyRevenue, setAvgDailyRevenue] = useState(null);
  const [avgDailyTransaction, setAvgDailyTransaction] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  const now = new Date();
  const displayMonth = activeFilters?.month || MONTH_NAMES[now.getMonth()];
  const displayYear = activeFilters?.year || String(now.getFullYear());
  const cityId = city?.city_send_name || city?.city_cd;
  const hasThreshold = !!activeFilters?.threshold;
  const { parkingBaysCount } = useParkingBaysCount(city, displayMonth, displayYear);
  const pndCoverage = useMemo(() => {
    const bays = parseCount(parkingBaysCount);
    const ratio = Number(baysPerPnd) > 0 ? Number(baysPerPnd) : 25;
    if (bays === null || activePndsCount === null) return { requiredPnds: null, diff: null };
    const requiredPnds = Math.ceil(bays / ratio);
    const diff = activePndsCount - requiredPnds;
    return { requiredPnds, diff };
  }, [parkingBaysCount, baysPerPnd, activePndsCount]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setOverviewLoading(true);
    setNoTransactionCount(null);
    setBelowThresholdCount(null);
    setParkingOccupancy(null);
    setAvgDailyRevenue(null);
    setAvgDailyTransaction(null);
    onBreakdownLoad?.({
      belowThreshold: new Set(),
      noTransaction: new Set(),
      revenueMap: new Map(),
    });
    if (!cityId) {
      setOverviewLoading(false);
      return;
    }
    const monthParam = buildMonthParam(displayMonth)
      ? displayMonth
      : displayMonth.includes("-")
        ? displayMonth
        : `01-${displayMonth}`;
    const threshold = activeFilters?.threshold || "";
    const contractParam = city?.city_cc_contract ? `&contract=${city.city_cc_contract}` : "";
    const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}${contractParam}`;
    apiCall("get", url, null, null, false, false)
      .then((response) => {
        if (!response?.success) return;
        const payload =
          response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;
        const topLevel = response.data ?? response;

        setNoTransactionCount(payload.no_tran_pnd_count ?? 0);
        setBelowThresholdCount(payload.below_threshold_pnd_count ?? 0);

        setActivePndsCount(payload.active_pnds_count ?? 0);
        setInactivePndsCount(payload.inactive_pnds_count ?? 0);

        const overviewTotal = payload?.payment_breakdown?.total || {};
        setParkingOccupancy(topLevel.parking_occupancy ?? overviewTotal.parking_occupancy ?? null);
        setAvgDailyRevenue(
          topLevel.monthly_avg_daily_revenue ?? overviewTotal.monthly_avg_daily_revenue ?? null
        );
        setAvgDailyTransaction(
          topLevel.monthly_avg_daily_transaction ??
            overviewTotal.monthly_avg_daily_transaction ??
            null
        );

        const belowSet = new Set();
        const noTxSet = new Set();
        const revMap = new Map();
        (payload.streets || []).forEach((st) => {
          (st.pnds || []).forEach((p) => {
            if (p.below_threshold === true) belowSet.add(String(p.pnd));
            if (p.no_transaction === true) noTxSet.add(String(p.pnd));
            revMap.set(String(p.pnd), {
              collection: p.collection,
              ticket_count: p.ticket_count,
              als: p.als,
              atv: p.atv,
              contribution: p.contribution,
              device_status_date: p.device_status_date,
              device_active_date: p.device_active_date,
            });
          });
        });
        onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet, revenueMap: revMap });
      })
      .catch(() => {})
      .finally(() => {
        setOverviewLoading(false);
        hasFetchedRef.current = false;
      });
  }, [
    cityId,
    displayMonth,
    displayYear,
    activeFilters?.threshold,
    city?.city_cc_contract,
    onBreakdownLoad,
  ]);

  const activeTotal = city.total_pnds || 0;

  const toggleFilter = (key) => {
    onTogglePndStatusFilter?.(key);
  };

  const panelRef = useRef(null);
  const exportRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(520);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onResizeMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startW.current = panelRef.current?.offsetWidth || 520;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const diff = startX.current - e.clientX;
      const newW = Math.min(900, Math.max(320, startW.current + diff));
      setPanelWidth(newW);
    };
    const onUp = () => {
      isDragging.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const bnaTotal = (city.streets || []).reduce(
    (s, st) => s + (st.pnds?.filter((p) => p.device_type?.includes("BNA")).length || 0),
    0
  );
  const txTotal = (city.streets || []).reduce(
    (s, st) => s + (st.pnds?.filter((p) => p.device_type?.includes("TX")).length || 0),
    0
  );

  const filteredStreets = (city.streets || []).filter(
    (street) =>
      !streetSearch.trim() ||
      street.street_name?.toLowerCase().includes(streetSearch.trim().toLowerCase())
  );

  return (
    <motion.div
      ref={panelRef}
      style={{ width: panelWidth, height: "100%" }}
      initial={{ y: "-100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: "-100%", opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="absolute top-0 right-0 bottom-0 z-40 overflow-y-auto cb-scroll bg-card border-l border-border shadow-2xl"
    >
      <div
        onMouseDown={onResizeMouseDown}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "6px",
          cursor: "col-resize",
          zIndex: 9999,
        }}
        className="hover:bg-primary/20 transition-colors pdf-export-ignore"
      />

      <div ref={exportRef}>
        <div className="sticky top-0 z-10 backdrop-blur-md border-b border-border bg-card/80">
          <div className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                {onBack && (
                  <button
                    onClick={onBack}
                    className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-foreground">{city.city_name}</h2>
                  <p className="text-[10px] font-mono text-muted-foreground">
                    Project Code: {city.city_cd}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <ExportPdfButton
                  onClick={handleExportPdf}
                  exporting={pdfExporting}
                  progress={pdfProgress}
                />
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-2.5">
          {/* ============ OVERVIEW SECTION ============ */}
          <AccordionSection
            id="overview"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<BarChart2 className="w-3.5 h-3.5" />}
            title="Overview"
          >
            {overviewLoading ? (
              <StatsGridShimmer count={6} />
            ) : (
              <>
                {streetsOnlyMode && (
                  <div className="flex items-center gap-1.5 mb-2.5 px-2.5 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/25">
                    <span className="text-[10px] text-indigo-500 font-semibold flex-1">
                      Showing: All Streets & PNDs
                    </span>
                    <button
                      onClick={onToggleStreetsOnly}
                      className="text-indigo-500 hover:text-indigo-500/70 flex-shrink-0"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {pndStatusFilters.length > 0 && !streetsOnlyMode && (
                  <div className="flex items-center gap-1.5 mb-2.5 px-2.5 py-1.5 rounded-lg bg-primary/10 border border-primary/25">
                    <span className="text-[10px] text-primary font-semibold flex-1">
                      Showing: {pndStatusFilters.map((f) => PND_STATUS_LABELS[f] || f).join(" + ")}
                    </span>
                    <button
                      onClick={onClearPndStatusFilters}
                      className="text-primary hover:text-primary/70 flex-shrink-0"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {pndStatusFilters.length === 0 && !streetsOnlyMode && (
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <SlidersHorizontal className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    <p className="text-[10px] text-muted-foreground">
                      Tap a card below to filter the map & data
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    onClick={onToggleStreetsOnly}
                    title="Click to view all streets & PNDs"
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      streetsOnlyMode
                        ? "border-indigo-500 bg-indigo-500/15 ring-1 ring-indigo-500"
                        : "border-border bg-secondary hover:bg-secondary/70 cursor-pointer"
                    }`}
                  >
                    {streetsOnlyMode && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-indigo-500 flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!streetsOnlyMode && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-muted-foreground/0 group-hover:text-muted-foreground/60 transition-colors" />
                    )}
                    <p
                      className={`text-[9px] uppercase tracking-widest mb-1 ${streetsOnlyMode ? "text-indigo-500" : "text-muted-foreground"}`}
                    >
                      Total Streets
                    </p>
                    <p
                      className={`text-2xl font-bold leading-none ${streetsOnlyMode ? "text-indigo-500" : "text-foreground"}`}
                    >
                      {city.total_streets || 0}
                    </p>
                  </button>

                  <button
                    onClick={() => toggleFilter("all")}
                    title="Click to view all PNDs"
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      pndStatusFilters.includes("all") && !streetsOnlyMode
                        ? "border-primary bg-primary/15 ring-1 ring-primary"
                        : "border-border bg-secondary hover:bg-secondary/70 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("all") && !streetsOnlyMode && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!(pndStatusFilters.includes("all") && !streetsOnlyMode) && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-muted-foreground/0 group-hover:text-muted-foreground/60 transition-colors" />
                    )}
                    <p
                      className={`text-[9px] uppercase tracking-widest mb-1 ${pndStatusFilters.includes("all") && !streetsOnlyMode ? "text-primary" : "text-muted-foreground"}`}
                    >
                      Total PNDs
                    </p>
                    <p
                      className={`text-2xl font-bold leading-none ${pndStatusFilters.includes("all") && !streetsOnlyMode ? "text-primary" : "text-foreground"}`}
                    >
                      {city.total_pnds || 0}
                    </p>
                  </button>

                  <button
                    disabled={!activePndsCount}
                    onClick={() => toggleFilter("active")}
                    title={
                      activePndsCount
                        ? "Click to filter map by Active PNDs"
                        : "No active PNDs in this period"
                    }
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      !activePndsCount
                        ? "border-emerald-500/15 bg-emerald-500/5 opacity-50 cursor-not-allowed"
                        : pndStatusFilters.includes("active")
                          ? "border-emerald-500 bg-emerald-500/20 ring-1 ring-emerald-500"
                          : "border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/15 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("active") && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!pndStatusFilters.includes("active") && !!activePndsCount && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-emerald-500/0 group-hover:text-emerald-500/60 transition-colors" />
                    )}
                    <div className="flex items-center gap-1 mb-1">
                      <CircleDot className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-emerald-500">
                        Active PNDs
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-500 leading-none">
                      {activePndsCount === null ? "—" : activePndsCount}
                    </p>
                  </button>

                  <button
                    disabled={!inactivePndsCount}
                    onClick={() => toggleFilter("inactive")}
                    title={
                      inactivePndsCount
                        ? "Click to filter map by Inactive PNDs"
                        : "No inactive PNDs in this period"
                    }
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      !inactivePndsCount
                        ? "border-red-400/15 bg-red-400/5 opacity-50 cursor-not-allowed"
                        : pndStatusFilters.includes("inactive")
                          ? "border-red-400 bg-red-400/20 ring-1 ring-red-400"
                          : "border-red-400/30 bg-red-400/10 hover:bg-red-400/15 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("inactive") && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-400 flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!pndStatusFilters.includes("inactive") && !!inactivePndsCount && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-red-400/0 group-hover:text-red-400/60 transition-colors" />
                    )}
                    <div className="flex items-center gap-1 mb-1">
                      <Ban className="w-3 h-3 text-red-400 flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-red-400">
                        Inactive PNDs
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-red-400 leading-none">
                      {inactivePndsCount === null ? "—" : inactivePndsCount}
                    </p>
                  </button>

                  <button
                    disabled={!noTransactionCount}
                    onClick={() => toggleFilter("no_transaction")}
                    title={
                      noTransactionCount
                        ? "Click to filter map by No Transaction PNDs"
                        : "No such PNDs in this period"
                    }
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      !noTransactionCount
                        ? "border-red-500/15 bg-red-500/5 opacity-50 cursor-not-allowed"
                        : pndStatusFilters.includes("no_transaction")
                          ? "border-red-500 bg-red-500/20 ring-1 ring-red-500"
                          : "border-red-500/30 bg-red-500/10 hover:bg-red-500/15 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("no_transaction") && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!pndStatusFilters.includes("no_transaction") && !!noTransactionCount && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-red-500/0 group-hover:text-red-500/60 transition-colors" />
                    )}
                    <div className="flex items-center gap-1 mb-1">
                      <Ban className="w-3 h-3 text-red-500 flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-red-500">
                        No Transaction
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-red-500 leading-none">
                      {noTransactionCount === null ? "—" : noTransactionCount}
                    </p>
                  </button>

                  <button
                    disabled={!belowThresholdCount}
                    onClick={() => toggleFilter("below_threshold")}
                    title={
                      belowThresholdCount
                        ? "Click to filter map by Below Threshold PNDs"
                        : "No such PNDs in this period"
                    }
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      !belowThresholdCount
                        ? "border-black/15 bg-black/5 opacity-50 cursor-not-allowed"
                        : pndStatusFilters.includes("below_threshold")
                          ? "border-black bg-black/20 ring-1 ring-black"
                          : "border-black/30 bg-black/10 hover:bg-black/15 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("below_threshold") && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-black flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!pndStatusFilters.includes("below_threshold") && !!belowThresholdCount && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-black/0 group-hover:text-black/60 transition-colors" />
                    )}
                    <div className="flex items-center gap-1 mb-1">
                      <AlertTriangle className="w-3 h-3 text-black flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-black">
                        Below Threshold
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-black leading-none">
                      {belowThresholdCount === null ? "—" : belowThresholdCount}
                    </p>
                  </button>

                  <div
                    title="Parking bays for this contract (from Enforcement)"
                    className="group relative text-left rounded-xl border border-amber-500/30 bg-amber-500/10 p-3"
                  >
                    <div className="flex items-center gap-1 mb-1">
                      <CircleParking className="w-3 h-3 text-amber-500 flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-amber-500">
                        Parking Bays
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-amber-500 leading-none">
                      {formatCount(parkingBaysCount)}
                    </p>
                  </div>

                  <div className="rounded-xl border border-border bg-secondary p-3">
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">
                      Required PNDs
                    </p>
                    <p className="text-2xl font-bold text-foreground leading-none">
                      {pndCoverage.requiredPnds === null
                        ? "—"
                        : formatCount(pndCoverage.requiredPnds)}
                    </p>
                  </div>

                  <div
                    className={`rounded-xl border p-3 ${
                      pndCoverage.diff === null
                        ? "border-border bg-secondary"
                        : pndCoverage.diff > 0
                          ? "border-emerald-500/30 bg-emerald-500/10"
                          : pndCoverage.diff < 0
                            ? "border-red-500/30 bg-red-500/10"
                            : "border-border bg-secondary"
                    }`}
                  >
                    <p
                      className={`text-[9px] uppercase tracking-widest mb-1 ${
                        pndCoverage.diff === null
                          ? "text-muted-foreground"
                          : pndCoverage.diff > 0
                            ? "text-emerald-500"
                            : pndCoverage.diff < 0
                              ? "text-red-500"
                              : "text-muted-foreground"
                      }`}
                    >
                      PND Status
                    </p>
                    <p
                      className={`text-2xl font-bold leading-none ${
                        pndCoverage.diff === null
                          ? "text-foreground"
                          : pndCoverage.diff > 0
                            ? "text-emerald-500"
                            : pndCoverage.diff < 0
                              ? "text-red-500"
                              : "text-foreground"
                      }`}
                    >
                      {pndCoverage.diff === null
                        ? "—"
                        : pndCoverage.diff > 0
                          ? `+${formatCount(pndCoverage.diff)} Extra`
                          : pndCoverage.diff < 0
                            ? `-${formatCount(Math.abs(pndCoverage.diff))} Shortage`
                            : "Balanced"}
                    </p>
                  </div>
                </div>
              </>
            )}
          </AccordionSection>

          {/* ============ DEVICE TYPE BREAKDOWN ============ */}
          <AccordionSection
            id="deviceType"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<Layers className="w-3.5 h-3.5" />}
            title="Device Type Breakdown"
          >
            <div className="grid grid-cols-2 gap-2.5">
              <div className="group relative rounded-lg bg-secondary p-3 overflow-hidden cursor-default">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Banknote className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#8B5CF6" }} />
                </div>
                <p className="text-[22px] font-medium leading-none">{bnaTotal}</p>
                <div
                  className="absolute inset-x-0 bottom-0 flex items-center justify-center py-1
                             bg-popover/95 border-t border-border opacity-0 translate-y-1
                             group-hover:opacity-100 group-hover:translate-y-0
                             transition-all duration-150 z-10"
                >
                  <span
                    className="text-[8px] font-bold uppercase tracking-widest px-1 text-center"
                    style={{ color: "#8B5CF6" }}
                  >
                    BNA Devices
                  </span>
                </div>
              </div>
              <div className="group relative rounded-lg bg-secondary p-3 overflow-hidden cursor-default">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CreditCard className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#F59E0B" }} />
                </div>
                <p className="text-[22px] font-medium leading-none">{txTotal}</p>
                <div
                  className="absolute inset-x-0 bottom-0 flex items-center justify-center py-1
                             bg-popover/95 border-t border-border opacity-0 translate-y-1
                             group-hover:opacity-100 group-hover:translate-y-0
                             transition-all duration-150 z-10"
                >
                  <span
                    className="text-[8px] font-bold uppercase tracking-widest px-1 text-center"
                    style={{ color: "#F59E0B" }}
                  >
                    TX Devices
                  </span>
                </div>
              </div>
            </div>
          </AccordionSection>

          {/* ============ PROJECT REVENUE SECTION ============ */}
          <AccordionSection
            id="projectRevenue"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<TrendingUp className="w-3.5 h-3.5" />}
            title="Project Revenue"
            badge={[
              `${activeFilters?.month || MONTH_NAMES[new Date().getMonth()]} ${activeFilters?.year || String(new Date().getFullYear())}`,
              ...(pndStatusFilters.length
                ? [pndStatusFilters.map((f) => PND_STATUS_LABELS[f]).join(" + ")]
                : []),
            ]}
          >
            {/* Project Revenue Overview */}
            <CityRevenueOverview
              city={city}
              filterMonth={activeFilters?.month}
              filterYear={activeFilters?.year}
              filterThreshold={activeFilters?.threshold}
              pndStatusFilters={pndStatusFilters}
              filteredMapData={filteredMapData}
              filteredMapLoading={filteredMapLoading}
            />

            {/* ✅ ADDED: Avg Daily Revenue, Avg Daily Transaction, Occupancy */}
            {!overviewLoading && (
              <div className="grid grid-cols-2 gap-2.5 mt-3">
                <div className="rounded-xl border border-border bg-secondary p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <TrendingUp className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground truncate">
                      Avg Daily Revenue
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-foreground leading-none">
                    {avgDailyRevenue != null
                      ? Number(String(avgDailyRevenue).replace(/,/g, "")).toLocaleString()
                      : "\u2014"}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-secondary p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <BarChart2 className="w-3 h-3 text-primary flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground truncate">
                      Avg Daily Transaction
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-foreground leading-none">
                    {avgDailyTransaction != null
                      ? Number(String(avgDailyTransaction).replace(/,/g, "")).toLocaleString()
                      : "\u2014"}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-secondary p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <CircleParking className="w-3 h-3 text-amber-500 flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground truncate">
                      Occupancy
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-foreground leading-none">
                    {parkingOccupancy ?? "\u2014"}
                  </p>
                </div>
              </div>
            )}
          </AccordionSection>

          {/* ============ PAYMENT BREAKDOWN ============ */}
          <AccordionSection
            id="paymentBreakdown"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<TrendingUp className="w-3.5 h-3.5" />}
            title="Payment Breakdown"
            badge={[
              `${activeFilters?.month || MONTH_NAMES[new Date().getMonth()]} ${activeFilters?.year || String(new Date().getFullYear())}`,
              ...(pndStatusFilters.length
                ? [pndStatusFilters.map((f) => PND_STATUS_LABELS[f]).join(" + ")]
                : []),
            ]}
          >
            <CityRevenueSection
              city={city}
              filterMonth={activeFilters?.month}
              filterYear={activeFilters?.year}
              filterThreshold={activeFilters?.threshold}
              onBreakdownLoad={onBreakdownLoad}
              filterStatus={pndStatusFilters}
            />
          </AccordionSection>

          {/* ============ STREETS REVENUE ============ */}
          <AccordionSection
            id="streetsRevenue"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<MapPin className="w-3.5 h-3.5" />}
            title="Streets Revenue"
            badge={[
              `${city.total_streets || 0} Streets`,
              `${activeFilters?.month || MONTH_NAMES[new Date().getMonth()]} ${activeFilters?.year || String(new Date().getFullYear())}`,
              ...(pndStatusFilters.length
                ? [pndStatusFilters.map((f) => PND_STATUS_LABELS[f]).join(" + ")]
                : []),
            ]}
          >
            <CityStreetsRevenueSummary
              city={city}
              filterMonth={activeFilters?.month}
              filterYear={activeFilters?.year}
              filterThreshold={activeFilters?.threshold}
              onSelectStreet={onSelectStreet}
              filterStatus={pndStatusFilters}
            />
          </AccordionSection>

          {/* ============ HOURLY REVENUE ============ */}
          <AccordionSection
            id="hourlyRevenue"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<Clock1 className="w-3.5 h-3.5" />}
            title="Hourly Revenue Pattern"
            badge={`${activeFilters?.month || MONTH_NAMES[new Date().getMonth()]} ${activeFilters?.year || String(new Date().getFullYear())}`}
          >
            <HourlyRevenueChart
              deviceId={city.city_send_name || city.city_cd}
              month={activeFilters?.month || MONTH_NAMES[new Date().getMonth()]}
              year={activeFilters?.year || String(new Date().getFullYear())}
              title={`${city.city_name} - Hourly Revenue`}
              valueType="city"
            />
          </AccordionSection>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// StreetDetailPanel
// ============================================
const StreetDetailPanel = ({
  street,
  city,
  onClose,
  activeFilters,
  onSelectPnd,
  onBack,
  onBreakdownLoad,
  pndStatusFilters = EMPTY_FILTERS,
  onTogglePndStatusFilter,
  onClearPndStatusFilters,
  filteredMapData = null,
  filteredMapLoading = false,
  baysPerPnd = 25,
  pageRef,
  mapComponentRef,
}) => {
  const [openSection, setOpenSection] = useState("overview");
  const handleToggleSection = (id) => setOpenSection((prev) => (prev === id ? null : id));
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const handleExportPdf = async () => {
    setPdfExporting(true);
    setPdfProgress(0);
    try {
      await exportPanelToPdf(
        pageRef?.current ? pageRef : exportRef,
        `Street_${street?.street_name || "Report"}`,
        setPdfProgress,
        mapComponentRef
      );
    } finally {
      setPdfExporting(false);
      setPdfProgress(0);
    }
  };
  const [noTransactionCount, setNoTransactionCount] = useState(null);
  const [belowThresholdCount, setBelowThresholdCount] = useState(null);
  const [activePndsCount, setActivePndsCount] = useState(null);
  const [inactivePndsCount, setInactivePndsCount] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const hasFetchedRef = useRef(false);

  const hasThreshold = !!activeFilters?.threshold;

  const _now = new Date();
  const displayMonth = activeFilters?.month || MONTH_NAMES[_now.getMonth()];
  const displayYear = activeFilters?.year || String(_now.getFullYear());
  const threshold = activeFilters?.threshold || "";
  const streetId = street?.street_send_name || street?.street_cd;
  const { parkingBaysCount } = useParkingBaysCount(city, displayMonth, displayYear);
  const pndCoverage = useMemo(() => {
    const bays = parseCount(parkingBaysCount);
    const ratio = Number(baysPerPnd) > 0 ? Number(baysPerPnd) : 25;
    if (bays === null || activePndsCount === null) return { requiredPnds: null, diff: null };
    const requiredPnds = Math.ceil(bays / ratio);
    const diff = activePndsCount - requiredPnds;
    return { requiredPnds, diff };
  }, [parkingBaysCount, baysPerPnd, activePndsCount]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    if (!streetId) {
      setOverviewLoading(false);
      return;
    }
    setOverviewLoading(true);
    setNoTransactionCount(null);
    setBelowThresholdCount(null);

    const monthParam = buildMonthParam(displayMonth);
    const url = `/get-pnd-collection?street=true&device_id=${streetId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}`;

    apiCall("get", url, null, null, false, false)
      .then((response) => {
        if (!response?.success) return;

        const breakdown = response.street_breakdown;

        const noTranCount = (breakdown?.pnds || []).filter((p) => p.no_transaction === true).length;
        const belowThrCount = (breakdown?.pnds || []).filter(
          (p) => p.below_threshold === true
        ).length;
        setNoTransactionCount(noTranCount);
        setBelowThresholdCount(belowThrCount);

        setActivePndsCount(breakdown?.summary?.active_pnds_count ?? 0);
        setInactivePndsCount(breakdown?.summary?.inactive_pnds_count ?? 0);

        const belowSet = new Set();
        const noTxSet = new Set();
        const revMap = new Map();
        (breakdown?.pnds || []).forEach((p) => {
          if (p.below_threshold === true) belowSet.add(String(p.pnd));
          if (p.no_transaction === true) noTxSet.add(String(p.pnd));
          revMap.set(String(p.pnd), {
            collection: p.collection,
            ticket_count: p.ticket_count,
            als: p.als,
            atv: p.atv,
            contribution: p.contribution,
            device_status_date: p.device_status_date,
            device_active_date: p.device_active_date,
          });
        });
        onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet, revenueMap: revMap });
      })
      .catch(() => {})
      .finally(() => {
        setOverviewLoading(false);
        hasFetchedRef.current = false;
      });
  }, [streetId, displayMonth, displayYear, threshold]);

  const panelRef = useRef(null);
  const exportRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(520);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onResizeMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startW.current = panelRef.current?.offsetWidth || 520;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const diff = startX.current - e.clientX;
      const newW = Math.min(900, Math.max(320, startW.current + diff));
      setPanelWidth(newW);
    };
    const onUp = () => {
      isDragging.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const bnaCount = street.pnds?.filter((p) => p.device_type?.includes("BNA")).length || 0;
  const txCount = street.pnds?.filter((p) => p.device_type?.includes("TX")).length || 0;

  const handleBreakdownLoad = useCallback(
    ({ belowThreshold, noTransaction }) => {
      onBreakdownLoad?.({ belowThreshold, noTransaction });
    },
    [onBreakdownLoad]
  );

  const handlePndSelect = (breakdownItem) => {
    const targetPndNumber = String(breakdownItem.pnd);
    const fullPnd = street.pnds?.find((p) => String(p.pnd_number) === targetPndNumber);
    if (fullPnd) onSelectPnd(fullPnd);
  };

  const toggleFilter = (key) => {
    onTogglePndStatusFilter?.(key);
  };

  return (
    <motion.div
      ref={panelRef}
      style={{ width: panelWidth, height: "100%" }}
      initial={{ y: "-100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: "-100%", opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="absolute top-0 right-0 bottom-0 z-40 overflow-y-auto cb-scroll bg-card border-l border-border shadow-2xl"
    >
      <div
        onMouseDown={onResizeMouseDown}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "6px",
          cursor: "col-resize",
          zIndex: 9999,
        }}
        className="hover:bg-primary/20 transition-colors pdf-export-ignore"
      />

      <div ref={exportRef}>
        <div className="sticky top-0 z-10 backdrop-blur-md border-b border-border bg-card/80">
          <div className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                {onBack && (
                  <button
                    onClick={onBack}
                    className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-foreground">{street.street_name}</h2>
                  <p className="text-[10px] font-mono text-muted-foreground">
                    {city?.city_name} • Code: {street.street_cd}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <ExportPdfButton
                  onClick={handleExportPdf}
                  exporting={pdfExporting}
                  progress={pdfProgress}
                />
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-2.5">
          <AccordionSection
            id="overview"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<BarChart2 className="w-3.5 h-3.5" />}
            title="Overview"
          >
            {overviewLoading ? (
              <StatsGridShimmer count={5} />
            ) : (
              <>
                {pndStatusFilters.length > 0 && (
                  <div className="flex items-center gap-1.5 mb-2.5 px-2.5 py-1.5 rounded-lg bg-primary/10 border border-primary/25">
                    <span className="text-[10px] text-primary font-semibold flex-1">
                      Showing: {pndStatusFilters.map((f) => PND_STATUS_LABELS[f] || f).join(" + ")}
                    </span>
                    <button
                      onClick={onClearPndStatusFilters}
                      className="text-primary hover:text-primary/70 flex-shrink-0"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {pndStatusFilters.length === 0 && (
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <SlidersHorizontal className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    <p className="text-[10px] text-muted-foreground">
                      Tap a card below to filter the map & data
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    onClick={() => toggleFilter("all")}
                    title="Click to view all PNDs"
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      pndStatusFilters.includes("all")
                        ? "border-primary bg-primary/15 ring-1 ring-primary"
                        : "border-border bg-secondary hover:bg-secondary/70 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("all") && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!pndStatusFilters.includes("all") && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-muted-foreground/0 group-hover:text-muted-foreground/60 transition-colors" />
                    )}
                    <p
                      className={`text-[9px] uppercase tracking-widest mb-1 ${pndStatusFilters.includes("all") ? "text-primary" : "text-muted-foreground"}`}
                    >
                      Total PNDs
                    </p>
                    <p
                      className={`text-2xl font-bold leading-none ${pndStatusFilters.includes("all") ? "text-primary" : "text-foreground"}`}
                    >
                      {street.total_pnds || 0}
                    </p>
                  </button>

                  <button
                    disabled={!activePndsCount}
                    onClick={() => toggleFilter("active")}
                    title={
                      activePndsCount
                        ? "Click to filter map by Active PNDs"
                        : "No active PNDs in this period"
                    }
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      !activePndsCount
                        ? "border-emerald-500/15 bg-emerald-500/5 opacity-50 cursor-not-allowed"
                        : pndStatusFilters.includes("active")
                          ? "border-emerald-500 bg-emerald-500/20 ring-1 ring-emerald-500"
                          : "border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/15 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("active") && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!pndStatusFilters.includes("active") && !!activePndsCount && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-emerald-500/0 group-hover:text-emerald-500/60 transition-colors" />
                    )}
                    <div className="flex items-center gap-1 mb-1">
                      <CircleDot className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-emerald-500">
                        Active PNDs
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-500 leading-none">
                      {activePndsCount === null ? "—" : activePndsCount}
                    </p>
                  </button>

                  <button
                    disabled={!inactivePndsCount}
                    onClick={() => toggleFilter("inactive")}
                    title={
                      inactivePndsCount
                        ? "Click to filter map by Inactive PNDs"
                        : "No inactive PNDs in this period"
                    }
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      !inactivePndsCount
                        ? "border-red-400/15 bg-red-400/5 opacity-50 cursor-not-allowed"
                        : pndStatusFilters.includes("inactive")
                          ? "border-red-400 bg-red-400/20 ring-1 ring-red-400"
                          : "border-red-400/30 bg-red-400/10 hover:bg-red-400/15 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("inactive") && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-400 flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!pndStatusFilters.includes("inactive") && !!inactivePndsCount && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-red-400/0 group-hover:text-red-400/60 transition-colors" />
                    )}
                    <div className="flex items-center gap-1 mb-1">
                      <Ban className="w-3 h-3 text-red-400 flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-red-400">
                        Inactive PNDs
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-red-400 leading-none">
                      {inactivePndsCount === null ? "—" : inactivePndsCount}
                    </p>
                  </button>

                  <button
                    disabled={!noTransactionCount}
                    onClick={() => toggleFilter("no_transaction")}
                    title={
                      noTransactionCount
                        ? "Click to filter map by No Transaction PNDs"
                        : "No such PNDs in this period"
                    }
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      !noTransactionCount
                        ? "border-red-500/15 bg-red-500/5 opacity-50 cursor-not-allowed"
                        : pndStatusFilters.includes("no_transaction")
                          ? "border-red-500 bg-red-500/20 ring-1 ring-red-500"
                          : "border-red-500/30 bg-red-500/10 hover:bg-red-500/15 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("no_transaction") && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!pndStatusFilters.includes("no_transaction") && !!noTransactionCount && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-red-500/0 group-hover:text-red-500/60 transition-colors" />
                    )}
                    <div className="flex items-center gap-1 mb-1">
                      <Ban className="w-3 h-3 text-red-500 flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-red-500">
                        No Transaction
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-red-500 leading-none">
                      {noTransactionCount === null ? "—" : noTransactionCount}
                    </p>
                  </button>

                  <button
                    disabled={!belowThresholdCount}
                    onClick={() => toggleFilter("below_threshold")}
                    title={
                      belowThresholdCount
                        ? "Click to filter map by Below Threshold PNDs"
                        : "No such PNDs in this period"
                    }
                    className={`group relative text-left rounded-xl border p-3 transition-all ${
                      !belowThresholdCount
                        ? "border-black/15 bg-black/5 opacity-50 cursor-not-allowed"
                        : pndStatusFilters.includes("below_threshold")
                          ? "border-black bg-black/20 ring-1 ring-black"
                          : "border-black/30 bg-black/10 hover:bg-black/15 cursor-pointer"
                    }`}
                  >
                    {pndStatusFilters.includes("below_threshold") && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-black flex items-center justify-center shadow-sm">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    {!pndStatusFilters.includes("below_threshold") && !!belowThresholdCount && (
                      <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-black/0 group-hover:text-black/60 transition-colors" />
                    )}
                    <div className="flex items-center gap-1 mb-1">
                      <AlertTriangle className="w-3 h-3 text-black flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-black">
                        Below Threshold
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-black leading-none">
                      {belowThresholdCount === null ? "—" : belowThresholdCount}
                    </p>
                  </button>

                  <div
                    title="Parking bays for this contract (from Enforcement)"
                    className="group relative text-left rounded-xl border border-amber-500/30 bg-amber-500/10 p-3"
                  >
                    <div className="flex items-center gap-1 mb-1">
                      <CircleParking className="w-3 h-3 text-amber-500 flex-shrink-0" />
                      <p className="text-[9px] uppercase tracking-widest text-amber-500">
                        Parking Bays
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-amber-500 leading-none">
                      {formatCount(parkingBaysCount)}
                    </p>
                  </div>

                  <div className="rounded-xl border border-border bg-secondary p-3">
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">
                      Required PNDs
                    </p>
                    <p className="text-2xl font-bold text-foreground leading-none">
                      {pndCoverage.requiredPnds === null
                        ? "—"
                        : formatCount(pndCoverage.requiredPnds)}
                    </p>
                  </div>

                  <div
                    className={`rounded-xl border p-3 ${
                      pndCoverage.diff === null
                        ? "border-border bg-secondary"
                        : pndCoverage.diff > 0
                          ? "border-emerald-500/30 bg-emerald-500/10"
                          : pndCoverage.diff < 0
                            ? "border-red-500/30 bg-red-500/10"
                            : "border-border bg-secondary"
                    }`}
                  >
                    <p
                      className={`text-[9px] uppercase tracking-widest mb-1 ${
                        pndCoverage.diff === null
                          ? "text-muted-foreground"
                          : pndCoverage.diff > 0
                            ? "text-emerald-500"
                            : pndCoverage.diff < 0
                              ? "text-red-500"
                              : "text-muted-foreground"
                      }`}
                    >
                      PND Status
                    </p>
                    <p
                      className={`text-1xl font-bold leading-none ${
                        pndCoverage.diff === null
                          ? "text-foreground"
                          : pndCoverage.diff > 0
                            ? "text-emerald-500"
                            : pndCoverage.diff < 0
                              ? "text-red-500"
                              : "text-foreground"
                      }`}
                    >
                      {pndCoverage.diff === null
                        ? "—"
                        : pndCoverage.diff > 0
                          ? `+${formatCount(pndCoverage.diff)} Extra`
                          : pndCoverage.diff < 0
                            ? `-${formatCount(Math.abs(pndCoverage.diff))} Shortage`
                            : "Balanced"}
                    </p>
                  </div>
                </div>
              </>
            )}
          </AccordionSection>

          <AccordionSection
            id="deviceType"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<Layers className="w-3.5 h-3.5" />}
            title="Device Type Breakdown"
          >
            <div className="grid grid-cols-2 gap-2.5">
              <div className="group relative rounded-lg bg-secondary p-3 overflow-hidden cursor-default">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Banknote className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#8B5CF6" }} />
                </div>
                <p className="text-[22px] font-medium leading-none">{bnaCount}</p>
                <div
                  className="absolute inset-x-0 bottom-0 flex items-center justify-center py-1
                             bg-popover/95 border-t border-border opacity-0 translate-y-1
                             group-hover:opacity-100 group-hover:translate-y-0
                             transition-all duration-150 z-10"
                >
                  <span
                    className="text-[8px] font-bold uppercase tracking-widest px-1 text-center"
                    style={{ color: "#8B5CF6" }}
                  >
                    BNA Devices
                  </span>
                </div>
              </div>
              <div className="group relative rounded-lg bg-secondary p-3 overflow-hidden cursor-default">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CreditCard className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#F59E0B" }} />
                </div>
                <p className="text-[22px] font-medium leading-none">{txCount}</p>
                <div
                  className="absolute inset-x-0 bottom-0 flex items-center justify-center py-1
                             bg-popover/95 border-t border-border opacity-0 translate-y-1
                             group-hover:opacity-100 group-hover:translate-y-0
                             transition-all duration-150 z-10"
                >
                  <span
                    className="text-[8px] font-bold uppercase tracking-widest px-1 text-center"
                    style={{ color: "#F59E0B" }}
                  >
                    TX Devices
                  </span>
                </div>
              </div>
            </div>
          </AccordionSection>

          <AccordionSection
            id="streetRevenue"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<TrendingUp className="w-3.5 h-3.5" />}
            title="Street Revenue"
            badge={[
              activeFilters?.month && activeFilters?.year
                ? `${activeFilters.month} ${activeFilters.year}`
                : `${MONTH_NAMES[new Date().getMonth()]} ${new Date().getFullYear()}`,
              ...(pndStatusFilters.length
                ? [pndStatusFilters.map((f) => PND_STATUS_LABELS[f]).join(" + ")]
                : []),
            ]}
          >
            <StreetRevenueOverview
              street={street}
              filterMonth={activeFilters?.month}
              filterYear={activeFilters?.year}
              filterThreshold={activeFilters?.threshold}
              pndStatusFilters={pndStatusFilters}
              filteredMapData={filteredMapData}
              filteredMapLoading={filteredMapLoading}
            />
          </AccordionSection>

          <AccordionSection
            id="pndBreakdown"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<TrendingUp className="w-3.5 h-3.5" />}
            title="PND Breakdown"
            badge={[
              activeFilters?.month && activeFilters?.year
                ? `${activeFilters.month} ${activeFilters.year}`
                : `${MONTH_NAMES[new Date().getMonth()]} ${new Date().getFullYear()}`,
              ...(pndStatusFilters.length
                ? [pndStatusFilters.map((f) => PND_STATUS_LABELS[f]).join(" + ")]
                : []),
            ]}
          >
            <StreetRevenueSection
              street={street}
              filterMonth={activeFilters?.month}
              filterYear={activeFilters?.year}
              filterThreshold={activeFilters?.threshold}
              onSelectPnd={handlePndSelect}
              onBreakdownLoad={handleBreakdownLoad}
              filterStatus={pndStatusFilters}
            />
          </AccordionSection>

          <AccordionSection
            id="hourlyRevenue"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<Clock className="w-3.5 h-3.5" />}
            title="Hourly Revenue Pattern"
            badge={
              activeFilters?.month ? `${activeFilters.month} ${activeFilters.year}` : undefined
            }
          >
            <HourlyRevenueChart
              deviceId={street.street_send_name || street.street_cd}
              month={activeFilters?.month || MONTH_NAMES[new Date().getMonth()]}
              year={activeFilters?.year || String(new Date().getFullYear())}
              title={`${street.street_name} - Hourly Revenue`}
            />
          </AccordionSection>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// PndDetailPanel
// ============================================
const PndDetailPanel = ({
  pnd,
  street,
  city,
  onClose,
  activeFilters,
  onBack,
  pageRef,
  mapComponentRef,
}) => {
  const [openSection, setOpenSection] = useState("location");
  const handleToggleSection = (id) => setOpenSection((prev) => (prev === id ? null : id));
  const deviceColor = getDeviceColor(pnd.device_type);
  const deviceType = getDeviceTypeName(pnd.device_type);

  const panelRef = useRef(null);
  const exportRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(520);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const handleExportPdf = async () => {
    setPdfExporting(true);
    setPdfProgress(0);
    try {
      await exportPanelToPdf(
        pageRef?.current ? pageRef : exportRef,
        `PND_${pnd?.device_type || pnd?.pnd_number || "Report"}`,
        setPdfProgress,
        mapComponentRef
      );
    } finally {
      setPdfExporting(false);
      setPdfProgress(0);
    }
  };
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onResizeMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startW.current = panelRef.current?.offsetWidth || 520;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const diff = startX.current - e.clientX;
      const newW = Math.min(900, Math.max(320, startW.current + diff));
      setPanelWidth(newW);
    };
    const onUp = () => {
      isDragging.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  return (
    <motion.div
      ref={panelRef}
      style={{ width: panelWidth, height: "100%" }}
      initial={{ y: "-100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: "-100%", opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="absolute top-0 right-0 bottom-0 z-40 overflow-y-auto cb-scroll bg-card border-l border-border shadow-2xl"
    >
      <div
        onMouseDown={onResizeMouseDown}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "6px",
          cursor: "col-resize",
          zIndex: 9999,
        }}
        className="hover:bg-primary/20 transition-colors pdf-export-ignore"
      />

      <div ref={exportRef}>
        <div className="sticky top-0 z-10 backdrop-blur-md border-b border-border bg-card/80">
          <div className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                {onBack && (
                  <button
                    onClick={onBack}
                    className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ background: deviceColor, boxShadow: `0 0 12px ${deviceColor}66` }}
                />
                <div>
                  <h2 className="text-sm font-bold text-foreground">PND {pnd.device_type}</h2>
                  <p className="text-[10px] font-mono text-muted-foreground">{deviceType}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <ExportPdfButton
                  onClick={handleExportPdf}
                  exporting={pdfExporting}
                  progress={pdfProgress}
                />
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-2.5">
          <AccordionSection
            id="location"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<MapPin className="w-3.5 h-3.5" />}
            title="Location Details"
          >
            <div className="space-y-2.5">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Project
                </p>
                <p className="text-sm font-medium text-foreground">{city?.city_name || "N/A"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Street</p>
                <p className="text-sm font-medium text-foreground">
                  {street?.street_name || "N/A"}
                </p>
              </div>
              {pnd.device_zone && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Zone</p>
                  <p className="text-sm font-medium text-foreground">{pnd.device_zone}</p>
                </div>
              )}
            </div>
          </AccordionSection>

          <AccordionSection
            id="deviceInfo"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<Layers className="w-3.5 h-3.5" />}
            title="Device Information"
          >
            {pnd.pnd_number && (
              <div className="mb-3 pb-3 border-b border-border">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  PND Number
                </p>
                <p className="text-sm font-mono font-semibold text-primary">#{pnd.pnd_number}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Device Type
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {(() => {
                    const DTypeIcon = getDeviceTypeIcon(pnd.device_type);
                    return (
                      <DTypeIcon
                        className="w-3.5 h-3.5 flex-shrink-0"
                        style={{ color: getDeviceColor(pnd.device_type) }}
                      />
                    );
                  })()}
                  <p className="text-sm font-medium text-foreground">
                    {pnd.device_type || "Not Installed"}
                  </p>
                </div>
              </div>
            </div>
          </AccordionSection>

          <AccordionSection
            id="revenue"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<TrendingUp className="w-3.5 h-3.5" />}
            title="Revenue"
            badge={
              activeFilters?.month && activeFilters?.year
                ? `${activeFilters.month} ${activeFilters.year}`
                : undefined
            }
          >
            <RevenueSection
              pnd={pnd}
              filterMonth={activeFilters?.month}
              filterYear={activeFilters?.year}
              filterThreshold={activeFilters?.threshold}
            />
          </AccordionSection>

          <AccordionSection
            id="hourlyRevenue"
            openId={openSection}
            onToggle={handleToggleSection}
            icon={<Clock className="w-3.5 h-3.5" />}
            title="Hourly Revenue Pattern"
            badge={
              activeFilters?.month ? `${activeFilters.month} ${activeFilters.year}` : undefined
            }
          >
            <HourlyRevenueChart
              deviceId={pnd.pnd_number || pnd.device_id}
              month={activeFilters?.month || MONTH_NAMES[new Date().getMonth()]}
              year={activeFilters?.year || String(new Date().getFullYear())}
              title={`PND ${pnd.pnd_number || pnd.device_type} - Hourly Revenue`}
            />
          </AccordionSection>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// API Hook
// ============================================
const usePndData = (enabled = true) => {
  const [citiesData, setCitiesData] = useState([]);
  const [countryInfo, setCountryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedOnce = useRef(false);
  const hasFetchedRef = useRef(false);

  const fetchPndData = async (filters = {}) => {
    if (hasLoadedOnce.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const params = {};
      if (filters?.month && filters?.year) {
        params.month = filters.month;
        params.year = filters.year;
      }
      if (filters?.city_send_name) params.city_send_name = filters.city_send_name;
      if (filters?.street_send_name) params.street_send_name = filters.street_send_name;
      if (filters?.country_cd) params.country_cd = filters.country_cd;
      if (Object.prototype.hasOwnProperty.call(filters || {}, "city")) params.city = filters.city;

      const response = await apiCall(
        "get",
        "/cp-map/data",
        Object.keys(params).length ? params : null,
        null,
        false,
        false
      );
      if (response?.success && response?.data) {
        const payload = response.data;
        if (Array.isArray(payload)) {
          setCitiesData(payload);
          setCountryInfo(null);
        } else {
          setCitiesData(payload.cities || []);
          // usePndData ke andar, setCountryInfo block replace karo:
setCountryInfo({
  country_cd: payload.country_cd,
  country_name: payload.country_name,
  total_cities: payload.total_cities,
  total_streets: payload.total_streets,
  total_pnds: payload.total_pnds,
  city_cc_contract: payload.city_cc_contract,
  coordinates: Array.isArray(payload.coordinates) ? payload.coordinates : null,
  overview: payload.overview || null,
});
        }
      } else {
        setError("Failed to load PND data");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
      hasLoadedOnce.current = true;
      hasFetchedRef.current = false;
    }
  };

  useEffect(() => {
    if (!enabled) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchPndData();
  }, [enabled]);
  return { citiesData, countryInfo, loading, refreshing, error, refetch: fetchPndData };
};

// ============================================
// OnStreetParkingPage
// ============================================
const OnStreetParkingPage = ({
  onCityChange,
  onStreetChange,              // 👈 NAYA
  onFiltersChangeExternal,
  selectedCityNameExternal,
  selectedStreetInfoExternal,  // 👈 NAYA
  selectedDateFilterExternal,
  onReady,
  isActive = true,
  monthGroups = [],
  yearOptions = [],
  optLoading = false,
  optError = null,
} = {}) => {
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [selectedPnd, setSelectedPnd] = useState(null);
  const [detailView, setDetailView] = useState("country");
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [activeFilters, setActiveFilters] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const pageRef = useRef(null);
  const mapComponentRef = useRef(null);

  const sidebarWidthRef = useRef(320);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const isSidebarDragging = useRef(false);
  const sidebarStartX = useRef(0);
  const sidebarStartW = useRef(0);

  const onSidebarResizeMouseDown = (e) => {
    isSidebarDragging.current = true;
    sidebarStartX.current = e.clientX;
    sidebarStartW.current = sidebarWidthRef.current;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isSidebarDragging.current) return;
      const diff = e.clientX - sidebarStartX.current;
      const newW = Math.min(600, Math.max(240, sidebarStartW.current + diff));
      sidebarWidthRef.current = newW;
      setSidebarWidth(newW);
    };
    const onUp = () => {
      isSidebarDragging.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const [belowThresholdPnds, setBelowThresholdPnds] = useState(new Set());
  const [noTransactionPnds, setNoTransactionPnds] = useState(new Set());
  const [pndRevenueMap, setPndRevenueMap] = useState(new Map());

  const [pndStatusFilters, setPndStatusFilters] = useState(EMPTY_FILTERS);
  const [streetsOnlyMode, setStreetsOnlyMode] = useState(false);
  const [baysPerPnd, setBaysPerPnd] = useState(25);

  const handleTogglePndStatusFilter = useCallback((key) => {
    setStreetsOnlyMode(false);
    setFilteredMapLoading(true);
    setPndStatusFilters((prev) => (prev.length === 1 && prev[0] === key ? EMPTY_FILTERS : [key]));
  }, []);

  const handleClearPndStatusFilters = useCallback(() => setPndStatusFilters(EMPTY_FILTERS), []);

  const [filteredMapData, setFilteredMapData] = useState(null);
  const [filteredMapLoading, setFilteredMapLoading] = useState(false);
  const filteredMapFetchIdRef = useRef(0);

  useEffect(() => {
    const cityId = selectedCity?.city_send_name || selectedCity?.city_cd;
    const streetId = selectedStreet?.street_send_name || selectedStreet?.street_cd;

    if (pndStatusFilters.length === 0 || (!cityId && !streetId)) {
      setFilteredMapData(null);
      setFilteredMapLoading(false);
      return;
    }

    const fetchId = ++filteredMapFetchIdRef.current;
    setFilteredMapLoading(true);

    const now = new Date();
    const displayMonth = activeFilters?.month || MONTH_NAMES[now.getMonth()];
    const displayYear = activeFilters?.year || String(now.getFullYear());
    const threshold = activeFilters?.threshold || "";
    const monthParam = buildMonthParam(displayMonth);
    const filtersParam = buildFiltersParam(pndStatusFilters);

    const url = streetId
      ? `/get-pnd-collection?street=true&device_id=${streetId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}${filtersParam}`
      : `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}${filtersParam}`;

    apiCall("get", url, null, null, false, false)
      .then((response) => {
        if (fetchId !== filteredMapFetchIdRef.current) return;
        if (!response?.success) {
          setFilteredMapData({ type: streetId ? "street" : "city", pnds: [], streets: [] });
          return;
        }

        if (streetId) {
          setFilteredMapData({
            type: "street",
            pnds: response.street_breakdown?.pnds || [],
            collection: response.collection,
            ticket_count: response.ticket_count,
            als: response.als,
            atv: response.atv,
          });
        } else {
          const payload =
            response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;
          setFilteredMapData({
            type: "city",
            streets: payload.streets || [],
            collection: response.collection,
            ticket_count: response.ticket_count,
            als: response.als,
            atv: response.atv,
          });
        }
      })
      .catch(() => {
        if (fetchId === filteredMapFetchIdRef.current)
          setFilteredMapData({ type: streetId ? "street" : "city", pnds: [], streets: [] });
      })
      .finally(() => {
        if (fetchId === filteredMapFetchIdRef.current) setFilteredMapLoading(false);
      });
  }, [selectedCity, selectedStreet, activeFilters, pndStatusFilters]);

  const handleToggleStreetsOnly = useCallback(() => {
    const next = !streetsOnlyMode;
    setStreetsOnlyMode(next);
    setPndStatusFilters(next ? ["all"] : EMPTY_FILTERS);
  }, [streetsOnlyMode]);

  const {
    citiesData: allCitiesData,
    countryInfo,
    loading,
    refreshing,
    error,
    refetch: fetchData,
  } = usePndData(isActive);

  useEffect(() => {
    if (!loading) {
      onReady?.();
    }
  }, [loading, onReady]);

  const { theme: appTheme } = useTheme();

  const { userContracts } = usePermissions();

  // ============================================
  // FILTER CITIES BASED ON USER PERMISSIONS
  // ============================================
  const citiesData = useMemo(() => {
    if (!userContracts) return allCitiesData;

    const normalized = normalizePermissions(userContracts);
    const allowedKeys = new Set([
      ...normalized.onstreet.map((c) => getContractKey(c)),
      ...normalized.offstreet.map((c) => getContractKey(c)),
    ]);

    if (allowedKeys.size === 0) return allCitiesData;

    return allCitiesData.filter((city) => {
      const cityName = normalizeName(city.city_name);

      for (const contract of ONSTREET_CONTRACTS.children) {
        const contractKey = getContractKey(contract);
        if (allowedKeys.has(contractKey) && normalizeName(contract.fullName) === cityName) {
          return true;
        }
      }

      return (
        allowedKeys.has(city.city_send_name) ||
        allowedKeys.has(city.city_cd) ||
        [...allowedKeys].some((key) => normalizeName(key) === cityName)
      );
    });
  }, [allCitiesData, userContracts]);

  // ============================================
  // CHECK IF COUNTRY IS ASSIGNED
  // ============================================
  const isCountryAssigned = useMemo(() => {
    if (!userContracts) return true;

    // Country/contract code now comes from the API response's `city_cc_contract`
    // key instead of the old hardcoded '901' - passed through so the
    // "Saudi Arabia" permission entry normalizes to the real, current code.
    const countryContractCode = countryInfo?.city_cc_contract || DEFAULT_COUNTRY_CONTRACT_CODE;
    const normalized = normalizePermissions(userContracts, countryContractCode);
    const allowedKeys = new Set([
      ...normalized.onstreet.map((c) => getContractKey(c)),
      ...normalized.offstreet.map((c) => getContractKey(c)),
    ]);

    if (allowedKeys.size === 0) return true;

    const saudiKey = getContractKey({
      name: countryContractCode,
      fullName: ONSTREET_CONTRACTS.fullName,
    });
    return allowedKeys.has(saudiKey);
  }, [userContracts, countryInfo]);

  const visibleCountryInfo = isCountryAssigned ? countryInfo : null;

  const handleBreakdownLoad = useCallback(({ belowThreshold, noTransaction, revenueMap }) => {
    setBelowThresholdPnds(new Set(belowThreshold));
    setNoTransactionPnds(new Set(noTransaction));
    if (revenueMap) setPndRevenueMap(new Map(revenueMap));
  }, []);

  useEffect(() => {
    if (!citiesData || citiesData.length === 0) return;

    if (!selectedCityNameExternal) {
      if (!isCountryAssigned) {
        const alreadyValid =
          selectedCity && citiesData.some((c) => c.city_cd === selectedCity.city_cd);
        if (!alreadyValid) {
          setSelectedCity(citiesData[0]);
          setSelectedStreet(null);
          setSelectedPnd(null);
          setDetailView("city");
          setPndStatusFilters(EMPTY_FILTERS);
          setStreetsOnlyMode(false);
        }
        return;
      }

      if (selectedCity) {
        setSelectedCity(null);
        setSelectedStreet(null);
        setSelectedPnd(null);
        setDetailView("country");
        setPndStatusFilters(EMPTY_FILTERS);
        setStreetsOnlyMode(false);
      }
      return;
    }

    if (
      selectedCity?.city_name &&
      normalizeName(selectedCity.city_name) === normalizeName(selectedCityNameExternal)
    )
      return;

    const target = normalizeName(selectedCityNameExternal);
    const match =
      citiesData.find((c) => normalizeName(c.city_name) === target) ||
      citiesData.find(
        (c) =>
          normalizeName(c.city_name).includes(target) || target.includes(normalizeName(c.city_name))
      );

    if (match) {
      setSelectedCity(match);
      setSelectedStreet(null);
      setSelectedPnd(null);
      setDetailView("city");
      setPndStatusFilters(EMPTY_FILTERS);
      setStreetsOnlyMode(false);
    }
  }, [selectedCityNameExternal, citiesData, isCountryAssigned]);

  // ── Reverse sync: street selected/cleared in the other tab carries over here ──
useEffect(() => {
  if (!selectedCity) return; // city pehle sync hone do

  if (!selectedStreetInfoExternal) {
    if (selectedStreet) {
      setSelectedStreet(null);
      setSelectedPnd(null);
      setDetailView("city");
    }
    return;
  }

  const alreadySynced =
    selectedStreet &&
    (selectedStreet.street_cd === selectedStreetInfoExternal.streetCd ||
      normalizeName(selectedStreet.street_name) ===
        normalizeName(selectedStreetInfoExternal.streetName));
  if (alreadySynced) return;

  const streets = selectedCity.streets || [];
  const match =
    streets.find((s) => s.street_cd === selectedStreetInfoExternal.streetCd) ||
    streets.find(
      (s) =>
        normalizeName(s.street_name) ===
        normalizeName(selectedStreetInfoExternal.streetName)
    );

  if (match) {
    setSelectedStreet(match);
    setSelectedPnd(null);
    setDetailView("street");
  }
}, [selectedStreetInfoExternal, selectedCity]);

  useEffect(() => {
    const hasIncoming = !!(
      selectedDateFilterExternal &&
      selectedDateFilterExternal.month &&
      selectedDateFilterExternal.year
    );
    const alreadySynced = hasIncoming
      ? activeFilters?.month === selectedDateFilterExternal.month &&
        activeFilters?.year === selectedDateFilterExternal.year
      : !activeFilters?.month && !activeFilters?.year;
    if (alreadySynced) return;

    const nextFilters = hasIncoming ? selectedDateFilterExternal : {};
    setActiveFilters(nextFilters);
    if (pndStatusFilters.length > 0) setFilteredMapLoading(true);
    const { threshold, ...mapFilters } = nextFilters;
    fetchData({
      ...mapFilters,
      ...(selectedCity && { city_send_name: selectedCity.city_send_name }),
      ...(selectedStreet && { street_send_name: selectedStreet.street_send_name }),
    });
  }, [selectedDateFilterExternal]);

  useEffect(() => {
    setIsDarkMode(appTheme === "dark");
  }, [appTheme]);

  const handleSelectCountry = useCallback(() => {
  setSelectedCity(null);
  setSelectedStreet(null);
  setSelectedPnd(null);
  setDetailView("country");
  setPndStatusFilters(EMPTY_FILTERS);
  setStreetsOnlyMode(false);
  onCityChange?.(null);
  onStreetChange?.(null); // 👈 NAYA
}, [onCityChange, onStreetChange]);

const handleSelectCity = useCallback(
  (city) => {
    setSelectedCity(city);
    setSelectedStreet(null);
    setSelectedPnd(null);
    setDetailView("city");
    setPndStatusFilters(EMPTY_FILTERS);
    setStreetsOnlyMode(false);
    onCityChange?.(city);
    onStreetChange?.(null); // 👈 NAYA — nayi city select ki tou street reset
  },
  [onCityChange, onStreetChange]
);

const handleSelectStreet = useCallback(
  (street) => {
    if (!street) return;
    setSelectedStreet(street);
    setSelectedPnd(null);
    setDetailView("street");
    setPndStatusFilters(EMPTY_FILTERS);
    setStreetsOnlyMode(false);
    onStreetChange?.(street); // 👈 NAYA
  },
  [onStreetChange]
);

const handleSelectPnd = useCallback((pnd) => {
  if (!pnd) return;
  setSelectedPnd(pnd);
  setDetailView("pnd");
}, []);

const handleCloseDetail = useCallback(() => {
  setDetailView("none");
  setSelectedPnd(null);
}, []);

const handleBackToStreet = useCallback(() => {
  setSelectedPnd(null);
  setDetailView("street");
}, []);

const handleBackToCity = useCallback(() => {
  setSelectedStreet(null);
  setSelectedPnd(null);
  setDetailView("city");
  onStreetChange?.(null); // 👈 NAYA
}, [onStreetChange]);

const handleBackFromCity = useCallback(() => {
  setSelectedCity(null);
  setDetailView("none");
  onCityChange?.(null);
  onStreetChange?.(null); // 👈 NAYA
}, [onCityChange, onStreetChange]);

const handleStreetFromCity = useCallback(
  (street) => {
    setSelectedStreet(street);
    setSelectedPnd(null);
    setDetailView("street");
    onStreetChange?.(street); // 👈 NAYA
  },
  [onStreetChange]
);

const handlePndFromStreet = useCallback((pnd) => {
  setSelectedPnd(pnd);
  setDetailView("pnd");
}, []);

  const handleFiltersChange = useCallback(
    (filters) => {
      setActiveFilters(filters);
      onFiltersChangeExternal?.(filters);
      if (pndStatusFilters.length > 0) setFilteredMapLoading(true);
      const { threshold, ...mapFilters } = filters;
      fetchData({
        ...mapFilters,
        ...(selectedCity && { city_send_name: selectedCity.city_send_name }),
        ...(selectedStreet && { street_send_name: selectedStreet.street_send_name }),
      });
    },
    [fetchData, selectedCity, selectedStreet, pndStatusFilters, onFiltersChangeExternal]
  );

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} onRetry={fetchData} />;

  const getPndContext = () => {
    if (!selectedPnd) return { street: null, city: null };
    const selectedPndNumber = selectedPnd.pnd_number;
    for (const city of citiesData) {
      for (const street of city.streets || []) {
        const matchingPnd = street.pnds?.find(
          (p) => String(p.pnd_number) === String(selectedPndNumber)
        );
        if (matchingPnd) return { street, city };
      }
    }
    return { street: null, city: null };
  };

  const getStreetCity = () => {
    if (!selectedStreet) return null;
    return (
      citiesData.find((c) => c.streets?.some((s) => s.street_cd === selectedStreet.street_cd)) ||
      null
    );
  };

  const { street: pndStreet, city: pndCity } = getPndContext();
  const streetCity = getStreetCity();

  return (
    <div ref={pageRef} className="flex h-full w-full overflow-hidden bg-background">
      <AnimatePresence initial={false}>
        {sidebarOpen && (
          <motion.div
            key="sidebar"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: sidebarWidth, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={
              isSidebarDragging.current ? { duration: 0 } : { duration: 0.25, ease: "easeInOut" }
            }
            className="flex-shrink-0 overflow-hidden relative"
            style={{ minWidth: 0 }}
          >
            <div
              onMouseDown={onSidebarResizeMouseDown}
              className="absolute top-0 right-0 bottom-0 hover:bg-primary/20 transition-colors pdf-export-ignore"
              style={{ width: "6px", cursor: "col-resize", zIndex: 50 }}
            />
            <CityStreetSidebar
              citiesData={citiesData}
              countryInfo={visibleCountryInfo}
              allCitiesData={allCitiesData}
              fullCountryInfo={countryInfo}
              selectedCity={selectedCity}
              selectedStreet={selectedStreet}
              selectedPnd={selectedPnd}
              onSelectCity={handleSelectCity}
              onSelectStreet={handleSelectStreet}
              onSelectPnd={handleSelectPnd}
              onSelectCountry={handleSelectCountry}
              activeFilters={activeFilters}
              onFiltersChange={handleFiltersChange}
              onToggle={() => setSidebarOpen(false)}
              baysPerPnd={baysPerPnd}
              onBaysPerPndChange={setBaysPerPnd}
              monthGroups={monthGroups}
              yearOptions={yearOptions}
              optLoading={optLoading}
              optError={optError}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 relative h-full min-h-0">
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="absolute top-4 left-3 z-50 p-2 rounded-lg bg-card border border-border shadow-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all pdf-export-ignore"
            title="Open sidebar"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {refreshing && (
          <div>
            {/* <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" /> */}
            {/* <span className="text-xs text-muted-foreground">Updating...</span> */}
          </div>
        )}

        <GoogleMapComponent
          ref={mapComponentRef}
          citiesData={citiesData}
          countryInfo={visibleCountryInfo || countryInfo}
          selectedCity={selectedCity}
          selectedStreet={selectedStreet}
          selectedPnd={selectedPnd}
          onPndClick={handleSelectPnd}
          onCityClick={handleSelectCity}
          onStreetClick={handleSelectStreet}
          isDarkMode={isDarkMode}
          belowThresholdPnds={belowThresholdPnds}
          noTransactionPnds={noTransactionPnds}
          pndStatusFilters={pndStatusFilters}
          streetsOnlyMode={streetsOnlyMode}
          pndRevenueMap={pndRevenueMap}
          sidebarOpen={sidebarOpen}
          detailPanelWidth={detailView !== "none" ? 520 : 0}
          filteredMapData={filteredMapData}
          filteredMapLoading={filteredMapLoading}
        />

        <AnimatePresence>
          {detailView === "country" && (
            <CountryDetailPanel
              key="country-panel"
              countryInfo={visibleCountryInfo}
              citiesData={citiesData}
              activeFilters={activeFilters}
              onClose={handleCloseDetail}
              onSelectCity={handleSelectCity}
              pageRef={pageRef}
              mapComponentRef={mapComponentRef}
            />
          )}
          {detailView === "city" && selectedCity && (
            <CityDetailPanel
              key="city-panel"
              city={selectedCity}
              onClose={handleCloseDetail}
              onBack={handleBackFromCity}
              activeFilters={activeFilters}
              onSelectStreet={handleStreetFromCity}
              onBreakdownLoad={handleBreakdownLoad}
              pndStatusFilters={pndStatusFilters}
              onTogglePndStatusFilter={handleTogglePndStatusFilter}
              onClearPndStatusFilters={handleClearPndStatusFilters}
              streetsOnlyMode={streetsOnlyMode}
              onToggleStreetsOnly={handleToggleStreetsOnly}
              filteredMapData={filteredMapData}
              filteredMapLoading={filteredMapLoading}
              baysPerPnd={baysPerPnd}
              pageRef={pageRef}
              mapComponentRef={mapComponentRef}
            />
          )}
          {detailView === "street" && selectedStreet && (
            <StreetDetailPanel
              key="street-panel"
              street={selectedStreet}
              city={streetCity}
              onClose={handleCloseDetail}
              onBack={selectedCity ? handleBackToCity : undefined}
              activeFilters={activeFilters}
              onSelectPnd={handlePndFromStreet}
              onBreakdownLoad={handleBreakdownLoad}
              pndStatusFilters={pndStatusFilters}
              onTogglePndStatusFilter={handleTogglePndStatusFilter}
              onClearPndStatusFilters={handleClearPndStatusFilters}
              filteredMapData={filteredMapData}
              filteredMapLoading={filteredMapLoading}
              baysPerPnd={baysPerPnd}
              pageRef={pageRef}
              mapComponentRef={mapComponentRef}
            />
          )}
          {detailView === "pnd" && selectedPnd && (
            <PndDetailPanel
              key="pnd-panel"
              pnd={selectedPnd}
              street={pndStreet}
              city={pndCity}
              onClose={handleCloseDetail}
              onBack={selectedStreet ? handleBackToStreet : undefined}
              activeFilters={activeFilters}
              pageRef={pageRef}
              mapComponentRef={mapComponentRef}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OnStreetParkingPage;
